import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { Typography, Modal, Alert, Input, Button, Tag, Table, Space, Form, message, Select, InputNumber, Switch, TimePicker, Tooltip, Dropdown, Checkbox, Popconfirm, DatePicker, Radio, Spin, Divider, Popover, Descriptions, Segmented, Card, Statistic, Progress, Drawer } from 'antd';
import { AppstoreOutlined, ArrowLeftOutlined, CalendarOutlined, OrderedListOutlined, PlusOutlined, DollarOutlined, CopyOutlined, EditOutlined, MoreOutlined, DeleteOutlined, UserOutlined, LockOutlined, SearchOutlined, MinusCircleOutlined, KeyOutlined, ReloadOutlined, DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const CourtContext = createContext(null);
const useCourtContext = () => {
  const context = useContext(CourtContext);
  if (!context) {
    throw new Error(
      "useCourtContext must be used within a CourtProvider. Please wrap your Court components with <CourtProvider>."
    );
  }
  return context;
};

const CourtLayout = () => {
  return /* @__PURE__ */ jsx(Outlet, {});
};

const { Title, Text } = Typography;
const PageHeader = ({ title, description, extra }) => /* @__PURE__ */ jsxs("div", { style: { marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }, children: [
  /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(Title, { level: 3, style: { marginBottom: description ? 4 : 0 }, children: title }),
    description && /* @__PURE__ */ jsx(Text, { type: "secondary", style: { fontSize: 14 }, children: description })
  ] }),
  extra
] });

const StaffPinGate = ({ open, supplierId, onVerified, onCancel }) => {
  const { t, staffAuth } = useCourtContext();
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const handleSubmit = async () => {
    if (!staffAuth || pin.length !== 4) return;
    setSubmitting(true);
    setError(null);
    try {
      const staff = await staffAuth.verifyPin(supplierId, pin);
      setPin("");
      onVerified(staff);
    } catch (err) {
      setError(t("court.staff.pinGate.error", { defaultValue: "M\xE3 PIN kh\xF4ng \u0111\xFAng ho\u1EB7c b\u1EA1n ch\u01B0a \u0111\u01B0\u1EE3c c\u1EA5p quy\u1EC1n. Vui l\xF2ng th\u1EED l\u1EA1i ho\u1EB7c li\xEAn h\u1EC7 qu\u1EA3n l\xFD." }));
    } finally {
      setSubmitting(false);
    }
  };
  return /* @__PURE__ */ jsxs(
    Modal,
    {
      open,
      onCancel,
      title: t("court.staff.pinGate.title", { defaultValue: "X\xE1c th\u1EF1c nh\xE2n vi\xEAn" }),
      footer: null,
      width: 360,
      destroyOnClose: true,
      children: [
        /* @__PURE__ */ jsx("p", { style: { color: "rgba(0,0,0,0.65)", marginBottom: 16 }, children: t("court.staff.pinGate.description", { defaultValue: "Nh\u1EADp m\xE3 PIN 4 s\u1ED1 \u0111\u1EC3 ti\u1EBFp t\u1EE5c \u0111\u1EB7t s\xE2n / x\xE1c nh\u1EADn thu ti\u1EC1n." }) }),
        error && /* @__PURE__ */ jsx(Alert, { type: "error", showIcon: true, message: error, style: { marginBottom: 16 } }),
        /* @__PURE__ */ jsx(
          Input.Password,
          {
            value: pin,
            onChange: (e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4)),
            onPressEnter: () => void handleSubmit(),
            maxLength: 4,
            placeholder: "\u2022\u2022\u2022\u2022",
            autoFocus: true,
            style: { textAlign: "center", fontSize: 24, letterSpacing: 8, marginBottom: 16 }
          }
        ),
        /* @__PURE__ */ jsx(Button, { type: "primary", block: true, onClick: () => void handleSubmit(), loading: submitting, disabled: !staffAuth || pin.length !== 4, children: t("court.staff.pinGate.submit", { defaultValue: "X\xE1c nh\u1EADn" }) })
      ]
    }
  );
};

const ORDER_STATUS_META = {
  n: { color: "orange", defaultLabel: "Ch\u01B0a thu ti\u1EC1n", key: "court.bookings.orderStatus.n" },
  p: { color: "green", defaultLabel: "\u0110\xE3 thu ti\u1EC1n", key: "court.bookings.orderStatus.p" },
  e: { color: "default", defaultLabel: "H\u1EBFt h\u1EA1n", key: "court.bookings.orderStatus.e" },
  c: { color: "volcano", defaultLabel: "\u0110\xE3 hu\u1EF7", key: "court.bookings.orderStatus.c" },
  r: { color: "purple", defaultLabel: "\u0110\xE3 ho\xE0n ti\u1EC1n", key: "court.bookings.orderStatus.r" }
};
const STATUS_META = ORDER_STATUS_META;
const OrderStatusTag = ({ orderStatus }) => {
  const { t } = useCourtContext();
  if (!orderStatus) return null;
  const meta = STATUS_META[orderStatus];
  if (!meta) return null;
  return /* @__PURE__ */ jsx(Tag, { color: meta.color, children: t(meta.key, { defaultValue: meta.defaultLabel }) });
};

const courtMenuItems = (getOrgUrl, t, onMenuClick) => {
  return {
    key: "court",
    icon: /* @__PURE__ */ jsx(AppstoreOutlined, {}),
    label: t("menu.organization.court"),
    children: [
      { key: "court-venues", label: t("menu.court.venues"), onClick: () => onMenuClick(getOrgUrl("court/venues")) }
    ]
  };
};

var common$1 = {
	active: "Active",
	inactive: "Inactive",
	add: "Add",
	edit: "Edit",
	save: "Save",
	cancel: "Cancel",
	close: "Close",
	confirm: "Confirm",
	deactivate: "Deactivate",
	reactivate: "Reactivate",
	name: "Name",
	code: "Code",
	yes: "Yes",
	no: "No",
	refresh: "Refresh",
	back: "Back",
	search: "Search",
	loading: "Loading...",
	noData: "No data"
};
var menu$1 = {
	organization: {
		court: "Courts (Pickleball)"
	},
	court: {
		venues: "Venues"
	}
};
var court$1 = {
	venues: {
		title: "Venues",
		pageDescription: "Venues owned by this organization — each venue manages its own courts and price rules.",
		nameColumn: "Venue name",
		eventSlugColumn: "Linked event",
		manageCourtsButton: "Manage courts",
		viewBookingsButton: "View bookings",
		empty: "This organization has no venues yet."
	},
	courts: {
		title: "Courts",
		pageDescription: "Courts in this venue — create, edit opening hours, deactivate or reactivate.",
		backToVenues: "Back to venues",
		resourceIdColumn: "Court ID",
		nameColumn: "Court name",
		sportTypeColumn: "Sport type",
		slotMinutesColumn: "Slot length (min)",
		openTimeColumn: "Open time",
		closeTimeColumn: "Close time",
		currencyColumn: "Currency",
		statusColumn: "Status",
		priceRulesButton: "Price rules",
		createTitle: "Add new court",
		editTitle: "Edit court",
		resourceIdFieldLabel: "Court ID (resource_id)",
		resourceIdPlaceholder: "e.g. court-1",
		nameFieldLabel: "Court name",
		sportTypeFieldLabel: "Sport type",
		sportTypePlaceholder: "e.g. pickleball",
		slotMinutesFieldLabel: "Slot length (minutes)",
		openTimeFieldLabel: "Open time",
		closeTimeFieldLabel: "Close time",
		currencyFieldLabel: "Currency",
		currencyPlaceholder: "e.g. VND",
		created: "Court created",
		updated: "Court updated",
		deactivated: "Court deactivated",
		reactivated: "Court reactivated",
		deactivateConfirmTitle: "Deactivate this court?",
		deactivateConfirmContent: "The court will no longer be available for booking. It can be reactivated at any time.",
		createDisabledTooltip: "Adding new courts has not been configured by the host.",
		empty: "This venue has no courts yet."
	},
	pricing: {
		title: "Price rules",
		pageDescription: "Price rules by weekday and time range for the selected court — higher priority wins on overlap.",
		backToCourts: "Back to courts",
		weekdayColumn: "Weekday",
		timeRangeColumn: "Time range",
		priceColumn: "Price",
		priorityColumn: "Priority",
		statusColumn: "Status",
		createTitle: "Add price rule",
		editTitle: "Edit price rule",
		weekdayFieldLabel: "Weekday",
		startTimeFieldLabel: "Start time",
		endTimeFieldLabel: "End time",
		priceFieldLabel: "Price",
		priorityFieldLabel: "Priority",
		priorityHelp: "A higher number wins when two price rules overlap in time.",
		created: "Price rule created",
		updated: "Price rule updated",
		deactivated: "Price rule deactivated",
		reactivated: "Price rule reactivated",
		deactivateConfirmTitle: "Deactivate this price rule?",
		deactivateConfirmContent: "The price rule will no longer apply when pricing bookings. It can be reactivated at any time.",
		conflictError: "This price rule conflicts with another active rule at the same priority and overlapping time.",
		empty: "This court has no price rules yet.",
		weekday: {
			"0": "Sunday",
			"1": "Monday",
			"2": "Tuesday",
			"3": "Wednesday",
			"4": "Thursday",
			"5": "Friday",
			"6": "Saturday"
		}
	},
	bookings: {
		title: "Bookings",
		pageDescription: "Bookings for the selected day — READ ONLY, cancellation is not available from this screen.",
		datePickerLabel: "Select date",
		resourceIdColumn: "Court",
		startAtColumn: "Start",
		endAtColumn: "End",
		statusColumn: "Status",
		orderRefColumn: "Order",
		orderRefEmpty: "—",
		viewOrderLink: "View order",
		empty: "No bookings for this day."
	},
	checkin: {
		button: "Check-in code",
		title: "Customer check-in code",
		hint: "The customer opens the Zalo Mini App and scans this code. Only the person who booked can check in, from 30 minutes before to 60 minutes after the start time.",
		regenerate: "Generate new code",
		expiresIn: "Valid for",
		expired: "Code expired — press \"Generate new code\".",
		oldInvalid: "Generating a new code invalidates the old one immediately. Each code can be used once.",
		qrAlt: "Check-in QR code",
		noQr: "Could not create the QR code, try again.",
		manualButton: "Check in by code",
		manualTitle: "Check in by booking code",
		manualOk: "Check in",
		codePlaceholder: "Enter the customer's booking code",
		overrideLabel: "Order was marked \"no-show\" — change to arrived",
		reasonPlaceholder: "Reason (required, recorded in the log)",
		reasonRequired: "A reason is required to change a no-show to arrived.",
		done: "Customer checked in.",
		already: "This order was already checked in."
	}
};
var en = {
	common: common$1,
	menu: menu$1,
	court: court$1
};

var common = {
	active: "Hoạt động",
	inactive: "Ngừng",
	add: "Thêm",
	edit: "Sửa",
	save: "Lưu",
	cancel: "Huỷ",
	close: "Đóng",
	confirm: "Xác nhận",
	deactivate: "Ngừng hoạt động",
	reactivate: "Kích hoạt lại",
	name: "Tên",
	code: "Mã",
	yes: "Có",
	no: "Không",
	refresh: "Tải lại",
	back: "Quay lại",
	search: "Tìm kiếm",
	loading: "Đang tải...",
	noData: "Không có dữ liệu"
};
var menu = {
	organization: {
		court: "Sân (Pickleball)"
	},
	court: {
		venues: "Danh sách sân"
	}
};
var court = {
	venues: {
		title: "Cụm sân",
		pageDescription: "Danh sách cụm sân (venue) của tổ chức — mỗi cụm sân quản lý riêng danh sách sân và khung giá.",
		nameColumn: "Tên cụm sân",
		eventSlugColumn: "Sự kiện gắn kèm",
		manageCourtsButton: "Quản lý sân",
		viewBookingsButton: "Xem lịch đặt",
		empty: "Tổ chức chưa có cụm sân nào."
	},
	courts: {
		title: "Danh sách sân",
		pageDescription: "Sân trong cụm sân này — tạo mới, sửa giờ mở/đóng cửa, ngừng hoặc kích hoạt lại.",
		backToVenues: "Quay lại danh sách cụm sân",
		resourceIdColumn: "Mã sân",
		nameColumn: "Tên sân",
		sportTypeColumn: "Môn thể thao",
		slotMinutesColumn: "Độ dài slot (phút)",
		openTimeColumn: "Giờ mở cửa",
		closeTimeColumn: "Giờ đóng cửa",
		currencyColumn: "Đơn vị tiền",
		statusColumn: "Trạng thái",
		priceRulesButton: "Khung giá",
		createTitle: "Thêm sân mới",
		editTitle: "Sửa thông tin sân",
		resourceIdFieldLabel: "Mã sân (resource_id)",
		resourceIdPlaceholder: "vd: court-1",
		nameFieldLabel: "Tên sân",
		sportTypeFieldLabel: "Môn thể thao",
		sportTypePlaceholder: "vd: pickleball",
		slotMinutesFieldLabel: "Độ dài slot (phút)",
		openTimeFieldLabel: "Giờ mở cửa",
		closeTimeFieldLabel: "Giờ đóng cửa",
		currencyFieldLabel: "Đơn vị tiền",
		currencyPlaceholder: "vd: VND",
		created: "Đã tạo sân",
		updated: "Đã cập nhật sân",
		deactivated: "Đã ngừng hoạt động sân",
		reactivated: "Đã kích hoạt lại sân",
		deactivateConfirmTitle: "Ngừng hoạt động sân này?",
		deactivateConfirmContent: "Sân sẽ không còn hiển thị để đặt lịch. Có thể kích hoạt lại bất cứ lúc nào.",
		createDisabledTooltip: "Tính năng thêm sân mới chưa được host cấu hình.",
		empty: "Cụm sân này chưa có sân nào."
	},
	pricing: {
		title: "Khung giá",
		pageDescription: "Khung giá theo ngày trong tuần và khung giờ cho sân đang chọn — ưu tiên (priority) cao hơn thắng khi 2 khung giờ chồng nhau.",
		backToCourts: "Quay lại danh sách sân",
		weekdayColumn: "Thứ",
		timeRangeColumn: "Khung giờ",
		priceColumn: "Giá",
		priorityColumn: "Ưu tiên",
		statusColumn: "Trạng thái",
		createTitle: "Thêm khung giá",
		editTitle: "Sửa khung giá",
		weekdayFieldLabel: "Thứ trong tuần",
		startTimeFieldLabel: "Giờ bắt đầu",
		endTimeFieldLabel: "Giờ kết thúc",
		priceFieldLabel: "Giá (đồng)",
		priorityFieldLabel: "Ưu tiên",
		priorityHelp: "Số lớn hơn thắng khi 2 khung giá chồng thời gian nhau.",
		created: "Đã tạo khung giá",
		updated: "Đã cập nhật khung giá",
		deactivated: "Đã ngừng khung giá",
		reactivated: "Đã kích hoạt lại khung giá",
		deactivateConfirmTitle: "Ngừng khung giá này?",
		deactivateConfirmContent: "Khung giá sẽ không còn áp dụng khi tính giá đặt sân. Có thể kích hoạt lại bất cứ lúc nào.",
		conflictError: "Khung giá bị trùng ưu tiên và chồng thời gian với 1 khung giá khác đang hoạt động.",
		empty: "Sân này chưa có khung giá nào.",
		weekday: {
			"0": "Chủ nhật",
			"1": "Thứ hai",
			"2": "Thứ ba",
			"3": "Thứ tư",
			"4": "Thứ năm",
			"5": "Thứ sáu",
			"6": "Thứ bảy"
		}
	},
	bookings: {
		title: "Lịch đặt sân",
		pageDescription: "Lịch đã đặt trong ngày. Ở chế độ Timeline, bấm vào một ô đặt sân để khách đến, bắt đầu, kết thúc, không đến, gia hạn, đổi lịch hoặc huỷ đơn; chế độ Bảng chỉ để xem và thu tiền.",
		datePickerLabel: "Chọn ngày",
		resourceIdColumn: "Sân",
		startAtColumn: "Bắt đầu",
		endAtColumn: "Kết thúc",
		statusColumn: "Trạng thái",
		orderRefColumn: "Đơn hàng",
		orderRefEmpty: "—",
		viewOrderLink: "Xem đơn",
		empty: "Ngày này chưa có lịch đặt nào."
	},
	checkin: {
		button: "Mã check-in",
		title: "Mã check-in cho khách",
		hint: "Khách mở Zalo Mini App và quét mã này. Chỉ người đặt sân mới check-in được, trong khoảng 30 phút trước đến 60 phút sau giờ bắt đầu.",
		regenerate: "Tạo mã mới",
		expiresIn: "Còn hiệu lực",
		expired: "Mã đã hết hạn — bấm \"Tạo mã mới\".",
		oldInvalid: "Tạo mã mới thì mã cũ hết hiệu lực ngay. Mỗi mã chỉ dùng một lần.",
		qrAlt: "Mã QR check-in",
		noQr: "Không tạo được mã QR, thử lại.",
		manualButton: "Check-in bằng mã",
		manualTitle: "Check-in bằng mã đặt chỗ",
		manualOk: "Check-in",
		codePlaceholder: "Nhập mã đặt chỗ của khách",
		overrideLabel: "Đơn đã bị ghi \"không đến\" — đổi thành đã đến",
		reasonPlaceholder: "Lý do (bắt buộc, được ghi vào nhật ký)",
		reasonRequired: "Đổi đơn không đến thành đã đến bắt buộc ghi lý do.",
		done: "Đã check-in cho khách.",
		already: "Đơn này đã check-in trước đó."
	}
};
var vi = {
	common: common,
	menu: menu,
	court: court
};

const courtTranslations = { en, vi };

const VenuesPage = () => {
  const { t, venues, venuesLoading, refreshVenues } = useCourtContext();
  const navigate = useNavigate();
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(
      PageHeader,
      {
        title: t("court.venues.title", { defaultValue: "C\u1EE5m s\xE2n" }),
        description: t("court.venues.pageDescription", {
          defaultValue: "Danh s\xE1ch c\u1EE5m s\xE2n (venue) c\u1EE7a t\u1ED5 ch\u1EE9c \u2014 m\u1ED7i c\u1EE5m s\xE2n qu\u1EA3n l\xFD ri\xEAng danh s\xE1ch s\xE2n v\xE0 khung gi\xE1."
        }),
        extra: /* @__PURE__ */ jsx(Button, { onClick: () => void refreshVenues(), children: t("common.refresh", { defaultValue: "T\u1EA3i l\u1EA1i" }) })
      }
    ),
    /* @__PURE__ */ jsx(
      Table,
      {
        rowKey: "supplierId",
        loading: venuesLoading,
        dataSource: venues,
        pagination: false,
        locale: { emptyText: t("court.venues.empty", { defaultValue: "T\u1ED5 ch\u1EE9c ch\u01B0a c\xF3 c\u1EE5m s\xE2n n\xE0o." }) },
        columns: [
          { title: t("court.venues.nameColumn", { defaultValue: "T\xEAn c\u1EE5m s\xE2n" }), dataIndex: "name", key: "name" },
          {
            title: t("court.venues.eventSlugColumn", { defaultValue: "S\u1EF1 ki\u1EC7n g\u1EAFn k\xE8m" }),
            dataIndex: "eventSlug",
            key: "eventSlug",
            render: (eventSlug) => /* @__PURE__ */ jsx(Tag, { color: "blue", children: eventSlug })
          },
          {
            title: "",
            key: "action",
            render: (_, record) => /* @__PURE__ */ jsxs(Space, { children: [
              /* @__PURE__ */ jsx(Button, { size: "small", onClick: () => navigate(`${record.supplierId}/courts`), children: t("court.venues.manageCourtsButton", { defaultValue: "Qu\u1EA3n l\xFD s\xE2n" }) }),
              /* @__PURE__ */ jsx(Button, { size: "small", onClick: () => navigate(`${record.supplierId}/bookings`), children: t("court.venues.viewBookingsButton", { defaultValue: "Xem l\u1ECBch \u0111\u1EB7t" }) }),
              /* @__PURE__ */ jsx(Button, { size: "small", onClick: () => navigate(`${record.supplierId}/settings`), children: t("court.venues.settingsButton", { defaultValue: "C\xE0i \u0111\u1EB7t" }) }),
              /* @__PURE__ */ jsx(Button, { size: "small", onClick: () => navigate(`${record.supplierId}/staff`), children: t("court.venues.staffButton", { defaultValue: "Nh\xE2n vi\xEAn" }) })
            ] })
          }
        ]
      }
    )
  ] });
};

const TIME_FORMAT$2 = "HH:mm";
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const TIMEZONES = ["Asia/Ho_Chi_Minh", "Asia/Bangkok", "Asia/Singapore", "Asia/Tokyo", "UTC"];
const CourtCalendarModal = ({ supplierId, court, onClose, onSaved }) => {
  const { t, api } = useCourtContext();
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const weekdayLabel = (w) => [
    t("court.calendar.sunday", { defaultValue: "Ch\u1EE7 nh\u1EADt" }),
    t("court.calendar.monday", { defaultValue: "Th\u1EE9 hai" }),
    t("court.calendar.tuesday", { defaultValue: "Th\u1EE9 ba" }),
    t("court.calendar.wednesday", { defaultValue: "Th\u1EE9 t\u01B0" }),
    t("court.calendar.thursday", { defaultValue: "Th\u1EE9 n\u0103m" }),
    t("court.calendar.friday", { defaultValue: "Th\u1EE9 s\xE1u" }),
    t("court.calendar.saturday", { defaultValue: "Th\u1EE9 b\u1EA3y" })
  ][w];
  useEffect(() => {
    if (!court) return;
    form.setFieldsValue({ timezone: court.timezone || "Asia/Ho_Chi_Minh", minMinutes: court.minMinutes || 0, stepMinutes: court.stepMinutes || 0 });
    void (async () => {
      try {
        const saved = await api.request(supplierId, `open-hours?resource_id=${encodeURIComponent(court.resourceId)}`, void 0, "GET") ?? [];
        setRows(
          WEEKDAY_ORDER.map((w) => {
            const s = saved.find((x) => x.weekday === w);
            return s ? { weekday: w, custom: true, closed: s.closed, open: s.openTime ? dayjs(s.openTime, TIME_FORMAT$2) : void 0, close: s.closeTime ? dayjs(s.closeTime, TIME_FORMAT$2) : void 0 } : { weekday: w, custom: false, closed: false };
          })
        );
      } catch (err) {
        if (err instanceof Error) message.error(err.message);
      }
    })();
  }, [court, api, supplierId, form]);
  const patch = (weekday, change) => setRows((prev) => prev.map((r) => r.weekday === weekday ? { ...r, ...change } : r));
  const handleSave = async () => {
    if (!court) return;
    const values = await form.validateFields();
    for (const r of rows) {
      if (r.custom && !r.closed && (!r.open || !r.close || !r.close.isAfter(r.open))) {
        message.error(t("court.calendar.hoursInvalid", { defaultValue: "Gi\u1EDD \u0111\xF3ng c\u1EEDa ph\u1EA3i sau gi\u1EDD m\u1EDF c\u1EEDa ({{day}}).", day: weekdayLabel(r.weekday) }));
        return;
      }
    }
    setSaving(true);
    try {
      await api.request(supplierId, `courts/${court.id}/calendar`, { timezone: values.timezone, min_minutes: values.minMinutes ?? 0, step_minutes: values.stepMinutes ?? 0 }, "PUT");
      await api.request(
        supplierId,
        "open-hours",
        {
          resource_id: court.resourceId,
          hours: rows.filter((r) => r.custom).map((r) => ({ weekday: r.weekday, closed: r.closed, open_time: r.closed ? void 0 : r.open?.format(TIME_FORMAT$2), close_time: r.closed ? void 0 : r.close?.format(TIME_FORMAT$2) }))
        },
        "PUT"
      );
      message.success(t("court.calendar.saved", { defaultValue: "\u0110\xE3 l\u01B0u l\u1ECBch v\u1EADn h\xE0nh" }));
      onSaved();
      onClose();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  return /* @__PURE__ */ jsxs(
    Modal,
    {
      open: !!court,
      title: t("court.calendar.title", { defaultValue: "L\u1ECBch v\u1EADn h\xE0nh \u2014 {{name}}", name: court?.name ?? "" }),
      onOk: () => void handleSave(),
      onCancel: onClose,
      confirmLoading: saving,
      width: 680,
      destroyOnClose: true,
      children: [
        /* @__PURE__ */ jsx(
          Alert,
          {
            type: "info",
            showIcon: true,
            style: { marginBottom: 16 },
            message: t("court.calendar.hint", {
              defaultValue: "Gi\u1EDD m\u1EDF/\u0111\xF3ng v\xE0 gi\u1EDD trong khung gi\xE1 l\xE0 gi\u1EDD \u0111\u1ECBa ph\u01B0\u01A1ng theo m\xFAi gi\u1EDD c\u1EE7a s\xE2n. Th\u1EE9 kh\xF4ng \u0111\u1EB7t gi\u1EDD ri\xEAng d\xF9ng gi\u1EDD m\u1EB7c \u0111\u1ECBnh c\u1EE7a s\xE2n ({{open}}\u2013{{close}}).",
              open: court?.openTime ?? "",
              close: court?.closeTime ?? ""
            })
          }
        ),
        /* @__PURE__ */ jsx(Form, { form, layout: "vertical", children: /* @__PURE__ */ jsxs(Space, { size: "large", wrap: true, align: "start", children: [
          /* @__PURE__ */ jsx(Form.Item, { name: "timezone", label: t("court.calendar.timezone", { defaultValue: "M\xFAi gi\u1EDD" }), style: { minWidth: 220 }, children: /* @__PURE__ */ jsx(Select, { options: TIMEZONES.map((z) => ({ value: z, label: z })) }) }),
          /* @__PURE__ */ jsx(
            Form.Item,
            {
              name: "minMinutes",
              label: t("court.calendar.minMinutes", { defaultValue: "\u0110\u1EB7t t\u1ED1i thi\u1EC3u (ph\xFAt)" }),
              tooltip: t("court.calendar.minHelp", { defaultValue: "0 = kh\xF4ng gi\u1EDBi h\u1EA1n. V\xED d\u1EE5 60: m\u1ED7i l\u01B0\u1EE3t \u0111\u1EB7t \xEDt nh\u1EA5t 60 ph\xFAt." }),
              children: /* @__PURE__ */ jsx(InputNumber, { min: 0, max: 1440, step: 15, precision: 0 })
            }
          ),
          /* @__PURE__ */ jsx(
            Form.Item,
            {
              name: "stepMinutes",
              label: t("court.calendar.stepMinutes", { defaultValue: "B\u01B0\u1EDBc c\u1ED9ng th\xEAm (ph\xFAt)" }),
              tooltip: t("court.calendar.stepHelp", { defaultValue: "0 = kh\xF4ng gi\u1EDBi h\u1EA1n. V\xED d\u1EE5 30: th\u1EDDi l\u01B0\u1EE3ng = t\u1ED1i thi\u1EC3u + b\u1ED9i s\u1ED1 c\u1EE7a 30 ph\xFAt." }),
              children: /* @__PURE__ */ jsx(InputNumber, { min: 0, max: 1440, step: 15, precision: 0 })
            }
          )
        ] }) }),
        /* @__PURE__ */ jsx(
          Table,
          {
            size: "small",
            rowKey: "weekday",
            pagination: false,
            dataSource: rows,
            columns: [
              { title: t("court.calendar.day", { defaultValue: "Th\u1EE9" }), key: "day", width: 110, render: (_, r) => weekdayLabel(r.weekday) },
              {
                title: t("court.calendar.customHours", { defaultValue: "Gi\u1EDD ri\xEAng" }),
                key: "custom",
                width: 90,
                render: (_, r) => /* @__PURE__ */ jsx(Switch, { size: "small", checked: r.custom, onChange: (v) => patch(r.weekday, { custom: v, closed: false }) })
              },
              {
                title: t("court.calendar.closedDay", { defaultValue: "Ngh\u1EC9 c\u1EA3 ng\xE0y" }),
                key: "closed",
                width: 110,
                render: (_, r) => /* @__PURE__ */ jsx(Switch, { size: "small", disabled: !r.custom, checked: r.custom && r.closed, onChange: (v) => patch(r.weekday, { closed: v }) })
              },
              {
                title: t("court.calendar.hours", { defaultValue: "Gi\u1EDD m\u1EDF \u2013 \u0111\xF3ng" }),
                key: "hours",
                render: (_, r) => r.custom && !r.closed ? /* @__PURE__ */ jsxs(Space, { children: [
                  /* @__PURE__ */ jsx(TimePicker, { size: "small", format: TIME_FORMAT$2, minuteStep: 15, allowClear: false, value: r.open, onChange: (v) => patch(r.weekday, { open: v ?? void 0 }) }),
                  /* @__PURE__ */ jsx(TimePicker, { size: "small", format: TIME_FORMAT$2, minuteStep: 15, allowClear: false, value: r.close, onChange: (v) => patch(r.weekday, { close: v ?? void 0 }) })
                ] }) : r.closed ? t("court.calendar.closedLabel", { defaultValue: "Ngh\u1EC9" }) : `${court?.openTime ?? ""} \u2013 ${court?.closeTime ?? ""}`
              }
            ]
          }
        )
      ]
    }
  );
};

const CONCURRENCY_LIMIT$1 = 8;
async function settleWithConcurrency$1(items, limit, fn) {
  const results = new Array(items.length);
  let cursor = 0;
  const workers = new Array(Math.min(limit, items.length)).fill(null).map(async () => {
    while (cursor < items.length) {
      const idx = cursor++;
      try {
        results[idx] = { status: "fulfilled", value: await fn(items[idx], idx) };
      } catch (error) {
        results[idx] = { status: "rejected", reason: error };
      }
    }
  });
  await Promise.all(workers);
  return results;
}
function slugify(input) {
  return input.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/g, "d").replace(/Đ/g, "D").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}
const CourtsPage = () => {
  const { t, api, onCreateCourt } = useCourtContext();
  const navigate = useNavigate();
  const { supplierId } = useParams();
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [editCourt, setEditCourt] = useState(null);
  const [editForm] = Form.useForm();
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm] = Form.useForm();
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkErrors, setBulkErrors] = useState([]);
  const [priceRulesByResource, setPriceRulesByResource] = useState({});
  const [copySourceCourt, setCopySourceCourt] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [calendarCourt, setCalendarCourt] = useState(null);
  const load = useCallback(async () => {
    if (!supplierId) return;
    setLoading(true);
    try {
      const resp = await api.request(supplierId, "courts", void 0, "GET");
      const activeCourts = (resp ?? []).filter((c) => c.active);
      setCourts(resp ?? []);
      const results = await settleWithConcurrency$1(
        activeCourts,
        CONCURRENCY_LIMIT$1,
        (court) => api.request(supplierId, `price-rules?resource_id=${encodeURIComponent(court.resourceId)}`, void 0, "GET")
      );
      const nextPriceRules = {};
      results.forEach((r, idx) => {
        if (r.status === "fulfilled") nextPriceRules[activeCourts[idx].resourceId] = r.value ?? [];
      });
      setPriceRulesByResource(nextPriceRules);
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [api, supplierId]);
  useEffect(() => {
    void load();
  }, [load]);
  const handleCreate = async () => {
    if (!supplierId || !onCreateCourt) return;
    try {
      const values = await createForm.validateFields();
      await onCreateCourt(Number(supplierId), {
        resource_id: values.resourceId,
        name: values.name,
        sport_type: values.sportType,
        slot_minutes: values.slotMinutes,
        open_time: values.openTime,
        close_time: values.closeTime,
        currency: values.currency
      });
      message.success(t("court.courts.created", { defaultValue: "\u0110\xE3 t\u1EA1o s\xE2n" }));
      createForm.resetFields();
      setCreateOpen(false);
      await load();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    }
  };
  const handleBulkCreate = async () => {
    if (!supplierId || !onCreateCourt) return;
    const values = await bulkForm.validateFields();
    const slug = slugify(values.resourceIdPrefix || values.namePrefix);
    if (!slug) {
      message.error(t("court.courts.bulkSlugRequired", { defaultValue: "Kh\xF4ng th\u1EC3 t\u1EA1o m\xE3 s\xE2n t\u1EEB t\xEAn \u2014 nh\u1EADp ti\u1EC1n t\u1ED1 m\xE3 s\xE2n ri\xEAng." }));
      return;
    }
    setBulkRunning(true);
    setBulkErrors([]);
    const errors = [];
    let created = 0;
    for (let i = 0; i < values.count; i += 1) {
      const idx = values.startIndex + i;
      try {
        await onCreateCourt(Number(supplierId), {
          resource_id: `${slug}-${idx}`,
          name: `${values.namePrefix} ${idx}`,
          sport_type: values.sportType,
          slot_minutes: values.slotMinutes,
          open_time: values.openTime,
          close_time: values.closeTime,
          currency: values.currency
        });
        created += 1;
      } catch (err) {
        errors.push(`${values.namePrefix} ${idx}: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
    setBulkRunning(false);
    setBulkErrors(errors);
    if (created > 0) {
      message.success(t("court.courts.bulkCreatedSummary", { defaultValue: `\u0110\xE3 t\u1EA1o ${created}/${values.count} s\xE2n` }));
    }
    await load();
    if (errors.length === 0) {
      bulkForm.resetFields();
      setBulkOpen(false);
    }
  };
  const openEdit = (court) => {
    setEditCourt(court);
    editForm.setFieldsValue({ name: court.name, openTime: court.openTime, closeTime: court.closeTime });
  };
  const handleUpdate = async () => {
    if (!supplierId || !editCourt) return;
    try {
      const values = await editForm.validateFields();
      await api.request(supplierId, `courts/${editCourt.id}`, { name: values.name, open_time: values.openTime, close_time: values.closeTime }, "PUT");
      message.success(t("court.courts.updated", { defaultValue: "\u0110\xE3 c\u1EADp nh\u1EADt s\xE2n" }));
      setEditCourt(null);
      await load();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    }
  };
  const handleDeactivate = async (court) => {
    if (!supplierId) return;
    try {
      await api.request(supplierId, `courts/${court.id}`, void 0, "DELETE");
      message.success(t("court.courts.deactivated", { defaultValue: "\u0110\xE3 ng\u1EEBng ho\u1EA1t \u0111\u1ED9ng s\xE2n" }));
      await load();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    }
  };
  const handleReactivate = async (court) => {
    if (!supplierId) return;
    try {
      await api.request(supplierId, `courts/${court.id}/reactivate`, void 0, "POST");
      message.success(t("court.courts.reactivated", { defaultValue: "\u0110\xE3 k\xEDch ho\u1EA1t l\u1EA1i s\xE2n" }));
      await load();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    }
  };
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(
      PageHeader,
      {
        title: t("court.courts.title", { defaultValue: "Danh s\xE1ch s\xE2n" }),
        description: t("court.courts.pageDescription", {
          defaultValue: "S\xE2n trong c\u1EE5m s\xE2n n\xE0y \u2014 t\u1EA1o m\u1EDBi, s\u1EEDa gi\u1EDD m\u1EDF/\u0111\xF3ng c\u1EEDa, ng\u1EEBng ho\u1EB7c k\xEDch ho\u1EA1t l\u1EA1i."
        }),
        extra: /* @__PURE__ */ jsxs(Space, { children: [
          /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(ArrowLeftOutlined, {}), onClick: () => navigate("../..", { relative: "path" }), children: t("court.courts.backToVenues", { defaultValue: "Quay l\u1EA1i danh s\xE1ch c\u1EE5m s\xE2n" }) }),
          /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(CalendarOutlined, {}), onClick: () => navigate("../bookings", { relative: "path" }), children: t("court.venues.viewBookingsButton", { defaultValue: "Xem l\u1ECBch \u0111\u1EB7t" }) }),
          /* @__PURE__ */ jsx(Button, { onClick: () => navigate("../rentals", { relative: "path" }), children: t("court.rentals.title", { defaultValue: "Thu\xEA s\xE2n s\u1EF1 ki\u1EC7n" }) }),
          /* @__PURE__ */ jsx(Button, { onClick: () => navigate("../reports", { relative: "path" }), children: t("court.reports.title", { defaultValue: "B\xE1o c\xE1o s\xE2n" }) }),
          /* @__PURE__ */ jsx(Button, { onClick: () => navigate("../refunds", { relative: "path" }), children: t("court.refunds.title", { defaultValue: "Ho\xE0n ti\u1EC1n ch\u1EDD x\u1EED l\xFD" }) }),
          /* @__PURE__ */ jsx(Button, { onClick: () => navigate("../deposits", { relative: "path" }), children: t("court.deposits.title", { defaultValue: "C\u1ECDc ch\u1EDD x\xE1c nh\u1EADn" }) }),
          /* @__PURE__ */ jsx(Button, { onClick: () => navigate("../closures", { relative: "path" }), children: t("court.closures.title", { defaultValue: "\u0110\xF3ng c\u1EEDa / b\u1EA3o tr\xEC" }) }),
          /* @__PURE__ */ jsx(Tooltip, { title: !onCreateCourt ? t("court.courts.createDisabledTooltip", { defaultValue: "T\xEDnh n\u0103ng th\xEAm s\xE2n m\u1EDBi ch\u01B0a \u0111\u01B0\u1EE3c host c\u1EA5u h\xECnh." }) : void 0, children: /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(OrderedListOutlined, {}), disabled: !onCreateCourt, onClick: () => setBulkOpen(true), children: t("court.courts.bulkCreateButton", { defaultValue: "T\u1EA1o h\xE0ng lo\u1EA1t" }) }) }),
          /* @__PURE__ */ jsx(Tooltip, { title: !onCreateCourt ? t("court.courts.createDisabledTooltip", { defaultValue: "T\xEDnh n\u0103ng th\xEAm s\xE2n m\u1EDBi ch\u01B0a \u0111\u01B0\u1EE3c host c\u1EA5u h\xECnh." }) : void 0, children: /* @__PURE__ */ jsx(Button, { type: "primary", icon: /* @__PURE__ */ jsx(PlusOutlined, {}), disabled: !onCreateCourt, onClick: () => setCreateOpen(true), children: t("common.add", { defaultValue: "Th\xEAm" }) }) })
        ] })
      }
    ),
    /* @__PURE__ */ jsx(
      Table,
      {
        rowKey: "id",
        loading,
        dataSource: courts,
        pagination: false,
        locale: { emptyText: t("court.courts.empty", { defaultValue: "C\u1EE5m s\xE2n n\xE0y ch\u01B0a c\xF3 s\xE2n n\xE0o." }) },
        columns: [
          { title: t("court.courts.resourceIdColumn", { defaultValue: "M\xE3 s\xE2n" }), dataIndex: "resourceId", key: "resourceId" },
          { title: t("court.courts.nameColumn", { defaultValue: "T\xEAn s\xE2n" }), dataIndex: "name", key: "name" },
          { title: t("court.courts.sportTypeColumn", { defaultValue: "M\xF4n th\u1EC3 thao" }), dataIndex: "sportType", key: "sportType" },
          { title: t("court.courts.slotMinutesColumn", { defaultValue: "\u0110\u1ED9 d\xE0i slot (ph\xFAt)" }), dataIndex: "slotMinutes", key: "slotMinutes" },
          { title: t("court.courts.openTimeColumn", { defaultValue: "Gi\u1EDD m\u1EDF c\u1EEDa" }), dataIndex: "openTime", key: "openTime" },
          { title: t("court.courts.closeTimeColumn", { defaultValue: "Gi\u1EDD \u0111\xF3ng c\u1EEDa" }), dataIndex: "closeTime", key: "closeTime" },
          { title: t("court.courts.currencyColumn", { defaultValue: "\u0110\u01A1n v\u1ECB ti\u1EC1n" }), dataIndex: "currency", key: "currency" },
          {
            // [Thêm — xem plan "CourtsPage: hiện tóm tắt khung giá..."] tóm tắt NGẮN GỌN, không thay nút
            // "Khung giá" (vẫn là lối vào trang chi tiết).
            title: t("court.courts.priceRulesColumn", { defaultValue: "Khung gi\xE1" }),
            key: "priceRules",
            render: (_, record) => {
              const activeRules = (priceRulesByResource[record.resourceId] ?? []).filter((r) => r.active);
              if (activeRules.length === 0) return t("court.courts.priceRulesEmpty", { defaultValue: "\u2014" });
              const prices = activeRules.map((r) => r.priceMinor);
              const min = Math.min(...prices);
              const max = Math.max(...prices);
              const priceLabel = min === max ? `${min.toLocaleString("vi-VN")}\u0111` : `${min.toLocaleString("vi-VN")}\u0111\u2013${max.toLocaleString("vi-VN")}\u0111`;
              return t("court.courts.priceRulesSummary", { defaultValue: `${activeRules.length} khung \xB7 ${priceLabel}` });
            }
          },
          {
            title: t("court.courts.statusColumn", { defaultValue: "Tr\u1EA1ng th\xE1i" }),
            dataIndex: "active",
            key: "active",
            render: (active) => active ? /* @__PURE__ */ jsx(Tag, { color: "green", children: t("common.active", { defaultValue: "Ho\u1EA1t \u0111\u1ED9ng" }) }) : /* @__PURE__ */ jsx(Tag, { color: "red", children: t("common.inactive", { defaultValue: "Ng\u1EEBng" }) })
          },
          {
            title: "",
            key: "action",
            render: (_, record) => {
              const hasActivePriceRules = (priceRulesByResource[record.resourceId] ?? []).some((r) => r.active);
              const items = [
                {
                  key: "pricing",
                  icon: /* @__PURE__ */ jsx(DollarOutlined, {}),
                  label: t("court.courts.priceRulesButton", { defaultValue: "Khung gi\xE1" }),
                  onClick: () => navigate(`${record.resourceId}/pricing`)
                },
                {
                  key: "calendar",
                  icon: /* @__PURE__ */ jsx(CalendarOutlined, {}),
                  label: t("court.courts.calendarButton", { defaultValue: "L\u1ECBch v\u1EADn h\xE0nh" }),
                  onClick: () => setCalendarCourt(record)
                },
                {
                  key: "copyPricing",
                  icon: /* @__PURE__ */ jsx(CopyOutlined, {}),
                  label: t("court.courts.copyPricingButton", { defaultValue: "Sao ch\xE9p gi\xE1" }),
                  disabled: !hasActivePriceRules,
                  title: !hasActivePriceRules ? t("court.courts.copyPricingDisabledTooltip", { defaultValue: "S\xE2n n\xE0y ch\u01B0a c\xF3 khung gi\xE1 n\xE0o \u0111\u1EC3 sao ch\xE9p." }) : void 0,
                  onClick: () => setCopySourceCourt(record)
                },
                {
                  key: "edit",
                  icon: /* @__PURE__ */ jsx(EditOutlined, {}),
                  label: t("common.edit", { defaultValue: "S\u1EEDa" }),
                  onClick: () => openEdit(record)
                },
                { type: "divider" },
                record.active ? {
                  key: "deactivate",
                  danger: true,
                  label: t("common.deactivate", { defaultValue: "Ng\u1EEBng ho\u1EA1t \u0111\u1ED9ng" }),
                  onClick: () => setDeactivateTarget(record)
                } : {
                  key: "reactivate",
                  label: t("common.reactivate", { defaultValue: "K\xEDch ho\u1EA1t l\u1EA1i" }),
                  onClick: () => void handleReactivate(record)
                }
              ];
              return /* @__PURE__ */ jsx(Dropdown, { menu: { items }, trigger: ["click"], children: /* @__PURE__ */ jsx(Button, { size: "small", icon: /* @__PURE__ */ jsx(MoreOutlined, {}) }) });
            }
          }
        ]
      }
    ),
    /* @__PURE__ */ jsx(CourtCalendarModal, { supplierId: supplierId ?? "", court: calendarCourt, onClose: () => setCalendarCourt(null), onSaved: () => void load() }),
    /* @__PURE__ */ jsx(
      Modal,
      {
        title: t("court.courts.createTitle", { defaultValue: "Th\xEAm s\xE2n m\u1EDBi" }),
        open: createOpen,
        onOk: handleCreate,
        onCancel: () => {
          createForm.resetFields();
          setCreateOpen(false);
        },
        destroyOnClose: true,
        children: /* @__PURE__ */ jsxs(Form, { form: createForm, layout: "vertical", children: [
          /* @__PURE__ */ jsx(Form.Item, { name: "resourceId", label: t("court.courts.resourceIdFieldLabel", { defaultValue: "M\xE3 s\xE2n (resource_id)" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, { placeholder: t("court.courts.resourceIdPlaceholder", { defaultValue: "vd: court-1" }) }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "name", label: t("court.courts.nameFieldLabel", { defaultValue: "T\xEAn s\xE2n" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, {}) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "sportType", label: t("court.courts.sportTypeFieldLabel", { defaultValue: "M\xF4n th\u1EC3 thao" }), rules: [{ required: true }], initialValue: "pickleball", children: /* @__PURE__ */ jsx(Input, { placeholder: t("court.courts.sportTypePlaceholder", { defaultValue: "vd: pickleball" }) }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "slotMinutes", label: t("court.courts.slotMinutesFieldLabel", { defaultValue: "\u0110\u1ED9 d\xE0i slot (ph\xFAt)" }), rules: [{ required: true }], initialValue: 60, children: /* @__PURE__ */ jsx(InputNumber, { min: 1, style: { width: "100%" } }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "openTime", label: t("court.courts.openTimeFieldLabel", { defaultValue: "Gi\u1EDD m\u1EDF c\u1EEDa" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, { placeholder: "06:00" }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "closeTime", label: t("court.courts.closeTimeFieldLabel", { defaultValue: "Gi\u1EDD \u0111\xF3ng c\u1EEDa" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, { placeholder: "22:00" }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "currency", label: t("court.courts.currencyFieldLabel", { defaultValue: "\u0110\u01A1n v\u1ECB ti\u1EC1n" }), rules: [{ required: true }], initialValue: "VND", children: /* @__PURE__ */ jsx(Input, { placeholder: t("court.courts.currencyPlaceholder", { defaultValue: "vd: VND" }) }) })
        ] })
      }
    ),
    /* @__PURE__ */ jsxs(
      Modal,
      {
        title: t("court.courts.bulkCreateTitle", { defaultValue: "T\u1EA1o h\xE0ng lo\u1EA1t s\xE2n" }),
        open: bulkOpen,
        onOk: () => void handleBulkCreate(),
        onCancel: () => {
          if (bulkRunning) return;
          bulkForm.resetFields();
          setBulkErrors([]);
          setBulkOpen(false);
        },
        confirmLoading: bulkRunning,
        okText: t("court.courts.bulkCreateSubmit", { defaultValue: "T\u1EA1o" }),
        destroyOnClose: true,
        children: [
          /* @__PURE__ */ jsxs(Form, { form: bulkForm, layout: "vertical", children: [
            /* @__PURE__ */ jsx(
              Form.Item,
              {
                name: "namePrefix",
                label: t("court.courts.bulkNamePrefixLabel", { defaultValue: "Ti\u1EC1n t\u1ED1 t\xEAn s\xE2n" }),
                rules: [{ required: true }],
                tooltip: t("court.courts.bulkNamePrefixHelp", { defaultValue: 'T\xEAn t\u1EEBng s\xE2n s\u1EBD l\xE0 "Ti\u1EC1n t\u1ED1 + s\u1ED1 th\u1EE9 t\u1EF1", vd "S\xE2n 1", "S\xE2n 2"...' }),
                children: /* @__PURE__ */ jsx(Input, { placeholder: t("court.courts.bulkNamePrefixPlaceholder", { defaultValue: "vd: S\xE2n" }) })
              }
            ),
            /* @__PURE__ */ jsx(
              Form.Item,
              {
                name: "resourceIdPrefix",
                label: t("court.courts.bulkResourceIdPrefixLabel", { defaultValue: "Ti\u1EC1n t\u1ED1 m\xE3 s\xE2n (tu\u1EF3 ch\u1ECDn)" }),
                tooltip: t("court.courts.bulkResourceIdPrefixHelp", { defaultValue: '\u0110\u1EC3 tr\u1ED1ng s\u1EBD t\u1EF1 sinh t\u1EEB t\xEAn s\xE2n. M\xE3 s\xE2n cu\u1ED1i c\xF9ng: "ti\u1EC1n-t\u1ED1-<s\u1ED1 th\u1EE9 t\u1EF1>".' }),
                children: /* @__PURE__ */ jsx(Input, { placeholder: t("court.courts.bulkResourceIdPrefixPlaceholder", { defaultValue: "vd: san (m\u1EB7c \u0111\u1ECBnh l\u1EA5y t\u1EEB t\xEAn s\xE2n)" }) })
              }
            ),
            /* @__PURE__ */ jsxs(Space, { style: { width: "100%" }, size: "middle", children: [
              /* @__PURE__ */ jsx(
                Form.Item,
                {
                  name: "startIndex",
                  label: t("court.courts.bulkStartIndexLabel", { defaultValue: "B\u1EAFt \u0111\u1EA7u t\u1EEB s\u1ED1" }),
                  rules: [{ required: true }],
                  initialValue: 1,
                  tooltip: t("court.courts.bulkStartIndexHelp", { defaultValue: "\u0110\u1ED5i s\u1ED1 n\xE0y n\u1EBFu t\u1EA1o ti\u1EBFp l\xF4 sau (vd \u0111\xE3 c\xF3 S\xE2n 1-5, t\u1EA1o ti\u1EBFp th\xEC b\u1EAFt \u0111\u1EA7u t\u1EEB 6) \u2014 tr\xE1nh tr\xF9ng m\xE3 s\xE2n." }),
                  children: /* @__PURE__ */ jsx(InputNumber, { min: 1, style: { width: 160 } })
                }
              ),
              /* @__PURE__ */ jsx(Form.Item, { name: "count", label: t("court.courts.bulkCountLabel", { defaultValue: "S\u1ED1 l\u01B0\u1EE3ng s\xE2n" }), rules: [{ required: true }], initialValue: 5, children: /* @__PURE__ */ jsx(InputNumber, { min: 1, max: 50, style: { width: 160 } }) })
            ] }),
            /* @__PURE__ */ jsx(Form.Item, { name: "sportType", label: t("court.courts.sportTypeFieldLabel", { defaultValue: "M\xF4n th\u1EC3 thao" }), rules: [{ required: true }], initialValue: "pickleball", children: /* @__PURE__ */ jsx(Input, { placeholder: t("court.courts.sportTypePlaceholder", { defaultValue: "vd: pickleball" }) }) }),
            /* @__PURE__ */ jsx(Form.Item, { name: "slotMinutes", label: t("court.courts.slotMinutesFieldLabel", { defaultValue: "\u0110\u1ED9 d\xE0i slot (ph\xFAt)" }), rules: [{ required: true }], initialValue: 60, children: /* @__PURE__ */ jsx(InputNumber, { min: 1, style: { width: "100%" } }) }),
            /* @__PURE__ */ jsx(Form.Item, { name: "openTime", label: t("court.courts.openTimeFieldLabel", { defaultValue: "Gi\u1EDD m\u1EDF c\u1EEDa" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, { placeholder: "06:00" }) }),
            /* @__PURE__ */ jsx(Form.Item, { name: "closeTime", label: t("court.courts.closeTimeFieldLabel", { defaultValue: "Gi\u1EDD \u0111\xF3ng c\u1EEDa" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, { placeholder: "22:00" }) }),
            /* @__PURE__ */ jsx(Form.Item, { name: "currency", label: t("court.courts.currencyFieldLabel", { defaultValue: "\u0110\u01A1n v\u1ECB ti\u1EC1n" }), rules: [{ required: true }], initialValue: "VND", children: /* @__PURE__ */ jsx(Input, { placeholder: t("court.courts.currencyPlaceholder", { defaultValue: "vd: VND" }) }) })
          ] }),
          bulkErrors.length > 0 && /* @__PURE__ */ jsx(
            Alert,
            {
              type: "warning",
              showIcon: true,
              message: t("court.courts.bulkPartialErrorTitle", { defaultValue: "M\u1ED9t s\u1ED1 s\xE2n t\u1EA1o th\u1EA5t b\u1EA1i" }),
              description: /* @__PURE__ */ jsx("ul", { style: { margin: 0, paddingLeft: 20 }, children: bulkErrors.map((e) => /* @__PURE__ */ jsx("li", { children: e }, e)) }),
              style: { marginTop: 12 }
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      Modal,
      {
        title: t("court.courts.editTitle", { defaultValue: "S\u1EEDa th\xF4ng tin s\xE2n" }),
        open: !!editCourt,
        onOk: handleUpdate,
        onCancel: () => setEditCourt(null),
        destroyOnClose: true,
        children: /* @__PURE__ */ jsxs(Form, { form: editForm, layout: "vertical", children: [
          /* @__PURE__ */ jsx(Form.Item, { name: "name", label: t("court.courts.nameFieldLabel", { defaultValue: "T\xEAn s\xE2n" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, {}) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "openTime", label: t("court.courts.openTimeFieldLabel", { defaultValue: "Gi\u1EDD m\u1EDF c\u1EEDa" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, { placeholder: "06:00" }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "closeTime", label: t("court.courts.closeTimeFieldLabel", { defaultValue: "Gi\u1EDD \u0111\xF3ng c\u1EEDa" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, { placeholder: "22:00" }) })
        ] })
      }
    ),
    supplierId && copySourceCourt && /* @__PURE__ */ jsx(
      CopyPricingModal,
      {
        open: !!copySourceCourt,
        supplierId,
        sourceCourt: copySourceCourt,
        allCourts: courts,
        priceRulesByResource,
        onClose: () => setCopySourceCourt(null),
        onDone: () => {
          setCopySourceCourt(null);
          void load();
        }
      }
    ),
    /* @__PURE__ */ jsx(
      Modal,
      {
        title: t("court.courts.deactivateConfirmTitle", { defaultValue: "Ng\u1EEBng ho\u1EA1t \u0111\u1ED9ng s\xE2n n\xE0y?" }),
        open: !!deactivateTarget,
        onCancel: () => setDeactivateTarget(null),
        onOk: async () => {
          if (!deactivateTarget) return;
          await handleDeactivate(deactivateTarget);
          setDeactivateTarget(null);
        },
        okText: t("common.confirm", { defaultValue: "X\xE1c nh\u1EADn" }),
        cancelText: t("common.cancel", { defaultValue: "Hu\u1EF7" }),
        okButtonProps: { danger: true },
        children: /* @__PURE__ */ jsx("p", { children: t("court.courts.deactivateConfirmContent", { defaultValue: "S\xE2n s\u1EBD kh\xF4ng c\xF2n hi\u1EC3n th\u1ECB \u0111\u1EC3 \u0111\u1EB7t l\u1ECBch. C\xF3 th\u1EC3 k\xEDch ho\u1EA1t l\u1EA1i b\u1EA5t c\u1EE9 l\xFAc n\xE0o." }) })
      }
    )
  ] });
};
const CopyPricingModal = ({
  open,
  supplierId,
  sourceCourt,
  allCourts,
  priceRulesByResource,
  onClose,
  onDone
}) => {
  const { t, api } = useCourtContext();
  const [step, setStep] = useState("select");
  const [selectedIds, setSelectedIds] = useState([]);
  const [running, setRunning] = useState(false);
  const [errors, setErrors] = useState([]);
  const targetCourts = allCourts.filter((c) => c.active && c.resourceId !== sourceCourt.resourceId);
  const selectedTargets = targetCourts.filter((c) => selectedIds.includes(c.resourceId));
  const activeRulesOf = (resourceId) => (priceRulesByResource[resourceId] ?? []).filter((r) => r.active);
  const targetsWithExisting = selectedTargets.filter((c) => activeRulesOf(c.resourceId).length > 0);
  const runCopy = async () => {
    setRunning(true);
    setErrors([]);
    const sourceRules = activeRulesOf(sourceCourt.resourceId);
    const collected = [];
    let createdCount = 0;
    for (const target of selectedTargets) {
      for (const oldRule of activeRulesOf(target.resourceId)) {
        try {
          await api.request(supplierId, `price-rules/${oldRule.id}`, void 0, "DELETE");
        } catch (err) {
          collected.push(`${target.name} \xB7 ng\u01B0ng khung c\u0169 ${oldRule.startTime}-${oldRule.endTime}: ${err instanceof Error ? err.message : String(err)}`);
        }
      }
      for (const rule of sourceRules) {
        try {
          await api.request(
            supplierId,
            "price-rules",
            {
              resource_id: target.resourceId,
              weekdays: rule.weekdays?.length ? rule.weekdays : void 0,
              start_time: rule.startTime,
              end_time: rule.endTime,
              price_minor: rule.priceMinor,
              priority: rule.priority
            },
            "POST"
          );
          createdCount += 1;
        } catch (err) {
          collected.push(`${target.name} \xB7 ${rule.startTime}-${rule.endTime}: ${err instanceof Error ? err.message : String(err)}`);
        }
      }
    }
    setRunning(false);
    setErrors(collected);
    if (createdCount > 0) {
      message.success(t("court.courts.copyPricingSuccess", { defaultValue: `\u0110\xE3 sao ch\xE9p ${createdCount} khung gi\xE1` }));
    }
    if (collected.length === 0) {
      onDone();
    }
  };
  const handleSubmitSelect = () => {
    if (selectedTargets.length === 0) return;
    if (targetsWithExisting.length > 0) {
      setStep("confirmOverwrite");
      return;
    }
    void runCopy();
  };
  if (step === "confirmOverwrite") {
    return /* @__PURE__ */ jsxs(
      Modal,
      {
        open,
        title: t("court.courts.copyPricingOverwriteTitle", { defaultValue: "Ghi \u0111\xE8 khung gi\xE1 \u0111\xE3 c\xF3?" }),
        onCancel: () => setStep("select"),
        onOk: () => void runCopy(),
        confirmLoading: running,
        okText: t("court.courts.copyPricingOverwriteConfirm", { defaultValue: "Ghi \u0111\xE8" }),
        cancelText: t("common.cancel", { defaultValue: "Hu\u1EF7" }),
        okButtonProps: { danger: true },
        children: [
          /* @__PURE__ */ jsx("p", { children: t("court.courts.copyPricingOverwriteBody", {
            defaultValue: "C\xE1c s\xE2n sau \u0111\xE3 c\xF3 khung gi\xE1 \u2014 khung gi\xE1 C\u0168 s\u1EBD b\u1ECB ng\u01B0ng ho\u1EA1t \u0111\u1ED9ng tr\u01B0\u1EDBc khi t\u1EA1o khung gi\xE1 m\u1EDBi copy t\u1EEB s\xE2n ngu\u1ED3n:"
          }) }),
          /* @__PURE__ */ jsx("ul", { children: targetsWithExisting.map((c) => /* @__PURE__ */ jsxs("li", { children: [
            c.name,
            " (",
            activeRulesOf(c.resourceId).length,
            " khung)"
          ] }, c.resourceId)) })
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs(
    Modal,
    {
      open,
      title: t("court.courts.copyPricingTitle", { defaultValue: `Sao ch\xE9p khung gi\xE1 t\u1EEB "${sourceCourt.name}"` }),
      onCancel: onClose,
      onOk: handleSubmitSelect,
      confirmLoading: running,
      okText: t("court.courts.copyPricingSubmit", { defaultValue: "Sao ch\xE9p" }),
      okButtonProps: { disabled: selectedTargets.length === 0 },
      cancelText: t("common.cancel", { defaultValue: "Hu\u1EF7" }),
      destroyOnClose: true,
      children: [
        /* @__PURE__ */ jsx("p", { style: { color: "rgba(0,0,0,0.65)" }, children: t("court.courts.copyPricingDescription", {
          defaultValue: `S\u1EBD sao ch\xE9p ${activeRulesOf(sourceCourt.resourceId).length} khung gi\xE1 \u0111ang ho\u1EA1t \u0111\u1ED9ng sang c\xE1c s\xE2n \u0111\u01B0\u1EE3c ch\u1ECDn b\xEAn d\u01B0\u1EDBi.`
        }) }),
        /* @__PURE__ */ jsx(
          Checkbox.Group,
          {
            style: { display: "flex", flexDirection: "column", gap: 8 },
            value: selectedIds,
            onChange: (v) => setSelectedIds(v),
            children: targetCourts.map((c) => {
              const existing = activeRulesOf(c.resourceId).length;
              return /* @__PURE__ */ jsxs(Checkbox, { value: c.resourceId, children: [
                c.name,
                existing > 0 && /* @__PURE__ */ jsxs("span", { style: { color: "rgba(0,0,0,0.45)" }, children: [
                  " ",
                  "(",
                  t("court.courts.copyPricingExistingCount", { defaultValue: `\u0111\xE3 c\xF3 ${existing} khung gi\xE1` }),
                  ")"
                ] })
              ] }, c.resourceId);
            })
          }
        ),
        targetCourts.length === 0 && /* @__PURE__ */ jsx(Alert, { type: "info", showIcon: true, message: t("court.courts.copyPricingNoTargets", { defaultValue: "Kh\xF4ng c\xF3 s\xE2n n\xE0o kh\xE1c \u0111\u1EC3 sao ch\xE9p t\u1EDBi." }) }),
        errors.length > 0 && /* @__PURE__ */ jsx(
          Alert,
          {
            type: "warning",
            showIcon: true,
            style: { marginTop: 12 },
            message: t("court.courts.copyPricingPartialErrorTitle", { defaultValue: "M\u1ED9t s\u1ED1 khung gi\xE1 sao ch\xE9p th\u1EA5t b\u1EA1i" }),
            description: /* @__PURE__ */ jsx("ul", { style: { margin: 0, paddingLeft: 20 }, children: errors.map((e) => /* @__PURE__ */ jsx("li", { children: e }, e)) })
          }
        )
      ]
    }
  );
};

function formatThousands(value) {
  if (value === void 0 || value === null || value === "") return "";
  return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}
function parseThousands(value) {
  if (!value) return "";
  const digitsOnly = value.replace(/[^\d]/g, "");
  return digitsOnly === "" ? "" : Number(digitsOnly);
}

const TIME_FORMAT$1 = "HH:mm";
const CourtPricingPage = () => {
  const { t, api } = useCourtContext();
  const navigate = useNavigate();
  const { supplierId, resourceId } = useParams();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [editRule, setEditRule] = useState(null);
  const [editForm] = Form.useForm();
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleForm] = Form.useForm();
  const [scheduleRunning, setScheduleRunning] = useState(false);
  const [scheduleErrors, setScheduleErrors] = useState([]);
  const [court, setCourt] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingSaving, setBillingSaving] = useState(false);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [periodOccurrences, setPeriodOccurrences] = useState(4);
  const highestActivePrice = rules.filter((r) => r.active).reduce((max, r) => Math.max(max, r.priceMinor), 0);
  const loadCourt = useCallback(async () => {
    if (!supplierId || !resourceId) return;
    setBillingLoading(true);
    try {
      const courts = await api.request(supplierId, "courts", void 0, "GET");
      const found = (courts ?? []).find((c) => c.resourceId === resourceId) ?? null;
      setCourt(found);
      if (found) {
        setDiscountPercent(found.recurringDiscountPercent ?? 0);
        setPeriodOccurrences(found.recurringPeriodOccurrences ?? 4);
      }
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setBillingLoading(false);
    }
  }, [api, supplierId, resourceId]);
  useEffect(() => {
    void loadCourt();
  }, [loadCourt]);
  const handleSaveBilling = async () => {
    if (!supplierId || !court) return;
    setBillingSaving(true);
    try {
      await api.request(
        supplierId,
        `courts/${court.id}/recurring-billing`,
        { recurring_discount_percent: discountPercent, recurring_period_occurrences: periodOccurrences },
        "PUT"
      );
      message.success(t("court.pricing.billingSaved", { defaultValue: "\u0110\xE3 l\u01B0u c\u1EA5u h\xECnh g\xF3i \u0111\u1ECBnh k\u1EF3" }));
      await loadCourt();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setBillingSaving(false);
    }
  };
  const WEEKDAY_DEFAULTS = ["Ch\u1EE7 nh\u1EADt", "Th\u1EE9 hai", "Th\u1EE9 ba", "Th\u1EE9 t\u01B0", "Th\u1EE9 n\u0103m", "Th\u1EE9 s\xE1u", "Th\u1EE9 b\u1EA3y"];
  const weekdayLabels = WEEKDAY_DEFAULTS.map((defaultValue, weekday) => t(`court.pricing.weekday.${weekday}`, { defaultValue }));
  const load = useCallback(async () => {
    if (!supplierId || !resourceId) return;
    setLoading(true);
    try {
      const resp = await api.request(supplierId, `price-rules?resource_id=${encodeURIComponent(resourceId)}`, void 0, "GET");
      setRules(resp ?? []);
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [api, supplierId, resourceId]);
  useEffect(() => {
    void load();
  }, [load]);
  const handleCreate = async () => {
    if (!supplierId || !resourceId) return;
    try {
      const values = await createForm.validateFields();
      const weekdays = values.weekdays?.length ? values.weekdays : void 0;
      await api.request(
        supplierId,
        "price-rules",
        {
          resource_id: resourceId,
          weekdays,
          start_time: values.startTime.format(TIME_FORMAT$1),
          end_time: values.endTime.format(TIME_FORMAT$1),
          price_minor: values.priceMinor,
          priority: values.priority,
          // Giá ngày lễ/đặc biệt (task 099 B2): khoảng ngày địa phương, gồm cả hai đầu.
          ...values.dateRange ? { date_from: values.dateRange[0].format("YYYY-MM-DD"), date_to: values.dateRange[1].format("YYYY-MM-DD"), label: values.label || void 0 } : {}
        },
        "POST"
      );
      message.success(t("court.pricing.created", { defaultValue: "\u0110\xE3 t\u1EA1o khung gi\xE1" }));
      createForm.resetFields();
      setCreateOpen(false);
      await load();
    } catch (err) {
      if (err instanceof Error) message.error(isConflict(err) ? t("court.pricing.conflictError", { defaultValue: "Khung gi\xE1 b\u1ECB tr\xF9ng \u01B0u ti\xEAn v\xE0 ch\u1ED3ng th\u1EDDi gian v\u1EDBi 1 khung gi\xE1 kh\xE1c \u0111ang ho\u1EA1t \u0111\u1ED9ng." }) : err.message);
    }
  };
  const handleCreateSchedule = async () => {
    if (!supplierId || !resourceId) return;
    const values = await scheduleForm.validateFields();
    const weekdays = values.weekdays?.length ? values.weekdays : void 0;
    const breakpoints = (values.breakpoints ?? []).filter((b) => b?.time).slice().sort((a, b) => a.time.diff(b.time));
    if (breakpoints.length < 2) {
      message.error(t("court.pricing.scheduleNeedAtLeast2", { defaultValue: "C\u1EA7n \xEDt nh\u1EA5t 2 m\u1ED1c gi\u1EDD (1 khung gi\xE1)." }));
      return;
    }
    for (let i = 0; i < breakpoints.length - 1; i += 1) {
      if (breakpoints[i].priceMinor === void 0 || breakpoints[i].priceMinor === null) {
        message.error(
          t("court.pricing.scheduleMissingPrice", {
            defaultValue: `M\u1ED1c ${breakpoints[i].time.format(TIME_FORMAT$1)} thi\u1EBFu gi\xE1.`
          })
        );
        return;
      }
    }
    setScheduleRunning(true);
    setScheduleErrors([]);
    const errors = [];
    let createdCount = 0;
    for (let i = 0; i < breakpoints.length - 1; i += 1) {
      const startTime = breakpoints[i].time.format(TIME_FORMAT$1);
      const endTime = breakpoints[i + 1].time.format(TIME_FORMAT$1);
      try {
        await api.request(
          supplierId,
          "/price-rules",
          {
            resource_id: resourceId,
            weekdays,
            start_time: startTime,
            end_time: endTime,
            price_minor: breakpoints[i].priceMinor,
            priority: values.priority ?? 0
          },
          "POST"
        );
        createdCount += 1;
      } catch (err) {
        const label = `${startTime}-${endTime}`;
        const msg = err instanceof Error && isConflict(err) ? t("court.pricing.conflictError", { defaultValue: "tr\xF9ng \u01B0u ti\xEAn v\xE0 ch\u1ED3ng th\u1EDDi gian v\u1EDBi 1 khung gi\xE1 kh\xE1c \u0111ang ho\u1EA1t \u0111\u1ED9ng" }) : err instanceof Error ? err.message : String(err);
        errors.push(`${label}: ${msg}`);
      }
    }
    setScheduleRunning(false);
    setScheduleErrors(errors);
    if (createdCount > 0) {
      message.success(t("court.pricing.scheduleCreatedSummary", { defaultValue: `\u0110\xE3 t\u1EA1o ${createdCount} khung gi\xE1` }));
    }
    await load();
    if (errors.length === 0) {
      scheduleForm.resetFields();
      setScheduleOpen(false);
    }
  };
  const openEdit = (rule) => {
    setEditRule(rule);
    editForm.setFieldsValue({ priceMinor: rule.priceMinor, priority: rule.priority });
  };
  const handleUpdate = async () => {
    if (!supplierId || !editRule) return;
    try {
      const values = await editForm.validateFields();
      await api.request(supplierId, `price-rules/${editRule.id}`, { price_minor: values.priceMinor, priority: values.priority }, "PUT");
      message.success(t("court.pricing.updated", { defaultValue: "\u0110\xE3 c\u1EADp nh\u1EADt khung gi\xE1" }));
      setEditRule(null);
      await load();
    } catch (err) {
      if (err instanceof Error) message.error(isConflict(err) ? t("court.pricing.conflictError", { defaultValue: "Khung gi\xE1 b\u1ECB tr\xF9ng \u01B0u ti\xEAn v\xE0 ch\u1ED3ng th\u1EDDi gian v\u1EDBi 1 khung gi\xE1 kh\xE1c \u0111ang ho\u1EA1t \u0111\u1ED9ng." }) : err.message);
    }
  };
  const handleDeactivate = async (rule) => {
    if (!supplierId) return;
    try {
      await api.request(supplierId, `price-rules/${rule.id}`, void 0, "DELETE");
      message.success(t("court.pricing.deactivated", { defaultValue: "\u0110\xE3 ng\u1EEBng khung gi\xE1" }));
      await load();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    }
  };
  const handleReactivate = async (rule) => {
    if (!supplierId) return;
    try {
      await api.request(supplierId, `price-rules/${rule.id}/reactivate`, { price_minor: rule.priceMinor, priority: rule.priority }, "POST");
      message.success(t("court.pricing.reactivated", { defaultValue: "\u0110\xE3 k\xEDch ho\u1EA1t l\u1EA1i khung gi\xE1" }));
      await load();
    } catch (err) {
      if (err instanceof Error) message.error(isConflict(err) ? t("court.pricing.conflictError", { defaultValue: "Khung gi\xE1 b\u1ECB tr\xF9ng \u01B0u ti\xEAn v\xE0 ch\u1ED3ng th\u1EDDi gian v\u1EDBi 1 khung gi\xE1 kh\xE1c \u0111ang ho\u1EA1t \u0111\u1ED9ng." }) : err.message);
    }
  };
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(
      PageHeader,
      {
        title: t("court.pricing.title", { defaultValue: "Khung gi\xE1" }),
        description: t("court.pricing.pageDescription", {
          defaultValue: "Khung gi\xE1 theo ng\xE0y trong tu\u1EA7n v\xE0 khung gi\u1EDD cho s\xE2n \u0111ang ch\u1ECDn \u2014 \u01B0u ti\xEAn (priority) cao h\u01A1n th\u1EAFng khi 2 khung gi\u1EDD ch\u1ED3ng nhau."
        }),
        extra: /* @__PURE__ */ jsxs(Space, { children: [
          /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(ArrowLeftOutlined, {}), onClick: () => navigate("../..", { relative: "path" }), children: t("court.pricing.backToCourts", { defaultValue: "Quay l\u1EA1i danh s\xE1ch s\xE2n" }) }),
          /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(CalendarOutlined, {}), onClick: () => setScheduleOpen(true), children: t("court.pricing.scheduleButton", { defaultValue: "T\u1EA1o l\u1ECBch gi\xE1" }) }),
          /* @__PURE__ */ jsx(Button, { type: "primary", icon: /* @__PURE__ */ jsx(PlusOutlined, {}), onClick: () => setCreateOpen(true), children: t("common.add", { defaultValue: "Th\xEAm" }) })
        ] })
      }
    ),
    /* @__PURE__ */ jsxs("div", { style: { marginBottom: 24, padding: 16, border: "1px solid #f0f0f0", borderRadius: 8 }, children: [
      /* @__PURE__ */ jsx("h4", { style: { marginTop: 0 }, children: t("court.pricing.recurringBillingTitle", { defaultValue: "G\xF3i \u0111\u1EB7t s\xE2n \u0111\u1ECBnh k\u1EF3 \u2014 chi\u1EBFt kh\u1EA5u & k\xEDch th\u01B0\u1EDBc k\u1EF3" }) }),
      /* @__PURE__ */ jsxs(Space, { align: "start", wrap: true, children: [
        /* @__PURE__ */ jsx(Form.Item, { label: t("court.pricing.discountPercent", { defaultValue: "Chi\u1EBFt kh\u1EA5u (%)" }), style: { marginBottom: 8 }, children: /* @__PURE__ */ jsx(InputNumber, { min: 0, max: 100, value: discountPercent, onChange: (v) => setDiscountPercent(v ?? 0), disabled: billingLoading }) }),
        /* @__PURE__ */ jsx(Form.Item, { label: t("court.pricing.periodOccurrences", { defaultValue: "S\u1ED1 bu\u1ED5i / k\u1EF3" }), style: { marginBottom: 8 }, children: /* @__PURE__ */ jsx(InputNumber, { min: 1, value: periodOccurrences, onChange: (v) => setPeriodOccurrences(v ?? 1), disabled: billingLoading }) }),
        /* @__PURE__ */ jsx(Button, { type: "primary", onClick: handleSaveBilling, loading: billingSaving, disabled: !court, children: t("common.save", { defaultValue: "L\u01B0u" }) })
      ] }),
      highestActivePrice > 0 && /* @__PURE__ */ jsx(
        Alert,
        {
          type: "info",
          showIcon: true,
          style: { marginTop: 8 },
          message: t("court.pricing.billingPreview", {
            defaultValue: `V\xED d\u1EE5: g\xF3i ${periodOccurrences} bu\u1ED5i \xD7 ${formatThousands(highestActivePrice)}\u0111 = ${formatThousands(highestActivePrice * periodOccurrences)}\u0111 \u2192 c\xF2n l\u1EA1i ${formatThousands(Math.round(highestActivePrice * periodOccurrences * (100 - discountPercent) / 100))}\u0111 sau chi\u1EBFt kh\u1EA5u`
          })
        }
      )
    ] }),
    /* @__PURE__ */ jsx(
      Table,
      {
        rowKey: "id",
        loading,
        dataSource: rules,
        pagination: false,
        locale: { emptyText: t("court.pricing.empty", { defaultValue: "S\xE2n n\xE0y ch\u01B0a c\xF3 khung gi\xE1 n\xE0o." }) },
        columns: [
          {
            title: t("court.pricing.weekdayColumn", { defaultValue: "Th\u1EE9" }),
            dataIndex: "weekdays",
            key: "weekdays",
            render: (weekdays) => !weekdays || weekdays.length === 0 ? /* @__PURE__ */ jsx(Tag, { color: "blue", children: t("court.pricing.allWeekdays", { defaultValue: "M\u1ECDi th\u1EE9" }) }) : weekdays.map((w) => weekdayLabels[w] ?? w).join(", ")
          },
          {
            title: t("court.pricing.dateColumn", { defaultValue: "Ng\xE0y \xE1p d\u1EE5ng" }),
            key: "dates",
            render: (_, record) => record.dateFrom && record.dateTo ? /* @__PURE__ */ jsxs(Space, { size: 4, children: [
              /* @__PURE__ */ jsx(Tag, { color: "gold", children: record.label || t("court.pricing.holiday", { defaultValue: "Ng\xE0y \u0111\u1EB7c bi\u1EC7t" }) }),
              record.dateFrom === record.dateTo ? record.dateFrom : `${record.dateFrom} \u2192 ${record.dateTo}`
            ] }) : "\u2014"
          },
          {
            title: t("court.pricing.timeRangeColumn", { defaultValue: "Khung gi\u1EDD" }),
            key: "timeRange",
            render: (_, record) => `${record.startTime} - ${record.endTime}`
          },
          {
            title: t("court.pricing.priceColumn", { defaultValue: "Gi\xE1" }),
            dataIndex: "priceMinor",
            key: "priceMinor",
            render: (v) => v.toLocaleString("vi-VN") + "\u0111"
          },
          { title: t("court.pricing.priorityColumn", { defaultValue: "\u01AFu ti\xEAn" }), dataIndex: "priority", key: "priority" },
          {
            title: t("court.pricing.statusColumn", { defaultValue: "Tr\u1EA1ng th\xE1i" }),
            dataIndex: "active",
            key: "active",
            render: (active) => active ? /* @__PURE__ */ jsx(Tag, { color: "green", children: t("common.active", { defaultValue: "Ho\u1EA1t \u0111\u1ED9ng" }) }) : /* @__PURE__ */ jsx(Tag, { color: "red", children: t("common.inactive", { defaultValue: "Ng\u1EEBng" }) })
          },
          {
            title: "",
            key: "action",
            render: (_, record) => /* @__PURE__ */ jsxs(Space, { children: [
              /* @__PURE__ */ jsx(Button, { size: "small", icon: /* @__PURE__ */ jsx(EditOutlined, {}), onClick: () => openEdit(record), children: t("common.edit", { defaultValue: "S\u1EEDa" }) }),
              record.active ? /* @__PURE__ */ jsx(
                Popconfirm,
                {
                  title: t("court.pricing.deactivateConfirmTitle", { defaultValue: "Ng\u1EEBng khung gi\xE1 n\xE0y?" }),
                  description: t("court.pricing.deactivateConfirmContent", { defaultValue: "Khung gi\xE1 s\u1EBD kh\xF4ng c\xF2n \xE1p d\u1EE5ng khi t\xEDnh gi\xE1 \u0111\u1EB7t s\xE2n. C\xF3 th\u1EC3 k\xEDch ho\u1EA1t l\u1EA1i b\u1EA5t c\u1EE9 l\xFAc n\xE0o." }),
                  onConfirm: () => void handleDeactivate(record),
                  okText: t("common.confirm", { defaultValue: "X\xE1c nh\u1EADn" }),
                  cancelText: t("common.cancel", { defaultValue: "Hu\u1EF7" }),
                  children: /* @__PURE__ */ jsx(Button, { size: "small", danger: true, children: t("common.deactivate", { defaultValue: "Ng\u1EEBng ho\u1EA1t \u0111\u1ED9ng" }) })
                }
              ) : /* @__PURE__ */ jsx(Button, { size: "small", onClick: () => void handleReactivate(record), children: t("common.reactivate", { defaultValue: "K\xEDch ho\u1EA1t l\u1EA1i" }) })
            ] })
          }
        ]
      }
    ),
    /* @__PURE__ */ jsxs(
      Modal,
      {
        title: t("court.pricing.scheduleTitle", { defaultValue: "T\u1EA1o l\u1ECBch gi\xE1 nhi\u1EC1u m\u1ED1c gi\u1EDD" }),
        open: scheduleOpen,
        onOk: () => void handleCreateSchedule(),
        onCancel: () => {
          if (scheduleRunning) return;
          scheduleForm.resetFields();
          setScheduleErrors([]);
          setScheduleOpen(false);
        },
        confirmLoading: scheduleRunning,
        okText: t("court.courts.bulkCreateSubmit", { defaultValue: "T\u1EA1o" }),
        width: 560,
        destroyOnClose: true,
        children: [
          /* @__PURE__ */ jsxs(Form, { form: scheduleForm, layout: "vertical", children: [
            /* @__PURE__ */ jsx(
              Form.Item,
              {
                name: "weekdays",
                label: t("court.pricing.scheduleWeekdaysLabel", { defaultValue: "\xC1p d\u1EE5ng cho th\u1EE9" }),
                tooltip: t("court.pricing.weekdaysEmptyMeansAll", { defaultValue: "\u0110\u1EC3 tr\u1ED1ng = \xE1p d\u1EE5ng cho M\u1ECCI th\u1EE9 trong tu\u1EA7n." }),
                children: /* @__PURE__ */ jsx(Checkbox.Group, { options: weekdayLabels.map((label, value) => ({ label, value })) })
              }
            ),
            /* @__PURE__ */ jsx(
              Form.Item,
              {
                label: t("court.pricing.scheduleBreakpointsLabel", { defaultValue: "M\u1ED1c gi\u1EDD v\xE0 gi\xE1" }),
                tooltip: t("court.pricing.scheduleBreakpointsHelp", {
                  defaultValue: "M\u1ED7i d\xF2ng l\xE0 1 m\u1ED1c gi\u1EDD. Gi\xE1 c\u1EE7a 1 d\xF2ng \xE1p d\u1EE5ng cho khung t\u1EEB m\u1ED1c \u0111\xF3 t\u1EDBi m\u1ED1c k\u1EBF ti\u1EBFp \u2014 d\xF2ng CU\u1ED0I ch\u1EC9 c\u1EA7n gi\u1EDD (gi\u1EDD \u0111\xF3ng c\u1EEDa), kh\xF4ng c\u1EA7n gi\xE1. Vd: 06:00\u2192120.000\u0111, 12:00\u2192100.000\u0111, 17:00\u2192140.000\u0111, 22:00 (k\u1EBFt th\xFAc)."
                }),
                children: /* @__PURE__ */ jsx(Form.List, { name: "breakpoints", initialValue: [{}, {}], children: (fields, { add, remove }) => /* @__PURE__ */ jsxs(Fragment, { children: [
                  fields.map((field, index) => /* @__PURE__ */ jsxs(Space, { align: "baseline", style: { display: "flex", marginBottom: 8 }, children: [
                    /* @__PURE__ */ jsx(
                      Form.Item,
                      {
                        name: [field.name, "time"],
                        rules: [{ required: true, message: t("court.pricing.scheduleTimeRequired", { defaultValue: "Nh\u1EADp gi\u1EDD" }) }],
                        noStyle: true,
                        children: /* @__PURE__ */ jsx(TimePicker, { format: TIME_FORMAT$1, placeholder: t("court.pricing.scheduleTimePlaceholder", { defaultValue: "Gi\u1EDD" }) })
                      }
                    ),
                    index < fields.length - 1 ? /* @__PURE__ */ jsx(
                      Form.Item,
                      {
                        name: [field.name, "priceMinor"],
                        rules: [{ required: true, message: t("court.pricing.schedulePriceRequired", { defaultValue: "Nh\u1EADp gi\xE1" }) }],
                        noStyle: true,
                        children: /* @__PURE__ */ jsx(
                          InputNumber,
                          {
                            min: 0,
                            step: 1e3,
                            formatter: formatThousands,
                            parser: parseThousands,
                            placeholder: t("court.pricing.priceFieldLabel", { defaultValue: "Gi\xE1 (\u0111\u1ED3ng)" }),
                            style: { width: 180 }
                          }
                        )
                      }
                    ) : /* @__PURE__ */ jsx("span", { style: { color: "rgba(0,0,0,0.45)", width: 180 }, children: t("court.pricing.scheduleClosingTime", { defaultValue: "(gi\u1EDD \u0111\xF3ng c\u1EEDa)" }) }),
                    fields.length > 2 && /* @__PURE__ */ jsx(DeleteOutlined, { onClick: () => remove(field.name) })
                  ] }, field.key)),
                  /* @__PURE__ */ jsx(Button, { type: "dashed", onClick: () => add(), block: true, icon: /* @__PURE__ */ jsx(PlusOutlined, {}), children: t("court.pricing.scheduleAddBreakpoint", { defaultValue: "Th\xEAm m\u1ED1c gi\u1EDD" }) })
                ] }) })
              }
            ),
            /* @__PURE__ */ jsx(
              Form.Item,
              {
                name: "priority",
                label: t("court.pricing.priorityFieldLabel", { defaultValue: "\u01AFu ti\xEAn" }),
                tooltip: t("court.pricing.priorityHelp", { defaultValue: "S\u1ED1 l\u1EDBn h\u01A1n th\u1EAFng khi 2 khung gi\xE1 ch\u1ED3ng th\u1EDDi gian nhau." }),
                initialValue: 0,
                children: /* @__PURE__ */ jsx(InputNumber, { min: 0, style: { width: "100%" } })
              }
            )
          ] }),
          scheduleErrors.length > 0 && /* @__PURE__ */ jsx(
            Alert,
            {
              type: "warning",
              showIcon: true,
              message: t("court.pricing.schedulePartialErrorTitle", { defaultValue: "M\u1ED9t s\u1ED1 khung gi\xE1 t\u1EA1o th\u1EA5t b\u1EA1i" }),
              description: /* @__PURE__ */ jsx("ul", { style: { margin: 0, paddingLeft: 20 }, children: scheduleErrors.map((e) => /* @__PURE__ */ jsx("li", { children: e }, e)) }),
              style: { marginTop: 12 }
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      Modal,
      {
        title: t("court.pricing.createTitle", { defaultValue: "Th\xEAm khung gi\xE1" }),
        open: createOpen,
        onOk: handleCreate,
        onCancel: () => {
          createForm.resetFields();
          setCreateOpen(false);
        },
        destroyOnClose: true,
        children: /* @__PURE__ */ jsxs(Form, { form: createForm, layout: "vertical", children: [
          /* @__PURE__ */ jsx(
            Form.Item,
            {
              name: "weekdays",
              label: t("court.pricing.weekdayFieldLabel", { defaultValue: "Th\u1EE9 trong tu\u1EA7n" }),
              tooltip: t("court.pricing.weekdaysEmptyMeansAll", { defaultValue: "\u0110\u1EC3 tr\u1ED1ng = \xE1p d\u1EE5ng cho M\u1ECCI th\u1EE9 trong tu\u1EA7n." }),
              children: /* @__PURE__ */ jsx(Checkbox.Group, { options: weekdayLabels.map((label, value) => ({ label, value })) })
            }
          ),
          /* @__PURE__ */ jsx(Form.Item, { name: "startTime", label: t("court.pricing.startTimeFieldLabel", { defaultValue: "Gi\u1EDD b\u1EAFt \u0111\u1EA7u" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(TimePicker, { format: TIME_FORMAT$1, style: { width: "100%" } }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "endTime", label: t("court.pricing.endTimeFieldLabel", { defaultValue: "Gi\u1EDD k\u1EBFt th\xFAc" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(TimePicker, { format: TIME_FORMAT$1, style: { width: "100%" } }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "priceMinor", label: t("court.pricing.priceFieldLabel", { defaultValue: "Gi\xE1 (\u0111\u1ED3ng)" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(InputNumber, { min: 0, step: 1e3, formatter: formatThousands, parser: parseThousands, style: { width: "100%" } }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "priority", label: t("court.pricing.priorityFieldLabel", { defaultValue: "\u01AFu ti\xEAn" }), rules: [{ required: true }], tooltip: t("court.pricing.priorityHelp", { defaultValue: "S\u1ED1 l\u1EDBn h\u01A1n th\u1EAFng khi 2 khung gi\xE1 ch\u1ED3ng th\u1EDDi gian nhau." }), initialValue: 0, children: /* @__PURE__ */ jsx(InputNumber, { min: 0, style: { width: "100%" } }) }),
          /* @__PURE__ */ jsx(
            Form.Item,
            {
              name: "dateRange",
              label: t("court.pricing.dateRangeFieldLabel", { defaultValue: "Ch\u1EC9 \xE1p d\u1EE5ng trong kho\u1EA3ng ng\xE0y (ng\xE0y l\u1EC5 / \u0111\u1EB7c bi\u1EC7t)" }),
              tooltip: t("court.pricing.dateRangeHelp", { defaultValue: "\u0110\u1EC3 tr\u1ED1ng = khung gi\xE1 th\u01B0\u1EDDng. C\xF3 ch\u1ECDn th\xEC khung gi\xE1 ch\u1EC9 \xE1p trong c\xE1c ng\xE0y n\xE0y (g\u1ED3m c\u1EA3 hai \u0111\u1EA7u); \u0111\u1EB7t \u01B0u ti\xEAn CAO h\u01A1n khung gi\xE1 th\u01B0\u1EDDng \u0111\u1EC3 ghi \u0111\xE8." }),
              children: /* @__PURE__ */ jsx(DatePicker.RangePicker, { style: { width: "100%" }, format: "DD/MM/YYYY" })
            }
          ),
          /* @__PURE__ */ jsx(Form.Item, { name: "label", label: t("court.pricing.labelFieldLabel", { defaultValue: "T\xEAn (vd L\u1EC5 2/9)" }), children: /* @__PURE__ */ jsx(Input, { maxLength: 100 }) })
        ] })
      }
    ),
    /* @__PURE__ */ jsx(
      Modal,
      {
        title: t("court.pricing.editTitle", { defaultValue: "S\u1EEDa khung gi\xE1" }),
        open: !!editRule,
        onOk: handleUpdate,
        onCancel: () => setEditRule(null),
        destroyOnClose: true,
        children: /* @__PURE__ */ jsxs(Form, { form: editForm, layout: "vertical", children: [
          /* @__PURE__ */ jsx(Form.Item, { name: "priceMinor", label: t("court.pricing.priceFieldLabel", { defaultValue: "Gi\xE1 (\u0111\u1ED3ng)" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(InputNumber, { min: 0, step: 1e3, formatter: formatThousands, parser: parseThousands, style: { width: "100%" } }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "priority", label: t("court.pricing.priorityFieldLabel", { defaultValue: "\u01AFu ti\xEAn" }), rules: [{ required: true }], tooltip: t("court.pricing.priorityHelp", { defaultValue: "S\u1ED1 l\u1EDBn h\u01A1n th\u1EAFng khi 2 khung gi\xE1 ch\u1ED3ng th\u1EDDi gian nhau." }), children: /* @__PURE__ */ jsx(InputNumber, { min: 0, style: { width: "100%" } }) })
        ] })
      }
    )
  ] });
};
function isConflict(err) {
  const withStatus = err;
  return withStatus.status === 409 || /409|conflict/i.test(err.message);
}

const { RangePicker } = DatePicker;
const DATE_FORMAT$4 = "YYYY-MM-DD";
const TIME_FORMAT = "HH:mm";
const WEEKDAY_DEFAULTS = ["Ch\u1EE7 nh\u1EADt", "Th\u1EE9 hai", "Th\u1EE9 ba", "Th\u1EE9 t\u01B0", "Th\u1EE9 n\u0103m", "Th\u1EE9 s\xE1u", "Th\u1EE9 b\u1EA3y"];
function formatMoney(priceMinor, currency) {
  const amount = Math.round(priceMinor);
  if (currency.toLowerCase() === "vnd") return `${amount.toLocaleString("vi-VN")}\u20AB`;
  return `${amount.toLocaleString("vi-VN")} ${currency.toUpperCase()}`;
}
const CreateBookingModal = ({ open, onClose, onCreated, supplierId, courts }) => {
  const { t, api, onCreateBooking, onMarkBookingPaid, staffAuth } = useCourtContext();
  const [form] = Form.useForm();
  const [staffVerified, setStaffVerified] = useState(false);
  const [staffName, setStaffName] = useState(null);
  const [pinGateOpen, setPinGateOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    setStaffVerified(!!staffAuth && staffAuth.hasValidSession(supplierId));
  }, [open, supplierId, staffAuth]);
  const [mode, setMode] = useState("single");
  const [submitting, setSubmitting] = useState(false);
  const [createdOrderCode, setCreatedOrderCode] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [markedPaid, setMarkedPaid] = useState(false);
  const [singleDate, setSingleDate] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slots, setSlots] = useState([]);
  const [bookedBlocks, setBookedBlocks] = useState([]);
  const [slotCurrency, setSlotCurrency] = useState("vnd");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const weekdayLabels = useMemo(
    () => WEEKDAY_DEFAULTS.map((defaultValue, weekday) => t(`court.pricing.weekday.${weekday}`, { defaultValue })),
    [t]
  );
  const daySlotItems = useMemo(() => {
    const free = slots.map((s) => ({ kind: "free", startAt: s.startAt, endAt: s.endAt, priceMinor: s.priceMinor }));
    const booked = bookedBlocks.map((b) => ({
      kind: b.status === "confirmed" ? "confirmed" : "held",
      startAt: b.startAt,
      endAt: b.endAt
    }));
    return [...free, ...booked].sort((a, b) => a.startAt.localeCompare(b.startAt));
  }, [slots, bookedBlocks]);
  useEffect(() => {
    if (!open) {
      form.resetFields();
      setMode("single");
      setSingleDate(null);
      setSlots([]);
      setBookedBlocks([]);
      setSelectedSlot(null);
      setPreview(null);
      setPreviewError(null);
      setCreatedOrderCode(null);
      setMarkingPaid(false);
      setMarkedPaid(false);
    }
  }, [open, form]);
  const loadSingleSlots = async (resourceId, date) => {
    setSlotsLoading(true);
    setSelectedSlot(null);
    try {
      const resp = await api.request(supplierId, "search", { resource_id: resourceId, date: date.format(DATE_FORMAT$4) }, "POST");
      setSlots(resp.freeSlots ?? []);
      setBookedBlocks(resp.items ?? []);
      setSlotCurrency(resp.currency || "vnd");
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
      setSlots([]);
      setBookedBlocks([]);
    } finally {
      setSlotsLoading(false);
    }
  };
  const loadRecurringPreview = async () => {
    setPreviewError(null);
    try {
      const values = await form.validateFields(["resourceId", "weekday", "timeRange", "dateRange"]);
      const [startTime, endTime] = values.timeRange;
      const [startDate, endDate] = values.dateRange;
      setPreviewLoading(true);
      setPreview(null);
      const resp = await api.request(
        supplierId,
        "reserve_recurring/preview",
        {
          resource_id: values.resourceId,
          weekday: String(values.weekday),
          start_time: startTime.format(TIME_FORMAT),
          end_time: endTime.format(TIME_FORMAT),
          start_date: startDate.format(DATE_FORMAT$4),
          end_date: endDate.format(DATE_FORMAT$4)
        },
        "POST"
      );
      setPreview(resp);
    } catch (err) {
      if (err instanceof Error) setPreviewError(err.message);
    } finally {
      setPreviewLoading(false);
    }
  };
  const handleSubmit = async () => {
    if (!onCreateBooking) return;
    try {
      const values = await form.validateFields();
      let input;
      if (mode === "single") {
        if (!selectedSlot) {
          message.error(t("court.bookings.create.pickSlotFirst", { defaultValue: "Vui l\xF2ng ch\u1ECDn 1 khung gi\u1EDD tr\u1ED1ng." }));
          return;
        }
        input = {
          mode: "single",
          resourceId: values.resourceId,
          startAt: selectedSlot.startAt,
          endAt: selectedSlot.endAt,
          attendeeName: values.attendeeName,
          attendeeEmail: values.attendeeEmail
        };
      } else {
        if (!preview || !preview.allAvailable) {
          message.error(
            t("court.bookings.create.previewRequired", {
              defaultValue: 'Vui l\xF2ng b\u1EA5m "Xem tr\u01B0\u1EDBc" v\xE0 \u0111\u1EA3m b\u1EA3o t\u1EA5t c\u1EA3 bu\u1ED5i \u0111\u1EC1u tr\u1ED1ng tr\u01B0\u1EDBc khi x\xE1c nh\u1EADn.'
            })
          );
          return;
        }
        const [startTime, endTime] = values.timeRange;
        const [startDate, endDate] = values.dateRange;
        input = {
          mode: "recurring",
          resourceId: values.resourceId,
          weekday: values.weekday,
          startTime: startTime.format(TIME_FORMAT),
          endTime: endTime.format(TIME_FORMAT),
          startDate: startDate.format(DATE_FORMAT$4),
          endDate: endDate.format(DATE_FORMAT$4),
          attendeeName: values.attendeeName,
          attendeeEmail: values.attendeeEmail
        };
      }
      setSubmitting(true);
      const result = await onCreateBooking(Number(supplierId), input);
      message.success(t("court.bookings.create.success", { defaultValue: "\u0110\xE3 t\u1EA1o booking th\xE0nh c\xF4ng." }));
      setCreatedOrderCode(result.orderCode);
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  const handleMarkPaid = async () => {
    if (!onMarkBookingPaid || !createdOrderCode) return;
    setMarkingPaid(true);
    try {
      await onMarkBookingPaid(Number(supplierId), createdOrderCode);
      setMarkedPaid(true);
      message.success(t("court.bookings.create.markPaidSuccess", { defaultValue: "\u0110\xE3 x\xE1c nh\u1EADn thu ti\u1EC1n." }));
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setMarkingPaid(false);
    }
  };
  if (createdOrderCode) {
    return /* @__PURE__ */ jsxs(
      Modal,
      {
        open,
        onCancel: () => {
          onCreated();
        },
        title: t("court.bookings.create.title", { defaultValue: "\u0110\u1EB7t s\xE2n h\u1ED9 kh\xE1ch" }),
        footer: null,
        width: 480,
        destroyOnClose: true,
        children: [
          /* @__PURE__ */ jsx(
            Alert,
            {
              type: "success",
              showIcon: true,
              message: t("court.bookings.create.created", { defaultValue: `\u0110\xE3 t\u1EA1o booking, m\xE3 \u0111\u01A1n: ${createdOrderCode}` }),
              style: { marginBottom: 16 }
            }
          ),
          /* @__PURE__ */ jsx("p", { style: { color: "rgba(0,0,0,0.45)", marginBottom: 16 }, children: t("court.bookings.create.paymentNote", {
            defaultValue: "N\u1EBFu kh\xE1ch ch\u01B0a tr\u1EA3 ti\u1EC1n ngay, l\u01B0u \xFD gi\u1EEF ch\u1ED7 \u1EDF ph\xEDa s\xE2n t\u1EF1 h\u1EBFt h\u1EA1n sau kho\u1EA3ng 20 ph\xFAt \u2014 n\xEAn x\xE1c nh\u1EADn thu ti\u1EC1n s\u1EDBm n\u1EBFu kh\xE1ch \u0111\xE3 ch\u1ED1t."
          }) }),
          markedPaid ? /* @__PURE__ */ jsx(Alert, { type: "success", showIcon: true, message: t("court.bookings.create.markPaidSuccess", { defaultValue: "\u0110\xE3 x\xE1c nh\u1EADn thu ti\u1EC1n." }) }) : /* @__PURE__ */ jsx(
            Button,
            {
              type: "primary",
              block: true,
              onClick: () => void handleMarkPaid(),
              loading: markingPaid,
              disabled: !onMarkBookingPaid,
              style: { marginBottom: 8 },
              children: t("court.bookings.create.markPaid", { defaultValue: "X\xE1c nh\u1EADn \u0111\xE3 thu ti\u1EC1n" })
            }
          ),
          /* @__PURE__ */ jsx(Button, { block: true, onClick: () => onCreated(), children: t("court.bookings.create.close", { defaultValue: "\u0110\xF3ng" }) })
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(
      Modal,
      {
        open,
        onCancel: onClose,
        title: t("court.bookings.create.title", { defaultValue: "\u0110\u1EB7t s\xE2n h\u1ED9 kh\xE1ch" }),
        footer: null,
        width: 640,
        destroyOnClose: true,
        children: [
          staffAuth && /* @__PURE__ */ jsx("div", { style: { marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }, children: staffVerified ? /* @__PURE__ */ jsxs(Space, { size: 4, children: [
            /* @__PURE__ */ jsx(UserOutlined, { style: { color: "#52c41a" } }),
            /* @__PURE__ */ jsx(Typography.Text, { type: "secondary", children: t("court.bookings.create.staffVerifiedAs", { defaultValue: `Nh\xE2n vi\xEAn: ${staffName ?? ""}` }) })
          ] }) : /* @__PURE__ */ jsxs(Space, { size: 4, children: [
            /* @__PURE__ */ jsx(UserOutlined, { style: { color: "rgba(0,0,0,0.35)" } }),
            /* @__PURE__ */ jsx(Typography.Text, { type: "secondary", children: t("court.bookings.create.staffNotVerified", { defaultValue: "Ch\u01B0a x\xE1c th\u1EF1c nh\xE2n vi\xEAn" }) }),
            /* @__PURE__ */ jsx(Typography.Link, { onClick: () => setPinGateOpen(true), children: t("court.bookings.create.staffVerifyLink", { defaultValue: "X\xE1c th\u1EF1c PIN" }) })
          ] }) }),
          /* @__PURE__ */ jsxs(Form, { form, layout: "vertical", children: [
            /* @__PURE__ */ jsx(Form.Item, { label: t("court.bookings.create.mode", { defaultValue: "H\xECnh th\u1EE9c" }), children: /* @__PURE__ */ jsxs(
              Radio.Group,
              {
                value: mode,
                onChange: (e) => {
                  setMode(e.target.value);
                  setPreview(null);
                  setSlots([]);
                  setBookedBlocks([]);
                  setSelectedSlot(null);
                },
                children: [
                  /* @__PURE__ */ jsx(Radio.Button, { value: "single", children: t("court.bookings.create.modeSingle", { defaultValue: "\u0110\u1EB7t l\u1EBB" }) }),
                  /* @__PURE__ */ jsx(Radio.Button, { value: "recurring", children: t("court.bookings.create.modeRecurring", { defaultValue: "\u0110\u1EB7t \u0111\u1ECBnh k\u1EF3" }) })
                ]
              }
            ) }),
            /* @__PURE__ */ jsx(
              Form.Item,
              {
                name: "resourceId",
                label: t("court.bookings.create.court", { defaultValue: "S\xE2n" }),
                rules: [{ required: true }],
                children: /* @__PURE__ */ jsx(
                  Select,
                  {
                    options: courts.map((c) => ({ value: c.resourceId, label: c.name })),
                    onChange: () => {
                      setSlots([]);
                      setBookedBlocks([]);
                      setSelectedSlot(null);
                      setPreview(null);
                      setSingleDate(null);
                    }
                  }
                )
              }
            ),
            mode === "single" && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx(Form.Item, { label: t("court.bookings.create.date", { defaultValue: "Ng\xE0y" }), required: true, children: /* @__PURE__ */ jsx(
                DatePicker,
                {
                  value: singleDate,
                  format: DATE_FORMAT$4,
                  onChange: (v) => {
                    setSingleDate(v);
                    const resourceId = form.getFieldValue("resourceId");
                    if (v && resourceId) void loadSingleSlots(resourceId, v);
                  },
                  disabled: !form.getFieldValue("resourceId")
                }
              ) }),
              slotsLoading && /* @__PURE__ */ jsx("div", { style: { padding: "24px 0", textAlign: "center" }, children: /* @__PURE__ */ jsx(Spin, { size: "small" }) }),
              !slotsLoading && singleDate && daySlotItems.length === 0 && /* @__PURE__ */ jsx(
                Alert,
                {
                  type: "info",
                  showIcon: true,
                  message: t("court.bookings.create.noSlots", { defaultValue: "Kh\xF4ng c\xF3 khung gi\u1EDD tr\u1ED1ng cho ng\xE0y n\xE0y." }),
                  style: { marginBottom: 16 }
                }
              ),
              !slotsLoading && daySlotItems.length > 0 && /* @__PURE__ */ jsx(Form.Item, { label: t("court.bookings.create.timeSlot", { defaultValue: "Khung gi\u1EDD" }), required: true, style: { marginBottom: 8 }, children: /* @__PURE__ */ jsx(
                "div",
                {
                  style: {
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(128px, 1fr))",
                    gap: 8
                  },
                  children: daySlotItems.map((item) => {
                    if (item.kind === "free") {
                      const isSelected = selectedSlot?.startAt === item.startAt;
                      return /* @__PURE__ */ jsxs(
                        "button",
                        {
                          type: "button",
                          onClick: () => setSelectedSlot({ startAt: item.startAt, endAt: item.endAt, priceMinor: item.priceMinor }),
                          style: {
                            border: `1px solid ${isSelected ? "#1677ff" : "#d9d9d9"}`,
                            borderRadius: 6,
                            padding: "8px 10px",
                            background: isSelected ? "#1677ff" : "#fff",
                            color: isSelected ? "#fff" : "rgba(0,0,0,0.88)",
                            cursor: "pointer",
                            textAlign: "left",
                            lineHeight: 1.4,
                            transition: "border-color 0.15s, background 0.15s"
                          },
                          children: [
                            /* @__PURE__ */ jsxs("div", { style: { fontWeight: 600, fontSize: 13 }, children: [
                              dayjs(item.startAt).format("HH:mm"),
                              "\u2013",
                              dayjs(item.endAt).format("HH:mm")
                            ] }),
                            /* @__PURE__ */ jsx("div", { style: { fontSize: 12, opacity: isSelected ? 0.9 : 0.65 }, children: formatMoney(item.priceMinor, slotCurrency) })
                          ]
                        },
                        item.startAt
                      );
                    }
                    return /* @__PURE__ */ jsxs(
                      "div",
                      {
                        style: {
                          border: "1px dashed #d9d9d9",
                          borderRadius: 6,
                          padding: "8px 10px",
                          background: "#f5f5f5",
                          color: "rgba(0,0,0,0.45)",
                          lineHeight: 1.4
                        },
                        children: [
                          /* @__PURE__ */ jsxs("div", { style: { fontWeight: 600, fontSize: 13 }, children: [
                            dayjs(item.startAt).format("HH:mm"),
                            "\u2013",
                            dayjs(item.endAt).format("HH:mm")
                          ] }),
                          /* @__PURE__ */ jsxs("div", { style: { fontSize: 12, display: "flex", alignItems: "center", gap: 4 }, children: [
                            /* @__PURE__ */ jsx(LockOutlined, { style: { fontSize: 11 } }),
                            item.kind === "confirmed" ? t("court.bookings.create.slotConfirmed", { defaultValue: "\u0110\xE3 \u0111\u1EB7t" }) : t("court.bookings.create.slotHeld", { defaultValue: "\u0110ang gi\u1EEF ch\u1ED7" })
                          ] })
                        ]
                      },
                      item.startAt
                    );
                  })
                }
              ) }),
              /* @__PURE__ */ jsx(Divider, { style: { margin: "8px 0 16px" } })
            ] }),
            mode === "recurring" && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx(Form.Item, { name: "weekday", label: t("court.bookings.create.weekday", { defaultValue: "Th\u1EE9 trong tu\u1EA7n" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(
                Radio.Group,
                {
                  options: weekdayLabels.map((label, value) => ({ label, value })),
                  onChange: () => setPreview(null)
                }
              ) }),
              /* @__PURE__ */ jsx(
                Form.Item,
                {
                  name: "timeRange",
                  label: t("court.bookings.create.timeRange", { defaultValue: "Khung gi\u1EDD m\u1ED7i bu\u1ED5i" }),
                  rules: [{ required: true }],
                  children: /* @__PURE__ */ jsx(TimePicker.RangePicker, { format: TIME_FORMAT, minuteStep: 15, onChange: () => setPreview(null) })
                }
              ),
              /* @__PURE__ */ jsx(
                Form.Item,
                {
                  name: "dateRange",
                  label: t("court.bookings.create.dateRange", { defaultValue: "Kho\u1EA3ng ng\xE0y l\u1EB7p l\u1EA1i" }),
                  rules: [{ required: true }],
                  children: /* @__PURE__ */ jsx(RangePicker, { format: DATE_FORMAT$4, onChange: () => setPreview(null) })
                }
              ),
              /* @__PURE__ */ jsx(Button, { onClick: () => void loadRecurringPreview(), loading: previewLoading, style: { marginBottom: 16 }, children: t("court.bookings.create.preview", { defaultValue: "Xem tr\u01B0\u1EDBc" }) }),
              previewError && /* @__PURE__ */ jsx(Alert, { type: "error", showIcon: true, message: previewError, style: { marginBottom: 16 } }),
              preview && /* @__PURE__ */ jsxs("div", { style: { marginBottom: 16 }, children: [
                /* @__PURE__ */ jsx(
                  Alert,
                  {
                    type: preview.allAvailable ? "success" : "warning",
                    showIcon: true,
                    message: preview.allAvailable ? t("court.bookings.create.allAvailable", {
                      defaultValue: `T\u1EA5t c\u1EA3 ${preview.occurrences.length} bu\u1ED5i \u0111\u1EC1u tr\u1ED1ng. T\u1ED5ng ti\u1EC1n: ${formatMoney(preview.priceMinor, preview.currency)}.`
                    }) : t("court.bookings.create.someConflict", {
                      defaultValue: "C\xF3 bu\u1ED5i b\u1ECB tr\xF9ng l\u1ECBch \u2014 kh\xF4ng th\u1EC3 t\u1EA1o c\u1EA3 g\xF3i cho t\u1EDBi khi gi\u1EA3i quy\u1EBFt xong."
                    }),
                    style: { marginBottom: 8 }
                  }
                ),
                /* @__PURE__ */ jsx("div", { style: { display: "flex", flexWrap: "wrap", gap: 8 }, children: preview.occurrences.map((o) => /* @__PURE__ */ jsxs(Tag, { color: o.available ? "green" : "red", children: [
                  dayjs(o.startAt).format("DD/MM"),
                  " ",
                  o.available ? "\u2713" : "\u2717 tr\xF9ng l\u1ECBch"
                ] }, o.startAt)) })
              ] }),
              /* @__PURE__ */ jsx(Divider, { style: { margin: "8px 0 16px" } })
            ] }),
            /* @__PURE__ */ jsx(Form.Item, { name: "attendeeName", label: t("court.bookings.create.attendeeName", { defaultValue: "T\xEAn kh\xE1ch" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, {}) }),
            /* @__PURE__ */ jsx(
              Form.Item,
              {
                name: "attendeeEmail",
                label: t("court.bookings.create.attendeeEmail", { defaultValue: "Email kh\xE1ch" }),
                rules: [{ required: true, type: "email" }],
                children: /* @__PURE__ */ jsx(Input, {})
              }
            ),
            /* @__PURE__ */ jsx(Divider, { style: { margin: "8px 0 16px" } }),
            /* @__PURE__ */ jsx(Button, { type: "primary", size: "large", block: true, onClick: () => void handleSubmit(), loading: submitting, disabled: !onCreateBooking, children: t("court.bookings.create.submit", { defaultValue: "T\u1EA1o booking" }) })
          ] })
        ]
      }
    ),
    staffAuth && /* @__PURE__ */ jsx(
      StaffPinGate,
      {
        open: pinGateOpen,
        supplierId,
        onCancel: () => setPinGateOpen(false),
        onVerified: (staff) => {
          setStaffVerified(true);
          setStaffName(staff.name);
          setPinGateOpen(false);
        }
      }
    )
  ] });
};

function useMarkOrderPaid(supplierId, eventSlug, onUpdated) {
  const { t, onMarkBookingPaid, onRecordRecurringPayment, getOrderSummary } = useCourtContext();
  const [pendingRef, setPendingRef] = useState(null);
  const markPaid = async (orderRef, isRecurringPending) => {
    const action = isRecurringPending ? onRecordRecurringPayment : onMarkBookingPaid;
    if (!action) return;
    setPendingRef(orderRef);
    try {
      await action(supplierId, orderRef);
      message.success(t("court.bookings.markPaidSuccess", { defaultValue: "\u0110\xE3 x\xE1c nh\u1EADn thu ti\u1EC1n." }));
      if (getOrderSummary && eventSlug) {
        const fresh = await getOrderSummary(eventSlug, orderRef);
        onUpdated(orderRef, fresh);
      }
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setPendingRef(null);
    }
  };
  return { markPaid, pendingRef, canMarkPaid: !!onMarkBookingPaid, canRecordRecurringPayment: !!onRecordRecurringPayment };
}

const DEFAULT_VENUE_TZ = "Asia/Ho_Chi_Minh";
const fmtCache = /* @__PURE__ */ new Map();
function formatter(tz) {
  let f = fmtCache.get(tz);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat("en-GB", {
        timeZone: tz,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      });
    } catch {
      f = formatter(DEFAULT_VENUE_TZ);
    }
    fmtCache.set(tz, f);
  }
  return f;
}
function venueWallClock(input, tz) {
  const d = dayjs(input).toDate();
  const parts = formatter(tz || DEFAULT_VENUE_TZ).formatToParts(d);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return dayjs(new Date(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second")));
}
const formatVenueTime = (input, tz) => venueWallClock(input, tz).format("HH:mm");
const formatVenueDateTime = (input, tz) => venueWallClock(input, tz).format("DD/MM HH:mm");
function venueLocalToISO(date, hhmm, tz) {
  const [y, mo, d] = date.split("-").map(Number);
  const [h, mi] = hhmm.split(":").map(Number);
  const target = Date.UTC(y, mo - 1, d, h, mi, 0);
  let guess = target;
  for (let i = 0; i < 3; i++) {
    const w2 = venueWallClock(new Date(guess), tz);
    const wallAsUtc = Date.UTC(w2.year(), w2.month(), w2.date(), w2.hour(), w2.minute(), 0);
    guess += target - wallAsUtc;
  }
  const w = venueWallClock(new Date(guess), tz);
  const offsetMin = Math.round((Date.UTC(w.year(), w.month(), w.date(), w.hour(), w.minute(), 0) - guess) / 6e4);
  const sign = offsetMin >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMin);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date}T${hhmm}:00${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

const LATE_AFTER_MINUTES = 15;
function classifyLive(playStatus, startAt, endAt, now = dayjs()) {
  switch (playStatus) {
    case "finished":
      return "done";
    case "no_show":
      return "no_show";
    case "arrived":
      return "arrived";
    case "playing":
      return now.isAfter(dayjs(endAt)) ? "overdue" : "playing";
  }
  if (now.isBefore(dayjs(startAt))) return "upcoming";
  if (now.isAfter(dayjs(startAt).add(LATE_AFTER_MINUTES, "minute"))) return "late";
  return "waiting";
}
const LIVE_META = {
  upcoming: { color: "#b7eb8f", key: "court.live.upcoming", label: "S\u1EAFp t\u1EDBi" },
  waiting: { color: "#ffe58f", key: "court.live.waiting", label: "Ch\u1EDD kh\xE1ch" },
  arrived: { color: "#b5f5ec", key: "court.live.arrived", label: "\u0110\xE3 \u0111\u1EBFn" },
  late: { color: "#ffbb96", key: "court.live.late", label: "Tr\u1EC5 gi\u1EDD" },
  playing: { color: "#91d5ff", key: "court.live.playing", label: "\u0110ang ch\u01A1i" },
  overdue: { color: "#ff9c6e", key: "court.live.overdue", label: "Qu\xE1 gi\u1EDD" },
  done: { color: "#d9d9d9", key: "court.live.done", label: "\u0110\xE3 k\u1EBFt th\xFAc" },
  no_show: { color: "#ffa39e", key: "court.live.noShow", label: "Kh\xF4ng \u0111\u1EBFn" },
  canceled: { color: "#f0f0f0", key: "court.live.canceled", label: "\u0110\xE3 hu\u1EF7" }
};
function allowedActions(playStatus, live, started = live !== "upcoming") {
  switch (playStatus ?? "booked") {
    case "booked":
      return live === "upcoming" ? ["arrived", "playing"] : ["arrived", "playing", "no_show", "extend"];
    case "arrived":
      return !started ? ["playing"] : ["playing", "no_show", "extend"];
    case "playing":
      return ["finished", "extend", "finish_early"];
    default:
      return [];
  }
}

const DATE_FORMAT$3 = "YYYY-MM-DD";
const PLAY_ACTION_LABEL = {
  arrived: { key: "court.live.actArrived", label: "Kh\xE1ch \u0111\xE3 \u0111\u1EBFn" },
  playing: { key: "court.live.actStart", label: "B\u1EAFt \u0111\u1EA7u ch\u01A1i" },
  finished: { key: "court.live.actFinish", label: "K\u1EBFt th\xFAc" },
  no_show: { key: "court.live.actNoShow", label: "Kh\xF4ng \u0111\u1EBFn" },
  extend: { key: "court.live.actExtend", label: "Gia h\u1EA1n +30\u2032" },
  finish_early: { key: "court.live.actFinishEarly", label: "K\u1EBFt th\xFAc s\u1EDBm" }
};
const LABEL_COL_WIDTH = 140;
const ROW_HEIGHT = 48;
const HEADER_HEIGHT = 28;
const SLOT_MIN_WIDTH = 44;
const BookingTimeline = ({
  courts,
  bookings,
  orderSummaries,
  orderDeposits,
  date,
  globalOpenTime,
  globalCloseTime,
  gridSlotMinutes,
  venue,
  onOrderPaid,
  onPlayAction,
  onCancelOrder,
  onRescheduleOrder,
  onCheckinOrder
}) => {
  const { t, getOrderUrl } = useCourtContext();
  const { markPaid, pendingRef, canMarkPaid, canRecordRecurringPayment } = useMarkOrderPaid(
    venue?.supplierId ?? 0,
    venue?.eventSlug,
    (orderRef, summary) => {
      onOrderPaid?.(orderRef, summary);
    }
  );
  const tz = courts.find((c) => c.timezone)?.timezone || DEFAULT_VENUE_TZ;
  const tzFor = (resourceId) => courts.find((c) => c.resourceId === resourceId)?.timezone || tz;
  const openAnchor = useMemo(
    () => dayjs(`${date.format(DATE_FORMAT$3)} ${globalOpenTime}`),
    [date, globalOpenTime]
  );
  const closeAnchor = useMemo(
    () => dayjs(`${date.format(DATE_FORMAT$3)} ${globalCloseTime}`),
    [date, globalCloseTime]
  );
  const totalMinutes = Math.max(0, closeAnchor.diff(openAnchor, "minute"));
  const safeSlotMinutes = gridSlotMinutes > 0 ? gridSlotMinutes : 60;
  const totalSlots = Math.max(1, Math.ceil(totalMinutes / safeSlotMinutes));
  const [nowPct, setNowPct] = useState(null);
  useEffect(() => {
    const isToday = date.isSame(venueWallClock(/* @__PURE__ */ new Date(), tz), "day");
    if (!isToday || totalMinutes <= 0) {
      setNowPct(null);
      return;
    }
    const update = () => {
      const pct = venueWallClock(/* @__PURE__ */ new Date(), tz).diff(openAnchor, "minute") / totalMinutes * 100;
      setNowPct(pct >= 0 && pct <= 100 ? pct : null);
    };
    update();
    const timer = setInterval(update, 6e4);
    return () => clearInterval(timer);
  }, [date, openAnchor, closeAnchor, totalMinutes, tz]);
  const bookingsByCourt = useMemo(() => {
    const map = {};
    courts.forEach((c) => {
      map[c.resourceId] = [];
    });
    bookings.forEach((b) => {
      if (!map[b.resourceId]) map[b.resourceId] = [];
      map[b.resourceId].push(b);
    });
    return map;
  }, [courts, bookings]);
  const hourTicks = useMemo(() => {
    const ticks = [];
    let cursor = openAnchor.startOf("hour");
    if (cursor.isBefore(openAnchor)) cursor = cursor.add(1, "hour");
    while (cursor.isBefore(closeAnchor) || cursor.isSame(closeAnchor)) {
      const col = Math.round(cursor.diff(openAnchor, "minute") / safeSlotMinutes) + 1;
      if (col >= 1 && col <= totalSlots + 1) {
        ticks.push({ col, label: cursor.format("HH:mm") });
      }
      cursor = cursor.add(1, "hour");
    }
    return ticks;
  }, [openAnchor, closeAnchor, totalSlots, safeSlotMinutes]);
  if (courts.length === 0) {
    return /* @__PURE__ */ jsx("div", { style: { padding: 24, textAlign: "center", color: "rgba(0,0,0,0.45)" }, children: t("court.courts.empty", { defaultValue: "C\u1EE5m s\xE2n n\xE0y ch\u01B0a c\xF3 s\xE2n n\xE0o." }) });
  }
  const gridTemplateColumns = `${LABEL_COL_WIDTH}px repeat(${totalSlots}, minmax(${SLOT_MIN_WIDTH}px, 1fr))`;
  const gridTemplateRows = `${HEADER_HEIGHT}px repeat(${courts.length}, ${ROW_HEIGHT}px)`;
  return /* @__PURE__ */ jsxs("div", { style: { position: "relative", overflowX: "auto" }, children: [
    /* @__PURE__ */ jsxs("div", { style: { display: "grid", gridTemplateColumns, gridTemplateRows, minWidth: LABEL_COL_WIDTH + totalSlots * SLOT_MIN_WIDTH }, children: [
      /* @__PURE__ */ jsx("div", { style: { gridColumn: 1, gridRow: 1 } }),
      hourTicks.map((tick) => /* @__PURE__ */ jsx(
        "div",
        {
          style: {
            gridColumn: tick.col + 1,
            gridRow: 1,
            fontSize: 11,
            color: "rgba(0,0,0,0.45)",
            borderLeft: "1px solid #f0f0f0",
            paddingLeft: 2
          },
          children: tick.label
        },
        tick.col
      )),
      courts.map((court, idx) => /* @__PURE__ */ jsxs(React.Fragment, { children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              gridColumn: 1,
              gridRow: idx + 2,
              display: "flex",
              alignItems: "center",
              fontWeight: 500,
              borderTop: "1px solid #f0f0f0",
              paddingRight: 8
            },
            title: court.name,
            children: court.name
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              gridColumn: `2 / ${totalSlots + 2}`,
              gridRow: idx + 2,
              borderTop: "1px solid #f0f0f0",
              background: idx % 2 === 0 ? "#fff" : "#fafafa"
            }
          }
        ),
        (bookingsByCourt[court.resourceId] ?? []).map((booking) => {
          const rawStart = Math.floor(venueWallClock(booking.startAt, tzFor(booking.resourceId)).diff(openAnchor, "minute") / safeSlotMinutes) + 1;
          const rawEnd = Math.ceil(venueWallClock(booking.endAt, tzFor(booking.resourceId)).diff(openAnchor, "minute") / safeSlotMinutes) + 1;
          const colStart = Math.min(Math.max(rawStart, 1), totalSlots + 1) + 1;
          const colEndRaw = Math.min(Math.max(rawEnd, 1), totalSlots + 1) + 1;
          const colEnd = Math.max(colEndRaw, colStart + 1);
          const summary = booking.orderRef ? orderSummaries[booking.orderRef] : void 0;
          const displayName = summary?.attendeeName || summary?.email || (booking.orderRef || t("court.bookings.orderRefEmpty", { defaultValue: "\u2014" }));
          const orderRef = booking.orderRef;
          const isPending = !!orderRef && pendingRef === orderRef;
          const isRecurringPending = !!summary?.pendingPeriod;
          const canConfirmPaid = !!orderRef && summary?.orderStatus !== "p" && (isRecurringPending ? canRecordRecurringPayment : canMarkPaid);
          const live = booking.status === "canceled" ? "canceled" : classifyLive(booking.playStatus, booking.startAt, booking.endAt);
          const liveMeta = LIVE_META[live];
          const actions = onPlayAction ? allowedActions(booking.playStatus, live, !dayjs().isBefore(dayjs(booking.startAt))) : [];
          const block = /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                gridColumn: `${colStart} / ${colEnd}`,
                gridRow: idx + 2,
                margin: "4px 2px",
                padding: "2px 6px",
                borderRadius: 6,
                background: booking.status === "confirmed" || booking.playStatus ? liveMeta.color : "#ffe58f",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: 12,
                cursor: orderRef && venue ? "pointer" : "default",
                zIndex: 1
              },
              children: [
                displayName,
                live !== "upcoming" && live !== "waiting" && /* @__PURE__ */ jsxs("span", { style: { marginLeft: 6, opacity: 0.7 }, children: [
                  "\xB7 ",
                  t(liveMeta.key, { defaultValue: liveMeta.label })
                ] })
              ]
            }
          );
          if (!venue || !orderRef && actions.length === 0) {
            return /* @__PURE__ */ jsx(React.Fragment, { children: block }, booking.reservationId);
          }
          return /* @__PURE__ */ jsx(
            Popover,
            {
              trigger: "click",
              title: `${formatVenueTime(booking.startAt, tzFor(booking.resourceId))}\u2013${formatVenueTime(booking.endAt, tzFor(booking.resourceId))}`,
              content: /* @__PURE__ */ jsxs(Space, { direction: "vertical", size: 4, style: { maxWidth: 240 }, children: [
                summary?.attendeeName && /* @__PURE__ */ jsx(Typography.Text, { strong: true, children: summary.attendeeName }),
                summary?.phone && /* @__PURE__ */ jsx(Typography.Text, { type: "secondary", children: summary.phone }),
                /* @__PURE__ */ jsx(Typography.Text, { type: "secondary", children: t(liveMeta.key, { defaultValue: liveMeta.label }) }),
                actions.length > 0 && /* @__PURE__ */ jsx(Space, { wrap: true, size: 4, children: actions.map((a) => /* @__PURE__ */ jsx(Button, { size: "small", danger: a === "no_show", onClick: () => void onPlayAction?.(booking, a), children: PLAY_ACTION_LABEL[a] ? t(PLAY_ACTION_LABEL[a].key, { defaultValue: PLAY_ACTION_LABEL[a].label }) : a }, a)) }),
                orderRef && /* @__PURE__ */ jsxs(Space, { size: 4, children: [
                  /* @__PURE__ */ jsxs(Typography.Text, { type: "secondary", children: [
                    t("court.bookings.orderRefColumn", { defaultValue: "\u0110\u01A1n h\xE0ng" }),
                    ": ",
                    orderRef
                  ] }),
                  summary?.orderStatus === "n" && (orderDeposits?.[orderRef]?.paid ?? 0) > 0 ? /* @__PURE__ */ jsxs(Tag, { color: "blue", children: [
                    t("court.bookings.depositPaidTag", { defaultValue: "\u0110\xE3 c\u1ECDc" }),
                    " ",
                    orderDeposits[orderRef].paid.toLocaleString("vi-VN"),
                    "/",
                    orderDeposits[orderRef].total.toLocaleString("vi-VN"),
                    "\u0111"
                  ] }) : /* @__PURE__ */ jsx(OrderStatusTag, { orderStatus: summary?.orderStatus })
                ] }),
                isRecurringPending && summary?.pendingPeriod && /* @__PURE__ */ jsx(Typography.Text, { type: "warning", children: t("court.bookings.recurringPendingPeriod", {
                  defaultValue: `G\xF3i \u0111\u1EB7t \u0111\u1ECBnh k\u1EF3 \u2014 \u0111ang ch\u1EDD tr\u1EA3 k\u1EF3 ${summary.pendingPeriod.periodIndex + 1}: ${summary.pendingPeriod.amountMinor.toLocaleString("vi-VN")}\u0111`,
                  periodIndex: summary.pendingPeriod.periodIndex + 1,
                  amount: summary.pendingPeriod.amountMinor.toLocaleString("vi-VN")
                }) }),
                canConfirmPaid && /* @__PURE__ */ jsx(
                  Popconfirm,
                  {
                    title: t("court.bookings.markPaidConfirm", { defaultValue: "X\xE1c nh\u1EADn \u0111\xE3 thu ti\u1EC1n cho \u0111\u01A1n n\xE0y?" }),
                    onConfirm: () => void markPaid(orderRef, isRecurringPending),
                    okText: t("common.confirm", { defaultValue: "X\xE1c nh\u1EADn" }),
                    cancelText: t("common.cancel", { defaultValue: "Hu\u1EF7" }),
                    children: /* @__PURE__ */ jsx(Button, { type: "primary", size: "small", loading: isPending, block: true, children: summary?.orderStatus === "n" && (orderDeposits?.[orderRef]?.paid ?? 0) > 0 ? t("court.bookings.collectRemaining", { defaultValue: "Thu ph\u1EA7n c\xF2n l\u1EA1i" }) : t("court.bookings.create.markPaid", { defaultValue: "X\xE1c nh\u1EADn \u0111\xE3 thu ti\u1EC1n" }) })
                  }
                ),
                orderRef && onCheckinOrder && (live === "upcoming" || live === "waiting" || live === "late") && /* @__PURE__ */ jsx(Button, { size: "small", onClick: () => onCheckinOrder(booking), children: t("court.checkin.button", { defaultValue: "M\xE3 check-in" }) }),
                orderRef && onRescheduleOrder && (live === "upcoming" || live === "waiting") && /* @__PURE__ */ jsx(Button, { size: "small", onClick: () => onRescheduleOrder(booking), children: t("court.reschedule.title", { defaultValue: "\u0110\u1ED5i l\u1ECBch" }) }),
                orderRef && onCancelOrder && live !== "done" && live !== "no_show" && live !== "canceled" && /* @__PURE__ */ jsx(Button, { size: "small", danger: true, onClick: () => onCancelOrder(booking), children: t("court.cancel.title", { defaultValue: "Hu\u1EF7 \u0111\u01A1n" }) }),
                orderRef && /* @__PURE__ */ jsx("a", { href: getOrderUrl(venue.eventSlug, orderRef), target: "_blank", rel: "noreferrer", children: t("court.bookings.viewOrderDetails", { defaultValue: "Xem chi ti\u1EBFt \u0111\u01A1n h\xE0ng \u2197" }) })
              ] }),
              children: block
            },
            booking.reservationId
          );
        })
      ] }, court.resourceId))
    ] }),
    nowPct !== null && /* @__PURE__ */ jsx(
      "div",
      {
        style: {
          position: "absolute",
          top: HEADER_HEIGHT,
          bottom: 0,
          left: `calc(${LABEL_COL_WIDTH}px + (100% - ${LABEL_COL_WIDTH}px) * ${nowPct} / 100)`,
          width: 2,
          background: "#ff4d4f",
          pointerEvents: "none",
          zIndex: 2
        }
      }
    )
  ] });
};

const money = (n) => `${Math.round(n).toLocaleString("vi-VN")}\u0111`;
const CancelOrderModal = ({ supplierId, orderCode, defaultBy = "staff", onClose, onDone }) => {
  const { t, api } = useCourtContext();
  const [by, setBy] = useState("staff");
  const [override, setOverride] = useState(null);
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!orderCode) return;
    setBy(defaultBy);
    setOverride(null);
    setReason("");
    setPreview(null);
  }, [orderCode, defaultBy]);
  useEffect(() => {
    if (!orderCode) return;
    let cancelled = false;
    setLoading(true);
    const q = `orders/cancel-preview?order_code=${encodeURIComponent(orderCode)}&by=${by}` + (override != null && by === "staff" ? `&refund_percent=${override}` : "");
    api.request(supplierId, q, void 0, "GET").then((r) => !cancelled && setPreview(r.preview)).catch((err) => err instanceof Error && message.error(err.message)).finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [api, supplierId, orderCode, by, override]);
  const submit = async () => {
    if (!orderCode) return;
    setSaving(true);
    try {
      const body = { order_code: orderCode, by, reason };
      if (override != null && by === "staff") body.refund_percent = override;
      const r = await api.request(supplierId, "orders/cancel", body, "POST");
      const refund = r?.preview?.refundMinor ?? 0;
      if (refund > 0) {
        message.success(
          r?.refundStatus === "failed" ? t("court.cancel.doneRefundFailed", { defaultValue: "\u0110\xE3 hu\u1EF7 \u0111\u01A1n v\xE0 nh\u1EA3 s\xE2n. T\u1EA1o t\xE1c v\u1EE5 ho\xE0n ti\u1EC1n ch\u01B0a th\xE0nh c\xF4ng \u2014 m\u1EDF l\u1EA1i \u0111\u01A1n \u0111\u1EC3 th\u1EED l\u1EA1i." }) : t("court.cancel.doneRefund", { defaultValue: '\u0110\xE3 hu\u1EF7 \u0111\u01A1n v\xE0 nh\u1EA3 s\xE2n. Ho\xE0n ti\u1EC1n: xem "Ho\xE0n ti\u1EC1n ch\u1EDD x\u1EED l\xFD".' })
        );
      } else {
        message.success(t("court.cancel.done", { defaultValue: "\u0110\xE3 hu\u1EF7 \u0111\u01A1n v\xE0 nh\u1EA3 s\xE2n." }));
      }
      onDone();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  return /* @__PURE__ */ jsx(
    Modal,
    {
      open: !!orderCode,
      title: t("court.cancel.title", { defaultValue: "Hu\u1EF7 \u0111\u01A1n" }) + (orderCode ? ` \u2014 ${orderCode}` : ""),
      onCancel: onClose,
      onOk: () => void submit(),
      okText: t("court.cancel.confirm", { defaultValue: "Hu\u1EF7 \u0111\u01A1n" }),
      okButtonProps: { danger: true, disabled: !preview?.cancelable },
      cancelText: t("common.close", { defaultValue: "\u0110\xF3ng" }),
      confirmLoading: saving,
      destroyOnClose: true,
      children: /* @__PURE__ */ jsxs(Spin, { spinning: loading, children: [
        /* @__PURE__ */ jsx(
          Select,
          {
            value: by,
            onChange: (v) => {
              setBy(v);
              if (v === "venue") setOverride(null);
            },
            style: { width: "100%", marginBottom: 12 },
            options: [
              { value: "staff", label: t("court.cancel.byStaff", { defaultValue: "Kh\xE1ch y\xEAu c\u1EA7u hu\u1EF7 (\xE1p d\u1EE5ng ch\xEDnh s\xE1ch)" }) },
              { value: "venue", label: t("court.cancel.byVenue", { defaultValue: "C\u01A1 s\u1EDF hu\u1EF7 (b\u1EA3o tr\xEC, th\u1EDDi ti\u1EBFt\u2026)" }) }
            ]
          }
        ),
        preview && !preview.cancelable && /* @__PURE__ */ jsx(Alert, { type: "warning", showIcon: true, message: preview.reason || t("court.cancel.notCancelable", { defaultValue: "\u0110\u01A1n n\xE0y kh\xF4ng hu\u1EF7 \u0111\u01B0\u1EE3c." }), style: { marginBottom: 12 } }),
        preview && /* @__PURE__ */ jsxs(Descriptions, { size: "small", column: 1, bordered: true, children: [
          /* @__PURE__ */ jsx(Descriptions.Item, { label: t("court.cancel.paid", { defaultValue: "Kh\xE1ch \u0111\xE3 tr\u1EA3" }), children: money(preview.paidMinor) }),
          /* @__PURE__ */ jsx(Descriptions.Item, { label: t("court.cancel.hours", { defaultValue: "C\xF2n t\u1EDBi gi\u1EDD ch\u01A1i" }), children: preview.hoursUntilStart > 0 ? `${preview.hoursUntilStart.toFixed(1)} gi\u1EDD` : t("court.cancel.started", { defaultValue: "\u0111\xE3 t\u1EDBi/qua gi\u1EDD ch\u01A1i" }) }),
          /* @__PURE__ */ jsxs(Descriptions.Item, { label: t("court.cancel.percent", { defaultValue: "M\u1EE9c ho\xE0n \xE1p d\u1EE5ng" }), children: [
            preview.refundPercent,
            "%",
            preview.overridden ? " (nh\xE2n vi\xEAn \u0111i\u1EC1u ch\u1EC9nh)" : "",
            !preview.hasPolicy && by === "staff" && /* @__PURE__ */ jsxs(Typography.Text, { type: "secondary", children: [
              " \u2014 ",
              t("court.cancel.noPolicy", { defaultValue: "c\u01A1 s\u1EDF ch\u01B0a c\u1EA5u h\xECnh m\u1ED1c hu\u1EF7" })
            ] })
          ] }),
          /* @__PURE__ */ jsx(Descriptions.Item, { label: t("court.cancel.refund", { defaultValue: "Ho\xE0n cho kh\xE1ch" }), children: /* @__PURE__ */ jsx("strong", { children: money(preview.refundMinor) }) }),
          /* @__PURE__ */ jsx(Descriptions.Item, { label: t("court.cancel.forfeit", { defaultValue: "C\u01A1 s\u1EDF gi\u1EEF l\u1EA1i" }), children: money(preview.forfeitMinor) })
        ] }),
        by === "staff" && /* @__PURE__ */ jsxs("div", { style: { marginTop: 12 }, children: [
          /* @__PURE__ */ jsx(Typography.Text, { type: "secondary", children: t("court.cancel.override", { defaultValue: "\u0110i\u1EC1u ch\u1EC9nh m\u1EE9c ho\xE0n (tu\u1EF3 ch\u1ECDn, v\xED d\u1EE5 \u01B0u \xE1i kh\xE1ch):" }) }),
          /* @__PURE__ */ jsx(InputNumber, { min: 0, max: 100, addonAfter: "%", value: override ?? void 0, onChange: (v) => setOverride(v == null ? null : Number(v)), style: { marginLeft: 8 } })
        ] }),
        /* @__PURE__ */ jsx(Input.TextArea, { rows: 2, placeholder: t("court.cancel.reason", { defaultValue: "L\xFD do hu\u1EF7" }), value: reason, onChange: (e) => setReason(e.target.value), style: { marginTop: 12 } })
      ] })
    }
  );
};

const RescheduleOrderModal = ({ supplierId, orderCode, courts, currentResourceId, currentStartAt, currentEndAt, onClose, onDone }) => {
  const { t, api } = useCourtContext();
  const [form] = Form.useForm();
  const [saving, setSaving] = React.useState(false);
  const initialValues = useMemo(() => {
    const tz = courts.find((c) => c.resourceId === currentResourceId)?.timezone || DEFAULT_VENUE_TZ;
    if (currentStartAt && currentEndAt) {
      const s0 = venueWallClock(currentStartAt, tz);
      const e0 = venueWallClock(currentEndAt, tz);
      return { resourceId: currentResourceId, date: s0, time: s0, minutes: Math.max(15, e0.diff(s0, "minute")), reason: "", ignorePolicy: false };
    }
    return { resourceId: currentResourceId, date: dayjs(), time: dayjs().startOf("hour").add(1, "hour"), minutes: 60, reason: "", ignorePolicy: false };
  }, [courts, currentResourceId, currentStartAt, currentEndAt]);
  const submit = async () => {
    if (!orderCode) return;
    try {
      const v = await form.validateFields();
      setSaving(true);
      const court = courts.find((c) => c.resourceId === v.resourceId);
      const tz = court?.timezone || DEFAULT_VENUE_TZ;
      const date = v.date.format("YYYY-MM-DD");
      const startHHmm = v.time.format("HH:mm");
      const endTotal = v.time.hour() * 60 + v.time.minute() + Number(v.minutes);
      const endHHmm = `${String(Math.floor(endTotal / 60) % 24).padStart(2, "0")}:${String(endTotal % 60).padStart(2, "0")}`;
      const r = await api.request(
        supplierId,
        "orders/reschedule",
        {
          order_code: orderCode,
          resource_id: v.resourceId === currentResourceId ? "" : v.resourceId,
          start_at: venueLocalToISO(date, startHHmm, tz),
          end_at: venueLocalToISO(date, endHHmm, tz),
          reason: v.reason || "",
          ignore_policy: !!v.ignorePolicy
        },
        "POST"
      );
      const diff = r?.priceDiffMinor ?? 0;
      message.success(
        t("court.reschedule.done", { defaultValue: "\u0110\xE3 \u0111\u1ED5i l\u1ECBch." }) + (diff !== 0 ? ` Gi\xE1 thay \u0111\u1ED5i ${diff > 0 ? "+" : ""}${diff.toLocaleString("vi-VN")}\u0111.` : "") + (r?.oldSlotLeaked ? " L\u01B0u \xFD: ch\u01B0a nh\u1EA3 \u0111\u01B0\u1EE3c ch\u1ED7 c\u0169 \u1EDF h\u1EC7 th\u1ED1ng s\xE2n \u2014 b\xE1o qu\u1EA3n tr\u1ECB." : "")
      );
      onDone();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  return /* @__PURE__ */ jsxs(
    Modal,
    {
      open: !!orderCode,
      title: t("court.reschedule.title", { defaultValue: "\u0110\u1ED5i l\u1ECBch" }) + (orderCode ? ` \u2014 ${orderCode}` : ""),
      onCancel: onClose,
      onOk: () => void submit(),
      okText: t("court.reschedule.confirm", { defaultValue: "\u0110\u1ED5i l\u1ECBch" }),
      cancelText: t("common.close", { defaultValue: "\u0110\xF3ng" }),
      confirmLoading: saving,
      destroyOnClose: true,
      children: [
        /* @__PURE__ */ jsx(
          Alert,
          {
            type: "info",
            showIcon: true,
            style: { marginBottom: 12 },
            message: t("court.reschedule.hint", { defaultValue: "Ch\u1ED7 m\u1EDBi \u0111\u01B0\u1EE3c gi\u1EEF tr\u01B0\u1EDBc, ch\u1ED7 c\u0169 ch\u1EC9 nh\u1EA3 khi \u0111\u1ED5i th\xE0nh c\xF4ng. C\u1ECDc \u0111\xE3 thu gi\u1EEF nguy\xEAn." })
          }
        ),
        /* @__PURE__ */ jsxs(Form, { form, layout: "vertical", initialValues, children: [
          /* @__PURE__ */ jsx(Form.Item, { name: "resourceId", label: t("court.reschedule.court", { defaultValue: "S\xE2n" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(Select, { options: courts.map((c) => ({ value: c.resourceId, label: c.name })) }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "date", label: t("court.reschedule.date", { defaultValue: "Ng\xE0y" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(DatePicker, { format: "DD/MM/YYYY", style: { width: "100%" } }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "time", label: t("court.reschedule.start", { defaultValue: "Gi\u1EDD b\u1EAFt \u0111\u1EA7u" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(TimePicker, { format: "HH:mm", minuteStep: 15, style: { width: "100%" } }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "minutes", label: t("court.reschedule.duration", { defaultValue: "Th\u1EDDi l\u01B0\u1EE3ng (ph\xFAt)" }), rules: [{ required: true, type: "number", min: 15 }], children: /* @__PURE__ */ jsx(InputNumber, { min: 15, step: 30, style: { width: "100%" } }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "reason", label: t("court.reschedule.reason", { defaultValue: "L\xFD do" }), children: /* @__PURE__ */ jsx(Input, {}) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "ignorePolicy", valuePropName: "checked", children: /* @__PURE__ */ jsx(Checkbox, { children: t("court.reschedule.ignorePolicy", { defaultValue: "B\u1ECF qua \u0111i\u1EC1u ki\u1EC7n \u0111\u1ED5i l\u1ECBch c\u1EE7a ch\xEDnh s\xE1ch (\u01B0u \xE1i kh\xE1ch)" }) }) })
        ] }, orderCode ?? "none")
      ]
    }
  );
};

const CheckinQrModal = ({ supplierId, orderCode, onClose }) => {
  const { t, api } = useCourtContext();
  const [png, setPng] = useState("");
  const [left, setLeft] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const timer = useRef(null);
  const seq = useRef(0);
  const stop = () => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
  };
  const generate = useCallback(async () => {
    if (!orderCode) return;
    const my = ++seq.current;
    stop();
    setLoading(true);
    setError("");
    setPng("");
    try {
      const r = await api.request(supplierId, "orders/checkin-token", { order_code: orderCode }, "POST");
      if (my !== seq.current) return;
      const img = r.qrPng ?? r.qr_png ?? "";
      const ttl = r.ttlSeconds ?? r.ttl_seconds ?? 300;
      if (!img) throw new Error(t("court.checkin.noQr", { defaultValue: "Kh\xF4ng t\u1EA1o \u0111\u01B0\u1EE3c m\xE3 QR, th\u1EED l\u1EA1i." }));
      setPng(img);
      setLeft(ttl);
      timer.current = setInterval(() => setLeft((s) => s > 0 ? s - 1 : 0), 1e3);
    } catch (err) {
      if (my === seq.current) setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, [api, supplierId, orderCode, t]);
  useEffect(() => {
    if (orderCode) void generate();
    return () => {
      seq.current++;
      stop();
    };
  }, [orderCode, generate]);
  const expired = !!png && left <= 0;
  const mm = String(Math.floor(left / 60)).padStart(1, "0");
  const ss = String(left % 60).padStart(2, "0");
  return /* @__PURE__ */ jsx(
    Modal,
    {
      open: !!orderCode,
      title: t("court.checkin.title", { defaultValue: "M\xE3 check-in cho kh\xE1ch" }),
      onCancel: onClose,
      footer: [
        /* @__PURE__ */ jsx(Button, { onClick: () => void generate().catch((e) => message.error(String(e))), disabled: loading, children: t("court.checkin.regenerate", { defaultValue: "T\u1EA1o m\xE3 m\u1EDBi" }) }, "new"),
        /* @__PURE__ */ jsx(Button, { type: "primary", onClick: onClose, children: t("common.close", { defaultValue: "\u0110\xF3ng" }) }, "close")
      ],
      width: 400,
      destroyOnClose: true,
      children: /* @__PURE__ */ jsxs("div", { style: { textAlign: "center" }, children: [
        /* @__PURE__ */ jsx(Typography.Paragraph, { type: "secondary", children: t("court.checkin.hint", { defaultValue: "Kh\xE1ch m\u1EDF Zalo Mini App v\xE0 qu\xE9t m\xE3 n\xE0y. Ch\u1EC9 ng\u01B0\u1EDDi \u0111\u1EB7t s\xE2n m\u1EDBi check-in \u0111\u01B0\u1EE3c, trong kho\u1EA3ng 30 ph\xFAt tr\u01B0\u1EDBc \u0111\u1EBFn 60 ph\xFAt sau gi\u1EDD b\u1EAFt \u0111\u1EA7u." }) }),
        loading && /* @__PURE__ */ jsx(Spin, {}),
        error && /* @__PURE__ */ jsx(Alert, { type: "error", showIcon: true, message: error }),
        png && /* @__PURE__ */ jsx("div", { style: { opacity: expired ? 0.25 : 1 }, children: /* @__PURE__ */ jsx("img", { src: png, alt: t("court.checkin.qrAlt", { defaultValue: "M\xE3 QR check-in" }), width: 280, height: 280 }) }),
        png && /* @__PURE__ */ jsx(Typography.Paragraph, { strong: true, type: expired ? "danger" : void 0, style: { marginTop: 8 }, children: expired ? t("court.checkin.expired", { defaultValue: 'M\xE3 \u0111\xE3 h\u1EBFt h\u1EA1n \u2014 b\u1EA5m "T\u1EA1o m\xE3 m\u1EDBi".' }) : `${t("court.checkin.expiresIn", { defaultValue: "C\xF2n hi\u1EC7u l\u1EF1c" })} ${mm}:${ss}` }),
        /* @__PURE__ */ jsx(Typography.Text, { type: "secondary", style: { fontSize: 12 }, children: t("court.checkin.oldInvalid", { defaultValue: "T\u1EA1o m\xE3 m\u1EDBi th\xEC m\xE3 c\u0169 h\u1EBFt hi\u1EC7u l\u1EF1c ngay. M\u1ED7i m\xE3 ch\u1EC9 d\xF9ng m\u1ED9t l\u1EA7n." }) })
      ] })
    }
  );
};

const ManualCheckinModal = ({ supplierId, open, onClose, onDone }) => {
  const { t, api } = useCourtContext();
  const [code, setCode] = useState("");
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) {
      setCode("");
      setOverride(false);
      setReason("");
      setError("");
    }
  }, [open]);
  const submit = async () => {
    const c = code.trim();
    if (!c) return;
    if (override && !reason.trim()) {
      setError(t("court.checkin.reasonRequired", { defaultValue: "\u0110\u1ED5i \u0111\u01A1n kh\xF4ng \u0111\u1EBFn th\xE0nh \u0111\xE3 \u0111\u1EBFn b\u1EAFt bu\u1ED9c ghi l\xFD do." }));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const r = await api.request(supplierId, "orders/checkin", { order_code: c, override, reason: reason.trim() }, "POST");
      message.success(r?.checkin?.already ? t("court.checkin.already", { defaultValue: "\u0110\u01A1n n\xE0y \u0111\xE3 check-in tr\u01B0\u1EDBc \u0111\xF3." }) : t("court.checkin.done", { defaultValue: "\u0110\xE3 check-in cho kh\xE1ch." }));
      onDone();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };
  return /* @__PURE__ */ jsxs(
    Modal,
    {
      open,
      title: t("court.checkin.manualTitle", { defaultValue: "Check-in b\u1EB1ng m\xE3 \u0111\u1EB7t ch\u1ED7" }),
      onCancel: onClose,
      onOk: () => void submit(),
      okText: t("court.checkin.manualOk", { defaultValue: "Check-in" }),
      cancelText: t("common.cancel", { defaultValue: "Hu\u1EF7" }),
      okButtonProps: { loading: saving, disabled: !code.trim() },
      destroyOnClose: true,
      children: [
        /* @__PURE__ */ jsx(
          Input,
          {
            autoFocus: true,
            value: code,
            onChange: (e) => setCode(e.target.value),
            onPressEnter: () => void submit(),
            placeholder: t("court.checkin.codePlaceholder", { defaultValue: "Nh\u1EADp m\xE3 \u0111\u1EB7t ch\u1ED7 c\u1EE7a kh\xE1ch" }),
            maxLength: 40,
            style: { marginBottom: 12 }
          }
        ),
        /* @__PURE__ */ jsx(Checkbox, { checked: override, onChange: (e) => setOverride(e.target.checked), children: t("court.checkin.overrideLabel", { defaultValue: '\u0110\u01A1n \u0111\xE3 b\u1ECB ghi "kh\xF4ng \u0111\u1EBFn" \u2014 \u0111\u1ED5i th\xE0nh \u0111\xE3 \u0111\u1EBFn' }) }),
        override && /* @__PURE__ */ jsx(
          Input.TextArea,
          {
            style: { marginTop: 8 },
            rows: 2,
            maxLength: 300,
            value: reason,
            onChange: (e) => setReason(e.target.value),
            placeholder: t("court.checkin.reasonPlaceholder", { defaultValue: "L\xFD do (b\u1EAFt bu\u1ED9c, \u0111\u01B0\u1EE3c ghi v\xE0o nh\u1EADt k\xFD)" })
          }
        ),
        error && /* @__PURE__ */ jsx(Alert, { style: { marginTop: 12 }, type: "error", showIcon: true, message: error })
      ]
    }
  );
};

const RESERVATION_STATUS_OPTIONS = [
  { value: "held", key: "court.bookings.reservationStatus.held", defaultLabel: "\u0110ang gi\u1EEF ch\u1ED7" },
  { value: "confirmed", key: "court.bookings.reservationStatus.confirmed", defaultLabel: "\u0110\xE3 x\xE1c nh\u1EADn" }
];
const DATE_FORMAT$2 = "YYYY-MM-DD";
const CONCURRENCY_LIMIT = 8;
async function settleWithConcurrency(items, limit, fn) {
  const results = new Array(items.length);
  let cursor = 0;
  const workers = new Array(Math.min(limit, items.length)).fill(null).map(async () => {
    while (cursor < items.length) {
      const idx = cursor++;
      try {
        results[idx] = { status: "fulfilled", value: await fn(items[idx], idx) };
      } catch (error) {
        results[idx] = { status: "rejected", reason: error };
      }
    }
  });
  await Promise.all(workers);
  return results;
}
const BookingsPage = () => {
  const { t, api, venues, getOrderUrl, onCreateBooking, getOrderSummary } = useCourtContext();
  const navigate = useNavigate();
  const { supplierId } = useParams();
  const [date, setDate] = useState(dayjs());
  const [bookings, setBookings] = useState([]);
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [cancelOrderCode, setCancelOrderCode] = useState(null);
  const [checkinQrCode, setCheckinQrCode] = useState(null);
  const [manualCheckinOpen, setManualCheckinOpen] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [viewMode, setViewMode] = useState("timeline");
  const [globalOpenTime, setGlobalOpenTime] = useState("06:00");
  const [globalCloseTime, setGlobalCloseTime] = useState("22:00");
  const [gridSlotMinutes, setGridSlotMinutes] = useState(60);
  const [orderSummaries, setOrderSummaries] = useState({});
  const [orderDeposits, setOrderDeposits] = useState({});
  const [searchText, setSearchText] = useState("");
  const [filterResourceIds, setFilterResourceIds] = useState([]);
  const [filterOrderStatuses, setFilterOrderStatuses] = useState([]);
  const [filterReservationStatuses, setFilterReservationStatuses] = useState([]);
  const venue = venues.find((v) => String(v.supplierId) === supplierId);
  const mergeOrderSummary = useCallback((orderRef, summary) => {
    setOrderSummaries((prev) => ({ ...prev, [orderRef]: summary }));
  }, []);
  const {
    markPaid: markPaidFromTable,
    pendingRef: pendingRefFromTable,
    canMarkPaid,
    canRecordRecurringPayment
  } = useMarkOrderPaid(venue?.supplierId ?? 0, venue?.eventSlug, mergeOrderSummary);
  const loadTokenRef = useRef(0);
  const load = useCallback(async () => {
    if (!supplierId) return;
    const myToken = ++loadTokenRef.current;
    setLoading(true);
    try {
      const courtsResp = await api.request(supplierId, "courts", void 0, "GET");
      const activeCourts = (courtsResp ?? []).filter((c) => c.active);
      setCourts(activeCourts);
      let results;
      try {
        const all = await api.request(
          supplierId,
          "search_all",
          { date: date.format(DATE_FORMAT$2) },
          "POST"
        );
        const byCourt = new Map((all?.courts ?? []).map((c) => [c.resourceId, c]));
        results = activeCourts.map((court) => {
          const c = byCourt.get(court.resourceId);
          if (!c || c.error) return { status: "rejected", reason: new Error(c?.error || "missing") };
          return {
            status: "fulfilled",
            value: { items: (c.items ?? []).map((item) => ({ ...item, resourceId: court.resourceId })), openTime: c.openTime, closeTime: c.closeTime, slotMinutes: c.slotMinutes }
          };
        });
      } catch {
        results = await settleWithConcurrency(
          activeCourts,
          CONCURRENCY_LIMIT,
          (court) => api.request(supplierId, "search", { resource_id: court.resourceId, date: date.format(DATE_FORMAT$2) }, "POST").then((resp) => ({
            items: (resp.items ?? []).map((item) => ({ ...item, resourceId: court.resourceId })),
            openTime: resp.openTime,
            closeTime: resp.closeTime,
            slotMinutes: resp.slotMinutes
          }))
        );
      }
      const merged = [];
      let minOpen;
      let maxClose;
      let minSlot;
      results.forEach((r, idx) => {
        if (r.status === "fulfilled") {
          merged.push(...r.value.items);
          if (r.value.openTime && (!minOpen || r.value.openTime < minOpen)) minOpen = r.value.openTime;
          if (r.value.closeTime && (!maxClose || r.value.closeTime > maxClose)) maxClose = r.value.closeTime;
          if (r.value.slotMinutes && (!minSlot || r.value.slotMinutes < minSlot)) minSlot = r.value.slotMinutes;
        } else {
          message.error(
            t("court.bookings.searchFailedForCourt", {
              defaultValue: `Kh\xF4ng t\u1EA3i \u0111\u01B0\u1EE3c l\u1ECBch cho s\xE2n "${activeCourts[idx]?.name}"`
            })
          );
        }
      });
      if (loadTokenRef.current !== myToken) return;
      setBookings(merged);
      if (minOpen) setGlobalOpenTime(minOpen);
      if (maxClose) setGlobalCloseTime(maxClose);
      if (minSlot) setGridSlotMinutes(minSlot);
    } catch (err) {
      if (loadTokenRef.current === myToken && err instanceof Error) message.error(err.message);
    } finally {
      if (loadTokenRef.current === myToken) setLoading(false);
    }
  }, [api, supplierId, date, t]);
  useEffect(() => {
    void load();
  }, [load]);
  const confirmNoShow = useCallback(
    async (booking) => {
      let paid = 0;
      let pct = 100;
      if (booking.orderRef && supplierId) {
        try {
          const st = await api.request(
            supplierId,
            `deposits/status?order_code=${encodeURIComponent(booking.orderRef)}`,
            void 0,
            "GET"
          );
          paid = st?.depositPaidMinor ?? 0;
          pct = st?.policySnapshot?.noShow?.forfeitPercent ?? st?.policySnapshot?.no_show?.forfeit_percent ?? 100;
        } catch {
        }
      }
      const forfeit = Math.round(paid * pct / 100);
      const fmt = (n) => `${n.toLocaleString("vi-VN")}\u0111`;
      return new Promise((resolve) => {
        Modal.confirm({
          title: t("court.noShow.confirmTitle", { defaultValue: "Ghi nh\u1EADn kh\xE1ch kh\xF4ng \u0111\u1EBFn?" }),
          content: paid > 0 ? `${t("court.noShow.confirmBody", { defaultValue: "Theo ch\xEDnh s\xE1ch, c\u01A1 s\u1EDF gi\u1EEF" })} ${pct}% c\u1ECDc = ${fmt(forfeit)} (\u0111\xE3 c\u1ECDc ${fmt(paid)})` + (paid - forfeit > 0 ? `, ho\xE0n kh\xE1ch ${fmt(paid - forfeit)} (xem "Ho\xE0n ti\u1EC1n ch\u1EDD x\u1EED l\xFD").` : ".") + ` ${t("court.noShow.irreversible", { defaultValue: "Kh\xF4ng ho\xE0n t\xE1c \u0111\u01B0\u1EE3c." })}` : t("court.noShow.noDeposit", { defaultValue: "\u0110\u01A1n ch\u01B0a c\xF3 c\u1ECDc n\xEAn kh\xF4ng c\xF3 kho\u1EA3n n\xE0o b\u1ECB gi\u1EEF. Kh\xF4ng ho\xE0n t\xE1c \u0111\u01B0\u1EE3c." }),
          okText: t("court.noShow.ok", { defaultValue: "Ghi nh\u1EADn kh\xF4ng \u0111\u1EBFn" }),
          okButtonProps: { danger: true },
          cancelText: t("common.cancel", { defaultValue: "Hu\u1EF7" }),
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        });
      });
    },
    [api, supplierId, t]
  );
  const handlePlayAction = useCallback(
    async (booking, action) => {
      if (!supplierId) return;
      if (action === "no_show" && !await confirmNoShow(booking)) return;
      try {
        if (action === "extend") {
          const r = await api.request(supplierId, "play/extend", { reservation_id: booking.reservationId, minutes: 30 }, "POST");
          const extra = r?.thisExtraPriceMinor ?? 0;
          message.success(t("court.live.extended", { defaultValue: "\u0110\xE3 gia h\u1EA1n 30 ph\xFAt" }) + (extra > 0 ? ` \u2014 ph\u1EE5 thu ${extra.toLocaleString("vi-VN")}\u0111 (thu t\u1EA1i qu\u1EA7y)` : ""));
        } else if (action === "finish_early") {
          const r = await api.request(supplierId, "play/finish-early", { reservation_id: booking.reservationId }, "POST");
          message.success(t("court.live.finishedEarly", { defaultValue: "\u0110\xE3 k\u1EBFt th\xFAc s\u1EDBm" }) + (r?.releasedMinutes ? ` \u2014 nh\u1EA3 ${r.releasedMinutes} ph\xFAt` : ""));
        } else {
          await api.request(supplierId, "play/status", { reservation_id: booking.reservationId, to: action }, "POST");
          if (action === "no_show" && booking.orderRef) {
            const r = await api.request(supplierId, "orders/no-show", { order_code: booking.orderRef }, "POST");
            message.success(
              t("court.live.noShowDone", { defaultValue: "\u0110\xE3 ghi nh\u1EADn kh\xF4ng \u0111\u1EBFn." }) + ` C\u01A1 s\u1EDF gi\u1EEF ${(r?.forfeitMinor ?? 0).toLocaleString("vi-VN")}\u0111` + ((r?.refundMinor ?? 0) > 0 ? `, ho\xE0n ${(r?.refundMinor ?? 0).toLocaleString("vi-VN")}\u0111 (xem "Ho\xE0n ti\u1EC1n ch\u1EDD x\u1EED l\xFD")` : "")
            );
          } else {
            message.success(t("court.live.updated", { defaultValue: "\u0110\xE3 c\u1EADp nh\u1EADt tr\u1EA1ng th\xE1i l\u01B0\u1EE3t ch\u01A1i" }));
          }
        }
        await load();
      } catch (err) {
        if (err instanceof Error) message.error(err.message);
      }
    },
    [api, supplierId, t, load, confirmNoShow]
  );
  useEffect(() => {
    if (!date.isSame(dayjs(), "day")) return;
    const timer = setInterval(() => {
      if (typeof document === "undefined" || document.visibilityState === "visible") void load();
    }, 3e4);
    return () => clearInterval(timer);
  }, [load, date]);
  useEffect(() => {
    if (!venue) return;
    const refs = Object.keys(orderSummaries).filter((ref) => orderSummaries[ref]?.orderStatus === "n" && !(ref in orderDeposits));
    if (refs.length === 0) return;
    let cancelled = false;
    void settleWithConcurrency(
      refs,
      CONCURRENCY_LIMIT,
      (ref) => api.request(
        venue.supplierId,
        `deposits/status?order_code=${encodeURIComponent(ref)}`,
        void 0,
        "GET"
      )
    ).then((results) => {
      if (cancelled) return;
      setOrderDeposits((prev) => {
        const next = { ...prev };
        results.forEach((r, idx) => {
          if (r.status === "fulfilled" && r.value) {
            next[refs[idx]] = { paid: r.value.depositPaidMinor ?? 0, required: r.value.depositRequiredMinor ?? 0, total: r.value.totalMinor ?? 0 };
          } else {
            next[refs[idx]] = { paid: 0, required: 0, total: 0 };
          }
        });
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [orderSummaries, venue]);
  useEffect(() => {
    if (!getOrderSummary || !venue) return;
    const uniqueRefs = Array.from(new Set(bookings.map((b) => b.orderRef).filter(Boolean)));
    const missingRefs = uniqueRefs.filter((ref) => !(ref in orderSummaries));
    if (missingRefs.length === 0) return;
    let cancelled = false;
    void settleWithConcurrency(missingRefs, CONCURRENCY_LIMIT, (ref) => getOrderSummary(venue.eventSlug, ref)).then((results) => {
      if (cancelled) return;
      setOrderSummaries((prev) => {
        const next = { ...prev };
        results.forEach((r, idx) => {
          const ref = missingRefs[idx];
          if (r.status === "fulfilled" && !(ref in prev)) {
            next[ref] = r.value;
          }
        });
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [bookings, getOrderSummary, venue]);
  const normalizedSearch = searchText.trim().toLowerCase();
  const filteredBookings = useMemo(
    () => bookings.filter((b) => {
      if (filterResourceIds.length > 0 && !filterResourceIds.includes(b.resourceId)) return false;
      if (filterReservationStatuses.length > 0 && !filterReservationStatuses.includes(b.status)) return false;
      const summary = b.orderRef ? orderSummaries[b.orderRef] : void 0;
      if (filterOrderStatuses.length > 0 && !(summary?.orderStatus && filterOrderStatuses.includes(summary.orderStatus))) return false;
      if (normalizedSearch) {
        const haystack = [summary?.attendeeName, summary?.phone, summary?.email, b.orderRef].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(normalizedSearch)) return false;
      }
      return true;
    }),
    [bookings, orderSummaries, filterResourceIds, filterReservationStatuses, filterOrderStatuses, normalizedSearch]
  );
  const filteredCourts = filterResourceIds.length > 0 ? courts.filter((c) => filterResourceIds.includes(c.resourceId)) : courts;
  const hasActiveFilter = !!normalizedSearch || filterResourceIds.length > 0 || filterOrderStatuses.length > 0 || filterReservationStatuses.length > 0;
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(
      PageHeader,
      {
        title: t("court.bookings.title", { defaultValue: "L\u1ECBch \u0111\u1EB7t s\xE2n" }),
        description: t("court.bookings.pageDescription", {
          defaultValue: "T\u1ED5ng quan l\u1ECBch \u0111\xE3 \u0111\u1EB7t trong ng\xE0y \u2014 hu\u1EF7/s\u1EEDa v\u1EABn qua lu\u1ED3ng Order ri\xEAng."
        }),
        extra: /* @__PURE__ */ jsxs(Space, { wrap: true, children: [
          /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(ArrowLeftOutlined, {}), onClick: () => navigate("../..", { relative: "path" }), children: t("court.venues.title", { defaultValue: "C\u1EE5m s\xE2n" }) }),
          /* @__PURE__ */ jsx(DatePicker, { value: date, onChange: (v) => v && setDate(v), format: DATE_FORMAT$2, allowClear: false }),
          /* @__PURE__ */ jsx(
            Segmented,
            {
              value: viewMode,
              onChange: (v) => setViewMode(v),
              options: [
                { label: t("court.bookings.viewTimeline", { defaultValue: "Timeline" }), value: "timeline" },
                { label: t("court.bookings.viewTable", { defaultValue: "B\u1EA3ng" }), value: "table" }
              ]
            }
          ),
          /* @__PURE__ */ jsx(Button, { onClick: () => setManualCheckinOpen(true), children: t("court.checkin.manualButton", { defaultValue: "Check-in b\u1EB1ng m\xE3" }) }),
          /* @__PURE__ */ jsx(
            Tooltip,
            {
              title: onCreateBooking ? void 0 : t("court.bookings.createDisabledTooltip", {
                defaultValue: "T\xEDnh n\u0103ng \u0111\u1EB7t s\xE2n h\u1ED9 kh\xE1ch ch\u01B0a \u0111\u01B0\u1EE3c host c\u1EA5u h\xECnh."
              }),
              children: /* @__PURE__ */ jsx(Button, { type: "primary", icon: /* @__PURE__ */ jsx(PlusOutlined, {}), disabled: !onCreateBooking, onClick: () => setCreateOpen(true), children: t("court.bookings.createButton", { defaultValue: "\u0110\u1EB7t s\xE2n" }) })
            }
          )
        ] })
      }
    ),
    /* @__PURE__ */ jsxs(Space, { wrap: true, style: { marginBottom: 16 }, children: [
      /* @__PURE__ */ jsx(
        Input,
        {
          allowClear: true,
          prefix: /* @__PURE__ */ jsx(SearchOutlined, {}),
          placeholder: t("court.bookings.searchPlaceholder", { defaultValue: "T\xECm t\xEAn kh\xE1ch, m\xE3 \u0111\u01A1n, S\u0110T, email..." }),
          value: searchText,
          onChange: (e) => setSearchText(e.target.value),
          style: { width: 260, maxWidth: "100%" }
        }
      ),
      /* @__PURE__ */ jsx(
        Select,
        {
          mode: "multiple",
          allowClear: true,
          placeholder: t("court.bookings.filterByCourt", { defaultValue: "L\u1ECDc theo s\xE2n" }),
          value: filterResourceIds,
          onChange: setFilterResourceIds,
          options: courts.map((c) => ({ value: c.resourceId, label: c.name })),
          style: { minWidth: 200 }
        }
      ),
      /* @__PURE__ */ jsx(
        Select,
        {
          mode: "multiple",
          allowClear: true,
          placeholder: t("court.bookings.filterByPayment", { defaultValue: "L\u1ECDc theo thanh to\xE1n" }),
          value: filterOrderStatuses,
          onChange: setFilterOrderStatuses,
          options: Object.entries(ORDER_STATUS_META).map(([value, meta]) => ({
            value,
            label: t(meta.key, { defaultValue: meta.defaultLabel })
          })),
          style: { minWidth: 200 }
        }
      ),
      /* @__PURE__ */ jsx(
        Select,
        {
          mode: "multiple",
          allowClear: true,
          placeholder: t("court.bookings.filterByReservation", { defaultValue: "L\u1ECDc theo ch\u1ED7 \u0111\u1EB7t" }),
          value: filterReservationStatuses,
          onChange: setFilterReservationStatuses,
          options: RESERVATION_STATUS_OPTIONS.map((o) => ({ value: o.value, label: t(o.key, { defaultValue: o.defaultLabel }) })),
          style: { minWidth: 200 }
        }
      ),
      hasActiveFilter && /* @__PURE__ */ jsx(
        Button,
        {
          type: "link",
          onClick: () => {
            setSearchText("");
            setFilterResourceIds([]);
            setFilterOrderStatuses([]);
            setFilterReservationStatuses([]);
          },
          children: t("court.bookings.clearFilters", { defaultValue: "Xo\xE1 b\u1ED9 l\u1ECDc" })
        }
      )
    ] }),
    viewMode === "timeline" ? /* @__PURE__ */ jsx(
      BookingTimeline,
      {
        courts: filteredCourts,
        bookings: filteredBookings,
        orderSummaries,
        orderDeposits,
        date,
        globalOpenTime,
        globalCloseTime,
        gridSlotMinutes,
        venue,
        onOrderPaid: mergeOrderSummary,
        onPlayAction: handlePlayAction,
        onCancelOrder: (b) => setCancelOrderCode(b.orderRef || null),
        onRescheduleOrder: (b) => setRescheduleTarget(b),
        onCheckinOrder: (b) => setCheckinQrCode(b.orderRef || null)
      }
    ) : /* @__PURE__ */ jsx(
      Table,
      {
        rowKey: "reservationId",
        loading,
        dataSource: filteredBookings,
        pagination: false,
        locale: {
          emptyText: hasActiveFilter ? t("court.bookings.emptyFiltered", { defaultValue: "Kh\xF4ng c\xF3 l\u1ECBch \u0111\u1EB7t n\xE0o kh\u1EDBp b\u1ED9 l\u1ECDc." }) : t("court.bookings.empty", { defaultValue: "Ng\xE0y n\xE0y ch\u01B0a c\xF3 l\u1ECBch \u0111\u1EB7t n\xE0o." })
        },
        columns: [
          {
            title: t("court.bookings.resourceIdColumn", { defaultValue: "S\xE2n" }),
            dataIndex: "resourceId",
            key: "resourceId",
            render: (resourceId) => courts.find((c) => c.resourceId === resourceId)?.name || resourceId
          },
          {
            title: t("court.bookings.startAtColumn", { defaultValue: "B\u1EAFt \u0111\u1EA7u" }),
            dataIndex: "startAt",
            key: "startAt",
            render: (v, row) => formatVenueDateTime(v, courts.find((c) => c.resourceId === row.resourceId)?.timezone || DEFAULT_VENUE_TZ)
          },
          {
            title: t("court.bookings.endAtColumn", { defaultValue: "K\u1EBFt th\xFAc" }),
            dataIndex: "endAt",
            key: "endAt",
            render: (v, row) => formatVenueDateTime(v, courts.find((c) => c.resourceId === row.resourceId)?.timezone || DEFAULT_VENUE_TZ)
          },
          {
            title: t("court.bookings.customerColumn", { defaultValue: "Kh\xE1ch" }),
            dataIndex: "orderRef",
            key: "customer",
            render: (orderRef) => {
              const summary = orderRef ? orderSummaries[orderRef] : void 0;
              if (!summary || !summary.attendeeName && !summary.phone) return "\u2014";
              return /* @__PURE__ */ jsxs(Space, { direction: "vertical", size: 0, children: [
                summary.attendeeName && /* @__PURE__ */ jsx(Typography.Text, { children: summary.attendeeName }),
                summary.phone && /* @__PURE__ */ jsx(Typography.Text, { type: "secondary", children: summary.phone })
              ] });
            }
          },
          {
            title: t("court.bookings.statusColumn", { defaultValue: "Tr\u1EA1ng th\xE1i" }),
            dataIndex: "status",
            key: "status",
            render: (status) => {
              const opt = RESERVATION_STATUS_OPTIONS.find((o) => o.value === status);
              return /* @__PURE__ */ jsx(Tag, { color: status === "confirmed" ? "green" : "gold", children: opt ? t(opt.key, { defaultValue: opt.defaultLabel }) : status });
            }
          },
          {
            title: t("court.bookings.orderRefColumn", { defaultValue: "\u0110\u01A1n h\xE0ng" }),
            dataIndex: "orderRef",
            key: "orderRef",
            render: (orderRef) => {
              if (!orderRef) return t("court.bookings.orderRefEmpty", { defaultValue: "\u2014" });
              if (!venue) return orderRef;
              return /* @__PURE__ */ jsx("a", { href: getOrderUrl(venue.eventSlug, orderRef), target: "_blank", rel: "noreferrer", children: orderRef });
            }
          },
          {
            // [Thêm — xem plan "Timeline: hiện trạng thái thu tiền"] cột MỚI, tách riêng khỏi "Đơn hàng"
            // — xem trạng thái + xác nhận thu tiền TẠI CHỖ, không bắt buộc mở Order Details.
            title: t("court.bookings.paymentColumn", { defaultValue: "Thanh to\xE1n" }),
            dataIndex: "orderRef",
            key: "payment",
            render: (orderRef) => {
              if (!orderRef) return null;
              const summary = orderSummaries[orderRef];
              const isRecurringPending = !!summary?.pendingPeriod;
              const canConfirmPaid = summary?.orderStatus !== "p" && (isRecurringPending ? canRecordRecurringPayment : canMarkPaid);
              return /* @__PURE__ */ jsxs(Space, { size: 8, direction: "vertical", children: [
                /* @__PURE__ */ jsxs(Space, { size: 8, children: [
                  summary?.orderStatus === "n" && (orderDeposits[orderRef]?.paid ?? 0) > 0 ? /* @__PURE__ */ jsxs(Tag, { color: "blue", children: [
                    t("court.bookings.depositPaidTag", { defaultValue: "\u0110\xE3 c\u1ECDc" }),
                    " ",
                    orderDeposits[orderRef].paid.toLocaleString("vi-VN"),
                    "/",
                    orderDeposits[orderRef].total.toLocaleString("vi-VN"),
                    "\u0111"
                  ] }) : /* @__PURE__ */ jsx(OrderStatusTag, { orderStatus: summary?.orderStatus }),
                  canConfirmPaid && /* @__PURE__ */ jsx(
                    Popconfirm,
                    {
                      title: t("court.bookings.markPaidConfirm", { defaultValue: "X\xE1c nh\u1EADn \u0111\xE3 thu ti\u1EC1n cho \u0111\u01A1n n\xE0y?" }),
                      onConfirm: () => void markPaidFromTable(orderRef, isRecurringPending),
                      okText: t("common.confirm", { defaultValue: "X\xE1c nh\u1EADn" }),
                      cancelText: t("common.cancel", { defaultValue: "Hu\u1EF7" }),
                      children: /* @__PURE__ */ jsx(Button, { size: "small", loading: pendingRefFromTable === orderRef, children: summary?.orderStatus === "n" && (orderDeposits[orderRef]?.paid ?? 0) > 0 ? t("court.bookings.collectRemaining", { defaultValue: "Thu ph\u1EA7n c\xF2n l\u1EA1i" }) : t("court.bookings.create.markPaid", { defaultValue: "X\xE1c nh\u1EADn \u0111\xE3 thu ti\u1EC1n" }) })
                    }
                  )
                ] }),
                isRecurringPending && summary?.pendingPeriod && /* @__PURE__ */ jsx(Typography.Text, { type: "secondary", style: { fontSize: 12 }, children: t("court.bookings.recurringPendingPeriodShort", {
                  defaultValue: `K\u1EF3 ${summary.pendingPeriod.periodIndex + 1}: ${summary.pendingPeriod.amountMinor.toLocaleString("vi-VN")}\u0111`,
                  periodIndex: summary.pendingPeriod.periodIndex + 1,
                  amount: summary.pendingPeriod.amountMinor.toLocaleString("vi-VN")
                }) })
              ] });
            }
          }
        ]
      }
    ),
    supplierId && /* @__PURE__ */ jsx(
      RescheduleOrderModal,
      {
        supplierId,
        orderCode: rescheduleTarget?.orderRef || null,
        courts,
        currentResourceId: rescheduleTarget?.resourceId,
        currentStartAt: rescheduleTarget?.startAt,
        currentEndAt: rescheduleTarget?.endAt,
        onClose: () => setRescheduleTarget(null),
        onDone: () => {
          setRescheduleTarget(null);
          void load();
        }
      }
    ),
    supplierId && /* @__PURE__ */ jsx(
      CancelOrderModal,
      {
        supplierId,
        orderCode: cancelOrderCode,
        onClose: () => setCancelOrderCode(null),
        onDone: () => {
          setCancelOrderCode(null);
          void load();
        }
      }
    ),
    supplierId && /* @__PURE__ */ jsx(CheckinQrModal, { supplierId, orderCode: checkinQrCode, onClose: () => setCheckinQrCode(null) }),
    supplierId && /* @__PURE__ */ jsx(
      ManualCheckinModal,
      {
        supplierId,
        open: manualCheckinOpen,
        onClose: () => setManualCheckinOpen(false),
        onDone: () => void load()
      }
    ),
    supplierId && /* @__PURE__ */ jsx(
      CreateBookingModal,
      {
        open: createOpen,
        onClose: () => setCreateOpen(false),
        onCreated: () => {
          setCreateOpen(false);
          void load();
        },
        supplierId,
        courts
      }
    )
  ] });
};

const BookingPolicyCard = ({ supplierId }) => {
  const { t, api } = useCourtContext();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.request(supplierId, "policy", void 0, "GET");
      const p = resp?.policy ?? {};
      form.setFieldsValue({
        mode: p.deposit?.mode ?? "none",
        value: p.deposit?.value,
        holdMinutes: p.deposit?.holdMinutes,
        bankName: p.deposit?.bank?.bankName,
        bankBin: p.deposit?.bank?.bankBin,
        accountNo: p.deposit?.bank?.accountNo,
        accountName: p.deposit?.bank?.accountName,
        depositTerms: p.deposit?.termsText,
        tiers: (p.cancellation?.tiers ?? []).map((x) => ({ hoursBefore: x.hoursBefore, refundPercent: x.refundPercent })),
        venueCancelRefund: p.cancellation?.venueCancelRefundPercent ?? 100,
        cancelTerms: p.cancellation?.termsText,
        forfeitPercent: p.noShow?.forfeitPercent,
        blockAfter: p.noShow?.blockOnlineAfter,
        windowDays: p.noShow?.windowDays,
        notifyConfirm: p.notifications?.confirm ?? true,
        reminderHours: p.notifications?.reminderHours ?? 3,
        notifyBalance: p.notifications?.balanceReminder ?? true,
        evHourRate: p.eventRental?.hourRateMinor,
        evDayRate: p.eventRental?.dayRateMinor,
        evHoldDays: p.eventRental?.holdDays,
        evDepositPct: p.eventRental?.depositPercent,
        evBalanceDays: p.eventRental?.balanceDaysBefore,
        evServices: (p.eventRental?.services ?? []).map((x) => ({ name: x.name, unitMinor: x.unitMinor })),
        evTiers: (p.eventRental?.cancellation ?? []).map((x) => ({ daysBefore: x.daysBefore, refundPercent: x.refundPercent })),
        evTerms: p.eventRental?.termsText
      });
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [api, supplierId, form]);
  useEffect(() => {
    void load();
  }, [load]);
  const save = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const policy = {};
      if (v.mode && v.mode !== "none") {
        policy.deposit = {
          mode: v.mode,
          value: v.value,
          hold_minutes: v.holdMinutes || 0,
          methods: ["cash", "bank_transfer"],
          bank: { bank_name: v.bankName || "", bank_bin: v.bankBin || "", account_no: v.accountNo || "", account_name: v.accountName || "", transfer_note: "{order_code}" },
          terms_text: v.depositTerms || ""
        };
      }
      if ((v.tiers ?? []).length > 0) {
        policy.cancellation = {
          tiers: v.tiers.map((x) => ({ hours_before: x.hoursBefore, refund_percent: x.refundPercent })),
          venue_cancel_refund_percent: v.venueCancelRefund ?? 100,
          terms_text: v.cancelTerms || ""
        };
      }
      if (v.forfeitPercent != null) {
        policy.no_show = { forfeit_percent: v.forfeitPercent, block_online_after: v.blockAfter || 0, window_days: v.windowDays || 0 };
      }
      if (v.evHourRate || v.evDayRate || (v.evServices ?? []).length || (v.evTiers ?? []).length || v.evTerms) {
        policy.event_rental = {
          hour_rate_minor: v.evHourRate || 0,
          day_rate_minor: v.evDayRate || 0,
          hold_days: v.evHoldDays || 0,
          deposit_percent: v.evDepositPct || 0,
          balance_days_before: v.evBalanceDays || 0,
          services: (v.evServices ?? []).map((x) => ({ name: x.name, unit_minor: x.unitMinor || 0 })),
          cancellation: (v.evTiers ?? []).map((x) => ({ days_before: x.daysBefore, refund_percent: x.refundPercent })),
          terms_text: v.evTerms || ""
        };
      }
      policy.notifications = { confirm: v.notifyConfirm !== false, reminder_hours: v.reminderHours || 0, balance_reminder: v.notifyBalance !== false };
      await api.request(supplierId, "policy", { policy }, "PUT");
      message.success(t("court.policy.saved", { defaultValue: "\u0110\xE3 l\u01B0u ch\xEDnh s\xE1ch \u0111\u1EB7t s\xE2n" }));
      await load();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  return /* @__PURE__ */ jsx(Card, { title: t("court.policy.title", { defaultValue: "Ch\xEDnh s\xE1ch \u0111\u1EB7t s\xE2n & \u0111\u1EB7t c\u1ECDc" }), style: { marginTop: 16 }, children: /* @__PURE__ */ jsx(Spin, { spinning: loading, children: /* @__PURE__ */ jsxs(Form, { form, layout: "vertical", initialValues: { mode: "none", venueCancelRefund: 100, notifyConfirm: true, reminderHours: 3, notifyBalance: true }, children: [
    /* @__PURE__ */ jsx(Typography.Title, { level: 5, children: t("court.policy.depositSection", { defaultValue: "\u0110\u1EB7t c\u1ECDc" }) }),
    /* @__PURE__ */ jsxs(Space, { wrap: true, align: "start", children: [
      /* @__PURE__ */ jsx(Form.Item, { name: "mode", label: t("court.policy.depositMode", { defaultValue: "H\xECnh th\u1EE9c c\u1ECDc" }), children: /* @__PURE__ */ jsx(
        Select,
        {
          style: { width: 200 },
          options: [
            { value: "none", label: t("court.policy.modeNone", { defaultValue: "Kh\xF4ng \u0111\u1EB7t c\u1ECDc" }) },
            { value: "percent", label: t("court.policy.modePercent", { defaultValue: "Theo % t\u1ED5ng \u0111\u01A1n" }) },
            { value: "fixed", label: t("court.policy.modeFixed", { defaultValue: "S\u1ED1 ti\u1EC1n c\u1ED1 \u0111\u1ECBnh" }) }
          ]
        }
      ) }),
      /* @__PURE__ */ jsx(Form.Item, { noStyle: true, shouldUpdate: (a, b) => a.mode !== b.mode, children: ({ getFieldValue }) => getFieldValue("mode") !== "none" && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Form.Item, { name: "value", label: getFieldValue("mode") === "percent" ? "%" : "\u0110\u1ED3ng", rules: [{ required: true, type: "number", min: 1, max: getFieldValue("mode") === "percent" ? 100 : void 0 }], children: /* @__PURE__ */ jsx(InputNumber, { min: 1, style: { width: 140 } }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "holdMinutes", label: t("court.policy.hold", { defaultValue: "Gi\u1EEF ch\u1ED7 ch\u1EDD c\u1ECDc (ph\xFAt, t\u1ED1i \u0111a 720)" }), rules: [{ required: true, type: "number", min: 5, max: 720 }], children: /* @__PURE__ */ jsx(InputNumber, { min: 5, max: 720, style: { width: 180 } }) })
      ] }) })
    ] }),
    /* @__PURE__ */ jsxs(Space, { wrap: true, children: [
      /* @__PURE__ */ jsx(Form.Item, { name: "bankName", label: t("court.policy.bankName", { defaultValue: "Ng\xE2n h\xE0ng" }), children: /* @__PURE__ */ jsx(Input, { style: { width: 200 } }) }),
      /* @__PURE__ */ jsx(Form.Item, { name: "bankBin", label: t("court.policy.bankBin", { defaultValue: "M\xE3 ng\xE2n h\xE0ng (BIN, \u0111\u1EC3 t\u1EA1o QR)" }), children: /* @__PURE__ */ jsx(Input, { style: { width: 160 }, placeholder: "970436" }) }),
      /* @__PURE__ */ jsx(Form.Item, { name: "accountNo", label: t("court.policy.accountNo", { defaultValue: "S\u1ED1 t\xE0i kho\u1EA3n" }), children: /* @__PURE__ */ jsx(Input, { style: { width: 200 } }) }),
      /* @__PURE__ */ jsx(Form.Item, { name: "accountName", label: t("court.policy.accountName", { defaultValue: "Ch\u1EE7 t\xE0i kho\u1EA3n" }), children: /* @__PURE__ */ jsx(Input, { style: { width: 220 } }) })
    ] }),
    /* @__PURE__ */ jsx(Form.Item, { name: "depositTerms", label: t("court.policy.depositTerms", { defaultValue: "\u0110i\u1EC1u kho\u1EA3n \u0111\u1EB7t c\u1ECDc hi\u1EC3n th\u1ECB cho kh\xE1ch" }), children: /* @__PURE__ */ jsx(Input.TextArea, { rows: 3 }) }),
    /* @__PURE__ */ jsx(Divider, {}),
    /* @__PURE__ */ jsx(Typography.Title, { level: 5, children: t("court.policy.cancelSection", { defaultValue: "Hu\u1EF7 v\xE0 ho\xE0n c\u1ECDc" }) }),
    /* @__PURE__ */ jsx(Typography.Paragraph, { type: "secondary", children: t("court.policy.cancelHint", { defaultValue: "M\u1ED7i d\xF2ng: hu\u1EF7 khi c\xF2n \xEDt nh\u1EA5t N gi\u1EDD tr\u01B0\u1EDBc gi\u1EDD ch\u01A1i th\xEC ho\xE0n X% ti\u1EC1n \u0111\xE3 c\u1ECDc. \xC1p d\u1EE5ng d\xF2ng c\xF3 s\u1ED1 gi\u1EDD l\u1EDBn nh\u1EA5t m\xE0 kh\xE1ch c\xF2n \u0111\u1EE7." }) }),
    /* @__PURE__ */ jsx(Form.List, { name: "tiers", children: (fields, { add, remove }) => /* @__PURE__ */ jsxs(Fragment, { children: [
      fields.map((f) => /* @__PURE__ */ jsxs(Space, { align: "baseline", style: { display: "flex" }, children: [
        /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "hoursBefore"], rules: [{ required: true, type: "number", min: 0 }], children: /* @__PURE__ */ jsx(InputNumber, { addonBefore: "C\xF2n \u2265", addonAfter: "gi\u1EDD", min: 0 }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "refundPercent"], rules: [{ required: true, type: "number", min: 0, max: 100 }], children: /* @__PURE__ */ jsx(InputNumber, { addonBefore: "Ho\xE0n", addonAfter: "%", min: 0, max: 100 }) }),
        /* @__PURE__ */ jsx(MinusCircleOutlined, { onClick: () => remove(f.name) })
      ] }, f.key)),
      /* @__PURE__ */ jsx(Button, { type: "dashed", icon: /* @__PURE__ */ jsx(PlusOutlined, {}), onClick: () => add({ hoursBefore: 24, refundPercent: 100 }), children: t("court.policy.addTier", { defaultValue: "Th\xEAm m\u1ED1c" }) })
    ] }) }),
    /* @__PURE__ */ jsx(Form.Item, { name: "venueCancelRefund", label: t("court.policy.venueCancel", { defaultValue: "C\u01A1 s\u1EDF hu\u1EF7 (b\u1EA3o tr\xEC, th\u1EDDi ti\u1EBFt) ho\xE0n %" }), style: { marginTop: 12 }, children: /* @__PURE__ */ jsx(InputNumber, { min: 0, max: 100, addonAfter: "%" }) }),
    /* @__PURE__ */ jsx(Form.Item, { name: "cancelTerms", label: t("court.policy.cancelTerms", { defaultValue: "\u0110i\u1EC1u kho\u1EA3n hu\u1EF7 hi\u1EC3n th\u1ECB cho kh\xE1ch" }), children: /* @__PURE__ */ jsx(Input.TextArea, { rows: 3 }) }),
    /* @__PURE__ */ jsx(Divider, {}),
    /* @__PURE__ */ jsx(Typography.Title, { level: 5, children: t("court.policy.noShowSection", { defaultValue: "Kh\xF4ng \u0111\u1EBFn (no-show)" }) }),
    /* @__PURE__ */ jsxs(Space, { wrap: true, children: [
      /* @__PURE__ */ jsx(Form.Item, { name: "forfeitPercent", label: t("court.policy.forfeit", { defaultValue: "M\u1EA5t % ti\u1EC1n c\u1ECDc" }), children: /* @__PURE__ */ jsx(InputNumber, { min: 0, max: 100, addonAfter: "%" }) }),
      /* @__PURE__ */ jsx(Form.Item, { name: "blockAfter", label: t("court.policy.blockAfter", { defaultValue: "Ch\u1EB7n \u0111\u1EB7t online sau (l\u1EA7n)" }), children: /* @__PURE__ */ jsx(InputNumber, { min: 0 }) }),
      /* @__PURE__ */ jsx(Form.Item, { name: "windowDays", label: t("court.policy.windowDays", { defaultValue: "Trong (ng\xE0y)" }), children: /* @__PURE__ */ jsx(InputNumber, { min: 0 }) })
    ] }),
    /* @__PURE__ */ jsx(Divider, {}),
    /* @__PURE__ */ jsx(Typography.Title, { level: 5, children: t("court.policy.notifySection", { defaultValue: "Tin nh\u1EAFn cho kh\xE1ch" }) }),
    /* @__PURE__ */ jsx(Typography.Paragraph, { type: "secondary", children: t("court.policy.notifyHelp", { defaultValue: "G\u1EEDi qua Zalo (ZBS) t\u1EDBi s\u1ED1 \u0111i\u1EC7n tho\u1EA1i c\u1EE7a \u0111\u01A1n; n\u1EBFu Zalo kh\xF4ng g\u1EEDi \u0111\u01B0\u1EE3c v\xE0 \u0111\u01A1n c\xF3 email th\u1EADt th\xEC g\u1EEDi email." }) }),
    /* @__PURE__ */ jsxs(Space, { wrap: true, align: "start", children: [
      /* @__PURE__ */ jsx(Form.Item, { name: "notifyConfirm", label: t("court.policy.notifyConfirm", { defaultValue: "B\xE1o khi gi\u1EEF ch\u1ED7 th\xE0nh c\xF4ng" }), valuePropName: "checked", children: /* @__PURE__ */ jsx(Switch, {}) }),
      /* @__PURE__ */ jsx(Form.Item, { name: "reminderHours", label: t("court.policy.reminderHours", { defaultValue: "Nh\u1EAFc tr\u01B0\u1EDBc gi\u1EDD ch\u01A1i (gi\u1EDD, 2\u201324)" }), children: /* @__PURE__ */ jsx(InputNumber, { min: 2, max: 24 }) }),
      /* @__PURE__ */ jsx(Form.Item, { name: "notifyBalance", label: t("court.policy.notifyBalance", { defaultValue: "Nh\u1EAFc kho\u1EA3n c\xF2n n\u1EE3 tr\u01B0\u1EDBc 24 gi\u1EDD" }), valuePropName: "checked", children: /* @__PURE__ */ jsx(Switch, {}) })
    ] }),
    /* @__PURE__ */ jsx(Divider, {}),
    /* @__PURE__ */ jsx(Typography.Title, { level: 5, children: t("court.policy.eventSection", { defaultValue: "Thu\xEA s\xE2n s\u1EF1 ki\u1EC7n (b\u1EA3ng gi\xE1 & ch\xEDnh s\xE1ch)" }) }),
    /* @__PURE__ */ jsxs(Space, { wrap: true, align: "start", children: [
      /* @__PURE__ */ jsx(Form.Item, { name: "evHourRate", label: "Gi\xE1 s\xE2n-gi\u1EDD (\u0111)", children: /* @__PURE__ */ jsx(InputNumber, { min: 0, style: { width: 150 } }) }),
      /* @__PURE__ */ jsx(Form.Item, { name: "evDayRate", label: "Gi\xE1 tr\u1ECDn g\xF3i / s\xE2n / ng\xE0y (\u0111)", children: /* @__PURE__ */ jsx(InputNumber, { min: 0, style: { width: 170 } }) }),
      /* @__PURE__ */ jsx(Form.Item, { name: "evDepositPct", label: "C\u1ECDc khi ch\u1ED1t (%)", children: /* @__PURE__ */ jsx(InputNumber, { min: 0, max: 100, placeholder: "30" }) }),
      /* @__PURE__ */ jsx(Form.Item, { name: "evBalanceDays", label: "Thu ph\u1EA7n c\xF2n l\u1EA1i tr\u01B0\u1EDBc (ng\xE0y)", children: /* @__PURE__ */ jsx(InputNumber, { min: 0, placeholder: "7" }) }),
      /* @__PURE__ */ jsx(Form.Item, { name: "evHoldDays", label: "Gi\u1EEF ch\u1ED7 t\u1EA1m (ng\xE0y, t\u1ED1i \u0111a 14)", children: /* @__PURE__ */ jsx(InputNumber, { min: 0, max: 14, placeholder: "3" }) })
    ] }),
    /* @__PURE__ */ jsx(Form.List, { name: "evServices", children: (fields, { add, remove }) => /* @__PURE__ */ jsxs(Fragment, { children: [
      fields.map((f) => /* @__PURE__ */ jsxs(Space, { align: "baseline", wrap: true, children: [
        /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "name"], rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, { placeholder: "D\u1ECBch v\u1EE5 k\xE8m (\u0111i\u1EC7n/\u0111\xE8n, \xE2m thanh...)", style: { width: 280 } }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "unitMinor"], children: /* @__PURE__ */ jsx(InputNumber, { min: 0, placeholder: "\u0110\u01A1n gi\xE1 (\u0111)", style: { width: 150 } }) }),
        /* @__PURE__ */ jsx(MinusCircleOutlined, { onClick: () => remove(f.name) })
      ] }, f.key)),
      /* @__PURE__ */ jsx(Button, { type: "dashed", icon: /* @__PURE__ */ jsx(PlusOutlined, {}), onClick: () => add({}), children: "Th\xEAm d\u1ECBch v\u1EE5 k\xE8m" })
    ] }) }),
    /* @__PURE__ */ jsx(Typography.Paragraph, { type: "secondary", style: { marginTop: 12 }, children: "M\u1ED1c hu\u1EF7 s\u1EF1 ki\u1EC7n theo NG\xC0Y tr\u01B0\u1EDBc gi\u1EDD b\u1EAFt \u0111\u1EA7u. B\u1ECF tr\u1ED1ng = m\u1EB7c \u0111\u1ECBnh: t\u1EEB 14 ng\xE0y ho\xE0n 100%, t\u1EEB 7 ng\xE0y 50%, c\xF2n l\u1EA1i 0%." }),
    /* @__PURE__ */ jsx(Form.List, { name: "evTiers", children: (fields, { add, remove }) => /* @__PURE__ */ jsxs(Fragment, { children: [
      fields.map((f) => /* @__PURE__ */ jsxs(Space, { align: "baseline", wrap: true, children: [
        /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "daysBefore"], rules: [{ required: true }], children: /* @__PURE__ */ jsx(InputNumber, { min: 0, addonBefore: "T\u1EEB", addonAfter: "ng\xE0y tr\u01B0\u1EDBc" }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "refundPercent"], rules: [{ required: true }], children: /* @__PURE__ */ jsx(InputNumber, { min: 0, max: 100, addonBefore: "Ho\xE0n", addonAfter: "%" }) }),
        /* @__PURE__ */ jsx(MinusCircleOutlined, { onClick: () => remove(f.name) })
      ] }, f.key)),
      /* @__PURE__ */ jsx(Button, { type: "dashed", icon: /* @__PURE__ */ jsx(PlusOutlined, {}), onClick: () => add({}), children: "Th\xEAm m\u1ED1c hu\u1EF7 s\u1EF1 ki\u1EC7n" })
    ] }) }),
    /* @__PURE__ */ jsx(Form.Item, { name: "evTerms", label: "\u0110i\u1EC1u kho\u1EA3n in tr\xEAn b\xE1o gi\xE1 / h\u1EE3p \u0111\u1ED3ng", style: { marginTop: 12 }, children: /* @__PURE__ */ jsx(Input.TextArea, { rows: 4 }) }),
    /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(Button, { type: "primary", loading: saving, onClick: () => void save(), children: t("common.save", { defaultValue: "L\u01B0u ch\xEDnh s\xE1ch" }) }) })
  ] }) }) });
};

const VenueSettingsPage = () => {
  const { t, api, listFnbOutlets } = useCourtContext();
  const navigate = useNavigate();
  const { supplierId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savedAdapterClass, setSavedAdapterClass] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [outletsLoading, setOutletsLoading] = useState(() => !!listFnbOutlets);
  const [pendingOutletId, setPendingOutletId] = useState(void 0);
  const [confirmSwitchToNative, setConfirmSwitchToNative] = useState(false);
  const load = async () => {
    if (!supplierId) return;
    setLoading(true);
    try {
      const resp = await api.request(supplierId, "venue/staff-settings", void 0, "GET");
      setSettings(resp);
      setSavedAdapterClass(resp.staffAdapterClass);
      setPendingOutletId(resp.staffAdapterOutletId);
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, [supplierId]);
  useEffect(() => {
    if (!listFnbOutlets) return;
    setOutletsLoading(true);
    listFnbOutlets().then(setOutlets).catch(() => setOutlets([])).finally(() => setOutletsLoading(false));
  }, [listFnbOutlets]);
  const outletsResolved = !listFnbOutlets || !outletsLoading;
  const canOfferFnbAdapter = !!listFnbOutlets && outlets.length > 0;
  const save = async (adapterClass, outletId) => {
    if (!supplierId) return;
    setSaving(true);
    try {
      const resp = await api.request(
        supplierId,
        "venue/staff-settings",
        { staff_adapter_class: adapterClass, staff_adapter_outlet_id: adapterClass === "fnb" ? outletId : null },
        "PUT"
      );
      setSettings(resp);
      setSavedAdapterClass(resp.staffAdapterClass);
      setPendingOutletId(resp.staffAdapterOutletId);
      message.success(t("court.staff.settings.saved", { defaultValue: "\u0110\xE3 l\u01B0u c\u1EA5u h\xECnh." }));
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  const handleToggle = (checked) => {
    if (checked) {
      setSettings((prev) => prev ? { ...prev, staffAdapterClass: "fnb" } : prev);
    } else if (savedAdapterClass === "fnb") {
      setConfirmSwitchToNative(true);
    } else {
      setSettings((prev) => prev ? { ...prev, staffAdapterClass: "court_native" } : prev);
    }
  };
  if (loading || !settings || !outletsResolved) {
    return /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx(PageHeader, { title: t("court.staff.settings.title", { defaultValue: "C\xE0i \u0111\u1EB7t c\u1EE5m s\xE2n" }) }),
      /* @__PURE__ */ jsx(Spin, {})
    ] });
  }
  const isFnb = settings.staffAdapterClass === "fnb";
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(
      PageHeader,
      {
        title: t("court.staff.settings.title", { defaultValue: "C\xE0i \u0111\u1EB7t c\u1EE5m s\xE2n" }),
        description: t("court.staff.settings.pageDescription", {
          defaultValue: "C\u1EA5u h\xECnh c\xE1ch nh\xE2n vi\xEAn \u0111\u0103ng nh\u1EADp \u0111\u1EC3 t\u1EF1 \u0111\u1EB7t/x\xE1c nh\u1EADn thu ti\u1EC1n h\u1ED9 kh\xE1ch."
        }),
        extra: (
          // relative:'path' — route đăng ký "phẳng" (venues/:supplierId/settings, xem CourtPricingPage.tsx
          // đã sửa cùng bug này) — mặc định 'route' đếm cấp route chứ không phải đoạn URL, cần lên 2 ĐOẠN
          // (settings -> :supplierId -> venues) để về đúng danh sách cụm sân.
          /* @__PURE__ */ jsxs(Space, { children: [
            /* @__PURE__ */ jsx(Button, { onClick: () => navigate("../staff", { relative: "path" }), children: t("court.staff.title", { defaultValue: "Nh\xE2n vi\xEAn" }) }),
            /* @__PURE__ */ jsx(Button, { onClick: () => navigate("../..", { relative: "path" }), children: t("common.back", { defaultValue: "Quay l\u1EA1i" }) })
          ] })
        )
      }
    ),
    /* @__PURE__ */ jsx("div", { style: { maxWidth: 560 }, children: canOfferFnbAdapter ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }, children: [
        /* @__PURE__ */ jsx(Switch, { checked: isFnb, onChange: handleToggle, loading: saving }),
        /* @__PURE__ */ jsx("span", { children: t("court.staff.settings.useFnbToggle", { defaultValue: "D\xF9ng chung nh\xE2n vi\xEAn v\u1EDBi F&B" }) })
      ] }),
      /* @__PURE__ */ jsx(
        Alert,
        {
          type: "info",
          showIcon: true,
          style: { marginBottom: 16 },
          message: t("court.staff.settings.fnbOutletWarning", {
            defaultValue: 'Nh\xE2n vi\xEAn ph\u1EA3i \u0111\u01B0\u1EE3c g\xE1n \u0111\xFAng outlet n\xE0y trong trang Nh\xE2n vi\xEAn c\u1EE7a F&B m\u1EDBi \u0111\u0103ng nh\u1EADp PIN \u0111\u01B0\u1EE3c \u1EDF \u0111\xE2y \u2014 n\u1EBFu ch\u01B0a g\xE1n, h\u1EC7 th\u1ED1ng s\u1EBD b\xE1o gi\u1ED1ng h\u1EC7t "sai PIN".'
          })
        }
      ),
      /* @__PURE__ */ jsx(
        Alert,
        {
          type: "warning",
          showIcon: true,
          style: { marginBottom: 16 },
          message: t("court.staff.settings.softGateWarning", {
            defaultValue: "PIN ch\u1EC9 \xE1p d\u1EE5ng khi thao t\xE1c qua m\xE0n h\xECnh \u0111\u1EB7t s\xE2n \u2014 t\xE0i kho\u1EA3n backoffice c\xF3 quy\u1EC1n v\u1EABn thao t\xE1c tr\u1EF1c ti\u1EBFp \u0111\u01B0\u1EE3c qua c\xE1c \u0111\u01B0\u1EDDng kh\xE1c, \u0111\xE2y kh\xF4ng ph\u1EA3i c\u01A1 ch\u1EBF kho\xE1 tuy\u1EC7t \u0111\u1ED1i."
          })
        }
      ),
      isFnb && /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: 8, marginBottom: 16 }, children: [
        /* @__PURE__ */ jsx(
          Select,
          {
            style: { flex: 1 },
            placeholder: t("court.staff.settings.selectOutlet", { defaultValue: "Ch\u1ECDn outlet F&B" }),
            loading: outletsLoading,
            value: pendingOutletId,
            onChange: setPendingOutletId,
            options: outlets.map((o) => ({ value: o.id, label: o.name }))
          }
        ),
        /* @__PURE__ */ jsx(
          Button,
          {
            type: "primary",
            loading: saving,
            disabled: !pendingOutletId,
            onClick: () => void save("fnb", pendingOutletId),
            children: t("common.save", { defaultValue: "L\u01B0u" })
          }
        )
      ] })
    ] }) : /* @__PURE__ */ jsx(
      Alert,
      {
        type: "info",
        showIcon: true,
        message: t("court.staff.settings.nativeOnly", {
          defaultValue: "T\u1ED5 ch\u1EE9c ch\u01B0a tri\u1EC3n khai F&B \u2014 nh\xE2n vi\xEAn s\xE2n d\xF9ng h\u1ED3 s\u01A1 ri\xEAng, qu\u1EA3n l\xFD \u1EDF trang Nh\xE2n vi\xEAn."
        })
      }
    ) }),
    supplierId && /* @__PURE__ */ jsx(BookingPolicyCard, { supplierId }),
    /* @__PURE__ */ jsx(
      Modal,
      {
        open: confirmSwitchToNative,
        onCancel: () => setConfirmSwitchToNative(false),
        onOk: () => {
          setConfirmSwitchToNative(false);
          void save("court_native");
        },
        okText: t("court.staff.settings.confirmSwitchOk", { defaultValue: "V\u1EABn \u0111\u1ED5i" }),
        okButtonProps: { danger: true },
        title: t("court.staff.settings.confirmSwitchTitle", { defaultValue: "\u0110\u1ED5i v\u1EC1 nh\xE2n vi\xEAn ri\xEAng c\u1EE7a Court?" }),
        children: /* @__PURE__ */ jsx("p", { children: t("court.staff.settings.confirmSwitchBody", {
          defaultValue: "M\u1ECDi nh\xE2n vi\xEAn hi\u1EC7n t\u1EA1i (\u0111ang d\xF9ng chung v\u1EDBi F&B) s\u1EBD KH\xD4NG \u0111\u0103ng nh\u1EADp PIN \u0111\u01B0\u1EE3c \u1EDF \u0111\xE2y n\u1EEFa \u2014 b\u1EA1n s\u1EBD ph\u1EA3i t\u1EA1o l\u1EA1i to\xE0n b\u1ED9 danh s\xE1ch nh\xE2n vi\xEAn v\xE0 PIN m\u1EDBi tr\u01B0\u1EDBc khi thao t\xE1c ti\u1EBFp."
        }) })
      }
    )
  ] });
};

const ROLE_OPTIONS = ["admin", "manager", "cashier"];
const ROLE_COLOR = {
  admin: "red",
  manager: "blue",
  cashier: "default"
};
const ROLE_HINT = "admin: to\xE0n quy\u1EC1n (k\u1EC3 c\u1EA3 qu\u1EA3n l\xFD nh\xE2n vi\xEAn) \xB7 manager: \u0111\u1EB7t + hu\u1EF7 \u0111\u01B0\u1EE3c s\xE2n h\u1ED9 kh\xE1ch \xB7 cashier: CH\u1EC8 \u0111\u1EB7t s\xE2n h\u1ED9 kh\xE1ch (kh\xF4ng hu\u1EF7 \u0111\u01B0\u1EE3c). X\xE1c nh\u1EADn \u0111\xE3 thu ti\u1EC1n (mark_paid) KH\xD4NG b\u1ECB kho\xE1 theo vai tr\xF2 n\xE0y \u2014 m\u1ECDi t\xE0i kho\u1EA3n backoffice \u0111\u1EC1u x\xE1c nh\u1EADn \u0111\u01B0\u1EE3c, \u0111\xE2y l\xE0 h\xE0nh vi chung to\xE0n h\u1EC7 th\u1ED1ng.";
const CourtStaffPage = () => {
  const { t, api } = useCourtContext();
  const navigate = useNavigate();
  const { supplierId } = useParams();
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [pinTarget, setPinTarget] = useState(null);
  const [pinForm] = Form.useForm();
  const [editTarget, setEditTarget] = useState(null);
  const [editForm] = Form.useForm();
  const loadSettings = useCallback(async () => {
    if (!supplierId) return;
    setSettingsLoading(true);
    try {
      const resp = await api.request(supplierId, "venue/staff-settings", void 0, "GET");
      setSettings(resp);
    } catch (error) {
      if (error instanceof Error) message.error(error.message);
    } finally {
      setSettingsLoading(false);
    }
  }, [api, supplierId]);
  const loadStaff = useCallback(async () => {
    if (!supplierId) return;
    setLoading(true);
    try {
      const rows = await api.request(supplierId, "staff/list", void 0, "GET");
      setStaff(rows ?? []);
    } catch (error) {
      if (error instanceof Error) message.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [api, supplierId]);
  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);
  useEffect(() => {
    if (settings?.staffAdapterClass === "court_native") {
      void loadStaff();
    }
  }, [settings, loadStaff]);
  const handleCreate = async () => {
    if (!supplierId) return;
    try {
      const values = await createForm.validateFields();
      await api.request(supplierId, "staff/create", { name: values.name, roles: values.roles }, "POST");
      message.success(t("court.staff.created", { defaultValue: "\u0110\xE3 t\u1EA1o nh\xE2n vi\xEAn" }));
      createForm.resetFields();
      setCreateOpen(false);
      await loadStaff();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    }
  };
  const openEdit = (s) => {
    setEditTarget(s);
    editForm.setFieldsValue({ name: s.name, roles: s.roles, active: s.active });
  };
  const handleUpdate = async () => {
    if (!editTarget || !supplierId) return;
    try {
      const values = await editForm.validateFields();
      await api.request(
        supplierId,
        `staff/${editTarget.id}`,
        { name: values.name, roles: values.roles, active: values.active },
        "PUT"
      );
      message.success(t("court.staff.updated", { defaultValue: "\u0110\xE3 c\u1EADp nh\u1EADt nh\xE2n vi\xEAn" }));
      setEditTarget(null);
      await loadStaff();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    }
  };
  const handleSetPin = async () => {
    if (!pinTarget || !supplierId) return;
    try {
      const values = await pinForm.validateFields();
      await api.request(supplierId, `staff/${pinTarget.id}/pin/set`, { pin: values.pin }, "POST");
      message.success(t("court.staff.pinSet", { defaultValue: "\u0110\xE3 \u0111\u1EB7t PIN" }));
      pinForm.resetFields();
      setPinTarget(null);
      await loadStaff();
    } catch (err) {
      if (err instanceof Error && err.message === "PIN_ALREADY_USED") {
        message.error(t("court.staff.pinConflictError", { defaultValue: "PIN n\xE0y \u0111\xE3 \u0111\u01B0\u1EE3c nh\xE2n vi\xEAn kh\xE1c d\xF9ng \u2014 ch\u1ECDn PIN kh\xE1c." }));
      } else if (err instanceof Error) {
        message.error(err.message);
      }
    }
  };
  if (settingsLoading || !settings) {
    return /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(PageHeader, { title: t("court.staff.title", { defaultValue: "Nh\xE2n vi\xEAn" }) }) });
  }
  if (settings.staffAdapterClass === "fnb") {
    return /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx(
        PageHeader,
        {
          title: t("court.staff.title", { defaultValue: "Nh\xE2n vi\xEAn" }),
          extra: /* @__PURE__ */ jsx(Button, { onClick: () => navigate("../settings", { relative: "path" }), children: t("court.staff.backToSettings", { defaultValue: "C\xE0i \u0111\u1EB7t c\u1EE5m s\xE2n" }) })
        }
      ),
      /* @__PURE__ */ jsx(
        Alert,
        {
          type: "info",
          showIcon: true,
          message: t("court.staff.usesFnbTitle", { defaultValue: "C\u1EE5m s\xE2n n\xE0y d\xF9ng chung nh\xE2n vi\xEAn v\u1EDBi F&B" }),
          description: t("court.staff.usesFnbDescription", {
            defaultValue: "Qu\u1EA3n l\xFD nh\xE2n vi\xEAn (t\u1EA1o m\u1EDBi, \u0111\u1EB7t PIN, ph\xE2n vai tr\xF2) t\u1EA1i trang Nh\xE2n vi\xEAn c\u1EE7a F&B \u2014 c\u1EE5m s\xE2n n\xE0y kh\xF4ng c\xF3 danh s\xE1ch nh\xE2n vi\xEAn ri\xEAng."
          })
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(
      PageHeader,
      {
        title: t("court.staff.title", { defaultValue: "Nh\xE2n vi\xEAn" }),
        description: t("court.staff.pageDescription", {
          defaultValue: "Nh\xE2n vi\xEAn \u0111\u0103ng nh\u1EADp b\u1EB1ng PIN ri\xEAng (t\xE1ch bi\u1EC7t t\xE0i kho\u1EA3n backoffice) tr\u01B0\u1EDBc khi \u0111\u1EB7t/x\xE1c nh\u1EADn thu ti\u1EC1n h\u1ED9 kh\xE1ch."
        }),
        extra: /* @__PURE__ */ jsx(Button, { type: "primary", icon: /* @__PURE__ */ jsx(PlusOutlined, {}), onClick: () => setCreateOpen(true), children: t("common.add", { defaultValue: "Th\xEAm" }) })
      }
    ),
    /* @__PURE__ */ jsx(
      Table,
      {
        rowKey: "id",
        loading,
        dataSource: staff,
        pagination: false,
        columns: [
          { title: t("common.name", { defaultValue: "T\xEAn" }), dataIndex: "name", key: "name" },
          {
            title: t("court.staff.rolesColumn", { defaultValue: "Vai tr\xF2" }),
            dataIndex: "roles",
            key: "roles",
            render: (roles) => /* @__PURE__ */ jsx(Space, { size: 4, children: (roles ?? []).map((r) => /* @__PURE__ */ jsx(Tag, { color: ROLE_COLOR[r] ?? "default", children: r }, r)) })
          },
          {
            title: t("court.staff.pinColumn", { defaultValue: "PIN" }),
            dataIndex: "hasPin",
            key: "hasPin",
            render: (hasPin) => hasPin ? /* @__PURE__ */ jsx(Tag, { color: "green", children: t("court.staff.pinSetTag", { defaultValue: "\u0110\xE3 \u0111\u1EB7t" }) }) : /* @__PURE__ */ jsx(Tag, { color: "orange", children: t("court.staff.pinNotSetTag", { defaultValue: "Ch\u01B0a \u0111\u1EB7t" }) })
          },
          {
            title: t("common.active", { defaultValue: "\u0110ang ho\u1EA1t \u0111\u1ED9ng" }),
            dataIndex: "active",
            key: "active",
            render: (active) => active ? /* @__PURE__ */ jsx(Tag, { color: "green", children: t("common.active", { defaultValue: "Ho\u1EA1t \u0111\u1ED9ng" }) }) : /* @__PURE__ */ jsx(Tag, { color: "red", children: t("common.inactive", { defaultValue: "Ng\u1EEBng" }) })
          },
          {
            title: "",
            key: "action",
            render: (_, record) => /* @__PURE__ */ jsxs(Space, { children: [
              /* @__PURE__ */ jsx(Button, { size: "small", icon: /* @__PURE__ */ jsx(EditOutlined, {}), onClick: () => openEdit(record), children: t("common.edit", { defaultValue: "S\u1EEDa" }) }),
              /* @__PURE__ */ jsx(Button, { size: "small", icon: /* @__PURE__ */ jsx(KeyOutlined, {}), onClick: () => setPinTarget(record), children: record.hasPin ? t("court.staff.changePinButton", { defaultValue: "\u0110\u1ED5i PIN" }) : t("court.staff.setPinButton", { defaultValue: "\u0110\u1EB7t PIN" }) })
            ] })
          }
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      Modal,
      {
        title: t("court.staff.createTitle", { defaultValue: "T\u1EA1o nh\xE2n vi\xEAn" }),
        open: createOpen,
        onOk: handleCreate,
        onCancel: () => setCreateOpen(false),
        destroyOnClose: true,
        children: /* @__PURE__ */ jsxs(Form, { form: createForm, layout: "vertical", children: [
          /* @__PURE__ */ jsx(Form.Item, { name: "name", label: t("common.name", { defaultValue: "T\xEAn" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, {}) }),
          /* @__PURE__ */ jsx(
            Form.Item,
            {
              name: "roles",
              label: t("court.staff.rolesColumn", { defaultValue: "Vai tr\xF2" }),
              rules: [{ required: true, message: t("court.staff.rolesRequired", { defaultValue: "Ch\u1ECDn \xEDt nh\u1EA5t 1 vai tr\xF2" }) }],
              help: t("court.staff.rolesHint", { defaultValue: ROLE_HINT }),
              children: /* @__PURE__ */ jsx(Select, { mode: "multiple", options: ROLE_OPTIONS.map((r) => ({ value: r, label: r })), placeholder: t("court.staff.rolesPlaceholder", { defaultValue: "Ch\u1ECDn vai tr\xF2" }) })
            }
          )
        ] })
      }
    ),
    /* @__PURE__ */ jsx(
      Modal,
      {
        title: t("court.staff.editTitle", { defaultValue: "S\u1EEDa nh\xE2n vi\xEAn {{name}}", name: editTarget?.name }),
        open: !!editTarget,
        onOk: handleUpdate,
        onCancel: () => setEditTarget(null),
        destroyOnClose: true,
        children: /* @__PURE__ */ jsxs(Form, { form: editForm, layout: "vertical", children: [
          /* @__PURE__ */ jsx(Form.Item, { name: "name", label: t("common.name", { defaultValue: "T\xEAn" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, {}) }),
          /* @__PURE__ */ jsx(
            Form.Item,
            {
              name: "roles",
              label: t("court.staff.rolesColumn", { defaultValue: "Vai tr\xF2" }),
              rules: [{ required: true, message: t("court.staff.rolesRequired", { defaultValue: "Ch\u1ECDn \xEDt nh\u1EA5t 1 vai tr\xF2" }) }],
              help: t("court.staff.rolesHint", { defaultValue: ROLE_HINT }),
              children: /* @__PURE__ */ jsx(Select, { mode: "multiple", options: ROLE_OPTIONS.map((r) => ({ value: r, label: r })), placeholder: t("court.staff.rolesPlaceholder", { defaultValue: "Ch\u1ECDn vai tr\xF2" }) })
            }
          ),
          /* @__PURE__ */ jsx(
            Form.Item,
            {
              name: "active",
              label: t("court.staff.activeFieldLabel", { defaultValue: "Ho\u1EA1t \u0111\u1ED9ng (t\u1EAFt = kho\xE1 nh\xE2n vi\xEAn)" }),
              valuePropName: "checked",
              children: /* @__PURE__ */ jsx(Switch, {})
            }
          )
        ] })
      }
    ),
    /* @__PURE__ */ jsx(
      Modal,
      {
        title: t("court.staff.setPinTitle", { defaultValue: "\u0110\u1EB7t PIN cho {{name}}", name: pinTarget?.name }),
        open: !!pinTarget,
        onOk: handleSetPin,
        onCancel: () => setPinTarget(null),
        destroyOnClose: true,
        children: /* @__PURE__ */ jsx(Form, { form: pinForm, layout: "vertical", children: /* @__PURE__ */ jsx(
          Form.Item,
          {
            name: "pin",
            label: t("court.staff.pinFieldLabel", { defaultValue: "PIN (4 s\u1ED1)" }),
            rules: [
              { required: true, message: t("court.staff.pinRequired", { defaultValue: "Nh\u1EADp PIN" }) },
              { pattern: /^\d{4}$/, message: t("court.staff.pinFormatError", { defaultValue: "PIN ph\u1EA3i g\u1ED3m \u0111\xFAng 4 ch\u1EEF s\u1ED1" }) }
            ],
            children: /* @__PURE__ */ jsx(Input.Password, { maxLength: 4, placeholder: t("court.staff.pinPlaceholder", { defaultValue: "vd: 1234" }) })
          }
        ) })
      }
    )
  ] });
};

const DT_FORMAT = "DD/MM/YYYY HH:mm";
const ClosuresPage = () => {
  const { t, api } = useCourtContext();
  const navigate = useNavigate();
  const { supplierId } = useParams();
  const [courts, setCourts] = useState([]);
  const [closures, setClosures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();
  const [conflicts, setConflicts] = useState(null);
  const [conflictsFor, setConflictsFor] = useState("");
  const [cancelOrderCode, setCancelOrderCode] = useState(null);
  const load = useCallback(async () => {
    if (!supplierId) return;
    setLoading(true);
    try {
      const from = dayjs().subtract(1, "day").format();
      const to = dayjs().add(180, "day").format();
      const [cs, cl] = await Promise.all([
        api.request(supplierId, "courts", void 0, "GET"),
        api.request(supplierId, `closures?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, void 0, "GET")
      ]);
      setCourts((cs ?? []).filter((c) => c.active));
      setClosures(cl ?? []);
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [api, supplierId]);
  useEffect(() => {
    void load();
  }, [load]);
  const courtName = (resourceId) => resourceId ? courts.find((c) => c.resourceId === resourceId)?.name ?? resourceId : t("court.closures.allCourts", { defaultValue: "C\u1EA3 c\u01A1 s\u1EDF" });
  const handleCreate = async () => {
    if (!supplierId) return;
    try {
      const v = await form.validateFields();
      const [start, end] = v.range;
      const res = await api.request(
        supplierId,
        "closures",
        { resource_id: v.resourceId || "", start_at: start.format(), end_at: end.format(), reason: v.reason },
        "POST"
      );
      message.success(t("court.closures.created", { defaultValue: "\u0110\xE3 t\u1EA1o \u0111\u1EE3t \u0111\xF3ng c\u1EEDa" }));
      setCreateOpen(false);
      form.resetFields();
      if (res?.conflicts?.length) {
        setConflicts(res.conflicts);
        setConflictsFor(courtName(res.closure.resourceId));
      }
      await load();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    }
  };
  const showConflicts = async (c) => {
    if (!supplierId) return;
    try {
      const list = await api.request(supplierId, `closures/${c.id}/conflicts`, void 0, "GET") ?? [];
      setConflicts(list);
      setConflictsFor(courtName(c.resourceId));
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    }
  };
  const handleCancel = async (c) => {
    if (!supplierId) return;
    try {
      await api.request(supplierId, `closures/${c.id}`, void 0, "DELETE");
      message.success(t("court.closures.canceled", { defaultValue: "\u0110\xE3 g\u1EE1 \u0111\u1EE3t \u0111\xF3ng c\u1EEDa" }));
      await load();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    }
  };
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(
      PageHeader,
      {
        title: t("court.closures.title", { defaultValue: "\u0110\xF3ng c\u1EEDa / b\u1EA3o tr\xEC" }),
        extra: /* @__PURE__ */ jsxs(Space, { children: [
          /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(ArrowLeftOutlined, {}), onClick: () => navigate("../courts", { relative: "path" }), children: t("common.back", { defaultValue: "Quay l\u1EA1i" }) }),
          /* @__PURE__ */ jsx(Button, { type: "primary", icon: /* @__PURE__ */ jsx(PlusOutlined, {}), onClick: () => setCreateOpen(true), children: t("court.closures.add", { defaultValue: "Th\xEAm \u0111\u1EE3t \u0111\xF3ng c\u1EEDa" }) })
        ] })
      }
    ),
    /* @__PURE__ */ jsx(
      Table,
      {
        rowKey: "id",
        loading,
        dataSource: closures,
        pagination: false,
        locale: { emptyText: t("court.closures.empty", { defaultValue: "Kh\xF4ng c\xF3 \u0111\u1EE3t \u0111\xF3ng c\u1EEDa n\xE0o s\u1EAFp t\u1EDBi." }) },
        columns: [
          { title: t("court.closures.scope", { defaultValue: "\xC1p d\u1EE5ng cho" }), key: "scope", render: (_, r) => r.resourceId ? courtName(r.resourceId) : /* @__PURE__ */ jsx(Tag, { color: "orange", children: courtName() }) },
          { title: t("court.closures.from", { defaultValue: "T\u1EEB" }), key: "from", render: (_, r) => dayjs(r.startAt).format(DT_FORMAT) },
          { title: t("court.closures.to", { defaultValue: "\u0110\u1EBFn" }), key: "to", render: (_, r) => dayjs(r.endAt).format(DT_FORMAT) },
          { title: t("court.closures.reason", { defaultValue: "L\xFD do" }), dataIndex: "reason", key: "reason" },
          {
            title: "",
            key: "action",
            render: (_, r) => /* @__PURE__ */ jsxs(Space, { children: [
              /* @__PURE__ */ jsx(Button, { size: "small", onClick: () => void showConflicts(r), children: t("court.closures.viewConflicts", { defaultValue: "\u0110\u01A1n b\u1ECB \u1EA3nh h\u01B0\u1EDFng" }) }),
              /* @__PURE__ */ jsx(Popconfirm, { title: t("court.closures.cancelConfirm", { defaultValue: "G\u1EE1 \u0111\u1EE3t \u0111\xF3ng c\u1EEDa n\xE0y?" }), onConfirm: () => void handleCancel(r), children: /* @__PURE__ */ jsx(Button, { size: "small", danger: true, children: t("court.closures.cancel", { defaultValue: "G\u1EE1" }) }) })
            ] })
          }
        ]
      }
    ),
    /* @__PURE__ */ jsx(Modal, { title: t("court.closures.add", { defaultValue: "Th\xEAm \u0111\u1EE3t \u0111\xF3ng c\u1EEDa" }), open: createOpen, onOk: () => void handleCreate(), onCancel: () => setCreateOpen(false), destroyOnClose: true, children: /* @__PURE__ */ jsxs(Form, { form, layout: "vertical", children: [
      /* @__PURE__ */ jsx(Form.Item, { name: "resourceId", label: t("court.closures.scope", { defaultValue: "\xC1p d\u1EE5ng cho" }), initialValue: "", children: /* @__PURE__ */ jsx(Select, { options: [{ value: "", label: courtName() }, ...courts.map((c) => ({ value: c.resourceId, label: c.name }))] }) }),
      /* @__PURE__ */ jsx(Form.Item, { name: "range", label: t("court.closures.period", { defaultValue: "Kho\u1EA3ng th\u1EDDi gian" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(DatePicker.RangePicker, { showTime: { format: "HH:mm" }, format: DT_FORMAT, style: { width: "100%" } }) }),
      /* @__PURE__ */ jsx(Form.Item, { name: "reason", label: t("court.closures.reason", { defaultValue: "L\xFD do" }), rules: [{ required: true }, { max: 500 }], children: /* @__PURE__ */ jsx(Input.TextArea, { rows: 2, maxLength: 500 }) }),
      /* @__PURE__ */ jsx(Alert, { type: "info", showIcon: true, message: t("court.closures.hint", { defaultValue: "\u0110\u01A1n \u0111\xE3 \u0111\u1EB7t trong kho\u1EA3ng n\xE0y kh\xF4ng b\u1ECB hu\u1EF7 t\u1EF1 \u0111\u1ED9ng; h\u1EC7 th\u1ED1ng s\u1EBD li\u1EC7t k\xEA \u0111\u1EC3 b\u1EA1n \u0111\u1ED5i s\xE2n ho\u1EB7c ho\xE0n ti\u1EC1n. \u0110\u1EB7t m\u1EDBi trong kho\u1EA3ng n\xE0y s\u1EBD b\u1ECB ch\u1EB7n." }) })
    ] }) }),
    /* @__PURE__ */ jsx(
      Modal,
      {
        open: conflicts !== null,
        title: t("court.closures.conflictsTitle", { defaultValue: "\u0110\u01A1n b\u1ECB \u1EA3nh h\u01B0\u1EDFng \u2014 {{scope}}", scope: conflictsFor }),
        footer: /* @__PURE__ */ jsx(Button, { onClick: () => setConflicts(null), children: t("common.close", { defaultValue: "\u0110\xF3ng" }) }),
        onCancel: () => setConflicts(null),
        width: 720,
        children: conflicts && conflicts.length === 0 ? /* @__PURE__ */ jsx(Alert, { type: "success", showIcon: true, message: t("court.closures.noConflicts", { defaultValue: "Kh\xF4ng c\xF3 \u0111\u01A1n n\xE0o b\u1ECB \u1EA3nh h\u01B0\u1EDFng." }) }) : /* @__PURE__ */ jsx(
          Table,
          {
            size: "small",
            rowKey: "reservationId",
            pagination: false,
            dataSource: conflicts ?? [],
            columns: [
              { title: t("court.closures.court", { defaultValue: "S\xE2n" }), key: "court", render: (_, r) => courtName(r.resourceId) },
              { title: t("court.closures.from", { defaultValue: "T\u1EEB" }), key: "s", render: (_, r) => dayjs(r.startAt).format(DT_FORMAT) },
              { title: t("court.closures.to", { defaultValue: "\u0110\u1EBFn" }), key: "e", render: (_, r) => dayjs(r.endAt).format(DT_FORMAT) },
              { title: t("court.closures.status", { defaultValue: "Tr\u1EA1ng th\xE1i" }), dataIndex: "status", key: "status" },
              { title: t("court.closures.orderRef", { defaultValue: "\u0110\u01A1n" }), dataIndex: "orderRef", key: "orderRef", render: (v) => v || "\u2014" },
              {
                title: "",
                key: "act",
                render: (_, r) => r.orderRef ? /* @__PURE__ */ jsx(Button, { size: "small", danger: true, onClick: () => setCancelOrderCode(r.orderRef ?? null), children: t("court.closures.cancelByVenue", { defaultValue: "Hu\u1EF7 do c\u01A1 s\u1EDF (ho\xE0n theo ch\xEDnh s\xE1ch)" }) }) : null
              }
            ]
          }
        )
      }
    ),
    supplierId && /* @__PURE__ */ jsx(
      CancelOrderModal,
      {
        supplierId,
        orderCode: cancelOrderCode,
        defaultBy: "venue",
        onClose: () => setCancelOrderCode(null),
        onDone: () => {
          setCancelOrderCode(null);
          if (conflicts) setConflicts(conflicts.filter((c) => c.orderRef !== cancelOrderCode));
        }
      }
    )
  ] });
};

const fmtMoney = (n) => `${n.toLocaleString("vi-VN")}\u0111`;
const DepositsPage = () => {
  const { t, api } = useCourtContext();
  const navigate = useNavigate();
  const { supplierId } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const clientRef = useRef("");
  const load = useCallback(async () => {
    if (!supplierId) return;
    setLoading(true);
    try {
      const resp = await api.request(supplierId, "deposits/pending", void 0, "GET");
      setItems(resp?.items ?? []);
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [api, supplierId]);
  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 3e4);
    return () => clearInterval(timer);
  }, [load]);
  const open = (row) => {
    clientRef.current = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    form.setFieldsValue({ amount: Math.max(row.depositRequiredMinor - row.depositPaidMinor, 0), provider: "banktransfer", receiptUrl: "", note: "" });
    setTarget(row);
  };
  const submit = async () => {
    if (!supplierId || !target) return;
    try {
      const v = await form.validateFields();
      setSaving(true);
      await api.request(
        supplierId,
        "deposits/record",
        { order_code: target.orderCode, amount_minor: v.amount, provider: v.provider, receipt_url: v.receiptUrl || "", note: v.note || "", client_ref: clientRef.current },
        "POST"
      );
      message.success(t("court.deposits.recorded", { defaultValue: "\u0110\xE3 ghi nh\u1EADn ti\u1EC1n c\u1ECDc" }));
      setTarget(null);
      await load();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  const remaining = (row) => Math.max(row.depositRequiredMinor - row.depositPaidMinor, 0);
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(
      PageHeader,
      {
        title: t("court.deposits.title", { defaultValue: "C\u1ECDc ch\u1EDD x\xE1c nh\u1EADn" }),
        description: t("court.deposits.description", {
          defaultValue: "\u0110\u01A1n \u0111\u1EB7t s\xE2n \u0111ang gi\u1EEF ch\u1ED7 ch\u1EDD kh\xE1ch \u0111\u1EB7t c\u1ECDc. X\xE1c nh\u1EADn khi \u0111\xE3 nh\u1EADn ti\u1EC1n; qu\xE1 h\u1EA1n gi\u1EEF ch\u1ED7 ch\u01B0a c\xF3 c\u1ECDc, s\xE2n t\u1EF1 \u0111\u01B0\u1EE3c nh\u1EA3."
        }),
        extra: /* @__PURE__ */ jsxs(Space, { wrap: true, children: [
          /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(ArrowLeftOutlined, {}), onClick: () => navigate("../courts", { relative: "path" }), children: t("court.courts.title", { defaultValue: "S\xE2n" }) }),
          /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(ReloadOutlined, {}), onClick: () => void load(), loading, children: t("common.refresh", { defaultValue: "L\xE0m m\u1EDBi" }) })
        ] })
      }
    ),
    /* @__PURE__ */ jsx(
      Table,
      {
        rowKey: "orderCode",
        loading,
        dataSource: items,
        pagination: false,
        scroll: { x: "max-content" },
        locale: { emptyText: t("court.deposits.empty", { defaultValue: "Kh\xF4ng c\xF3 \u0111\u01A1n n\xE0o \u0111ang ch\u1EDD c\u1ECDc." }) },
        columns: [
          { title: t("court.deposits.order", { defaultValue: "\u0110\u01A1n" }), dataIndex: "orderCode", render: (v) => /* @__PURE__ */ jsx(Typography.Text, { copyable: true, children: v }) },
          {
            title: t("court.deposits.customer", { defaultValue: "Kh\xE1ch" }),
            render: (_, r) => /* @__PURE__ */ jsxs(Space, { direction: "vertical", size: 0, children: [
              /* @__PURE__ */ jsx("span", { children: r.customer || "\u2014" }),
              r.phone && /* @__PURE__ */ jsx(Typography.Text, { type: "secondary", children: r.phone })
            ] })
          },
          { title: t("court.deposits.court", { defaultValue: "S\xE2n" }), dataIndex: "resourceName", render: (v) => v || "\u2014" },
          {
            title: t("court.deposits.time", { defaultValue: "Gi\u1EDD ch\u01A1i" }),
            render: (_, r) => r.startsAt ? `${formatVenueDateTime(r.startsAt)} \u2013 ${r.endsAt ? formatVenueDateTime(r.endsAt).slice(-5) : ""}` : "\u2014"
          },
          { title: t("court.deposits.total", { defaultValue: "T\u1ED5ng \u0111\u01A1n" }), dataIndex: "totalMinor", align: "right", render: fmtMoney },
          {
            title: t("court.deposits.needed", { defaultValue: "C\u1EA7n c\u1ECDc" }),
            align: "right",
            render: (_, r) => /* @__PURE__ */ jsxs(Space, { direction: "vertical", size: 0, style: { textAlign: "right" }, children: [
              /* @__PURE__ */ jsx("strong", { children: fmtMoney(remaining(r)) }),
              r.depositPaidMinor > 0 && /* @__PURE__ */ jsxs(Typography.Text, { type: "secondary", children: [
                t("court.deposits.alreadyPaid", { defaultValue: "\u0111\xE3 nh\u1EADn" }),
                " ",
                fmtMoney(r.depositPaidMinor)
              ] })
            ] })
          },
          {
            title: t("court.deposits.expires", { defaultValue: "Gi\u1EEF ch\u1ED7 \u0111\u1EBFn" }),
            dataIndex: "expiresAt",
            render: (v) => {
              if (!v) return "\u2014";
              const mins = dayjs(v).diff(dayjs(), "minute");
              return /* @__PURE__ */ jsx(Tag, { color: mins <= 10 ? "red" : mins <= 30 ? "gold" : "default", children: mins <= 0 ? t("court.deposits.expiring", { defaultValue: "s\u1EAFp h\u1EBFt h\u1EA1n" }) : `${formatVenueDateTime(v).slice(-5)} (${mins}\u2032)` });
            }
          },
          {
            title: "",
            render: (_, r) => /* @__PURE__ */ jsx(Button, { type: "primary", size: "small", onClick: () => open(r), children: t("court.deposits.confirm", { defaultValue: "X\xE1c nh\u1EADn \u0111\xE3 nh\u1EADn c\u1ECDc" }) })
          }
        ]
      }
    ),
    /* @__PURE__ */ jsxs(
      Modal,
      {
        open: !!target,
        title: t("court.deposits.confirmTitle", { defaultValue: "X\xE1c nh\u1EADn \u0111\xE3 nh\u1EADn c\u1ECDc" }) + (target ? ` \u2014 ${target.orderCode}` : ""),
        onCancel: () => setTarget(null),
        onOk: () => void submit(),
        okText: t("court.deposits.confirm", { defaultValue: "X\xE1c nh\u1EADn \u0111\xE3 nh\u1EADn c\u1ECDc" }),
        cancelText: t("common.cancel", { defaultValue: "Hu\u1EF7" }),
        confirmLoading: saving,
        destroyOnClose: true,
        children: [
          /* @__PURE__ */ jsx(
            Alert,
            {
              type: "info",
              showIcon: true,
              style: { marginBottom: 12 },
              message: t("court.deposits.confirmHint", {
                defaultValue: "Ch\u1EC9 x\xE1c nh\u1EADn khi ti\u1EC1n \u0111\xE3 v\xE0o qu\u1EF9 / t\xE0i kho\u1EA3n. Nh\u1EADp s\u1ED1 ti\u1EC1n th\u1EF1c nh\u1EADn; \u0111\u1EE7 m\u1EE9c c\u1ECDc th\xEC ch\u1ED7 \u0111\u01B0\u1EE3c gi\u1EEF ch\u1EAFc."
              })
            }
          ),
          /* @__PURE__ */ jsxs(Form, { form, layout: "vertical", children: [
            /* @__PURE__ */ jsx(Form.Item, { name: "amount", label: t("court.deposits.amount", { defaultValue: "S\u1ED1 ti\u1EC1n th\u1EF1c nh\u1EADn (\u0111\u1ED3ng)" }), rules: [{ required: true, type: "number", min: 1 }], children: /* @__PURE__ */ jsx(InputNumber, { style: { width: "100%" }, min: 1, step: 1e4, formatter: (v) => `${v ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, "."), parser: (v) => Number(`${v ?? ""}`.replace(/\./g, "")) }) }),
            /* @__PURE__ */ jsx(Form.Item, { name: "provider", label: t("court.deposits.method", { defaultValue: "H\xECnh th\u1EE9c" }), rules: [{ required: true }], children: /* @__PURE__ */ jsx(
              Select,
              {
                options: [
                  { value: "banktransfer", label: t("court.deposits.bank", { defaultValue: "Chuy\u1EC3n kho\u1EA3n" }) },
                  { value: "cash", label: t("court.deposits.cash", { defaultValue: "Ti\u1EC1n m\u1EB7t" }) }
                ]
              }
            ) }),
            /* @__PURE__ */ jsx(Form.Item, { name: "receiptUrl", label: t("court.deposits.receipt", { defaultValue: "Li\xEAn k\u1EBFt \u1EA3nh bi\xEAn lai / ch\u1EE9ng t\u1EEB (n\u1EBFu c\xF3)" }), children: /* @__PURE__ */ jsx(Input, { placeholder: "https://\u2026" }) }),
            /* @__PURE__ */ jsx(Form.Item, { name: "note", label: t("court.deposits.note", { defaultValue: "Ghi ch\xFA" }), children: /* @__PURE__ */ jsx(Input, {}) })
          ] })
        ]
      }
    )
  ] });
};

const CHANNEL_LABEL$1 = { cash: "Ti\u1EC1n m\u1EB7t", banktransfer: "Chuy\u1EC3n kho\u1EA3n" };
const RefundsPage = () => {
  const { t, api } = useCourtContext();
  const navigate = useNavigate();
  const { supplierId } = useParams();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const load = useCallback(async () => {
    if (!supplierId) return;
    setLoading(true);
    try {
      const r = await api.request(supplierId, "refunds/pending", void 0, "GET");
      setTasks(r?.tasks ?? []);
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [api, supplierId]);
  useEffect(() => {
    void load();
  }, [load]);
  const submit = async () => {
    if (!supplierId || !target) return;
    try {
      const v = await form.validateFields();
      setSaving(true);
      await api.request(supplierId, "refunds/done", { refund_id: target.id, receipt_url: v.receiptUrl || "", note: v.note || "" }, "POST");
      message.success(t("court.refunds.done", { defaultValue: "\u0110\xE3 ghi nh\u1EADn ho\xE0n ti\u1EC1n" }));
      setTarget(null);
      await load();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(
      PageHeader,
      {
        title: t("court.refunds.title", { defaultValue: "Ho\xE0n ti\u1EC1n ch\u1EDD x\u1EED l\xFD" }),
        description: t("court.refunds.description", { defaultValue: 'Ho\xE0n \u0111\xFAng k\xEAnh kh\xE1ch \u0111\xE3 tr\u1EA3, r\u1ED3i b\u1EA5m "\u0110\xE3 ho\xE0n" k\xE8m bi\xEAn lai. H\u1EC7 th\u1ED1ng kh\xF4ng t\u1EF1 chuy\u1EC3n ti\u1EC1n.' }),
        extra: /* @__PURE__ */ jsxs(Space, { wrap: true, children: [
          /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(ArrowLeftOutlined, {}), onClick: () => navigate("../courts", { relative: "path" }), children: t("court.courts.title", { defaultValue: "S\xE2n" }) }),
          /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(ReloadOutlined, {}), onClick: () => void load(), loading, children: t("common.refresh", { defaultValue: "L\xE0m m\u1EDBi" }) })
        ] })
      }
    ),
    /* @__PURE__ */ jsx(
      Table,
      {
        rowKey: "id",
        loading,
        dataSource: tasks,
        pagination: false,
        scroll: { x: "max-content" },
        locale: { emptyText: t("court.refunds.empty", { defaultValue: "Kh\xF4ng c\xF3 kho\u1EA3n ho\xE0n n\xE0o \u0111ang ch\u1EDD." }) },
        columns: [
          { title: t("court.refunds.order", { defaultValue: "\u0110\u01A1n" }), dataIndex: "orderCode", render: (v) => /* @__PURE__ */ jsx(Typography.Text, { copyable: true, children: v }) },
          { title: t("court.refunds.amount", { defaultValue: "S\u1ED1 ti\u1EC1n ho\xE0n" }), dataIndex: "amountMinor", align: "right", render: (n) => /* @__PURE__ */ jsxs("strong", { children: [
            n.toLocaleString("vi-VN"),
            "\u0111"
          ] }) },
          {
            title: t("court.refunds.channel", { defaultValue: "Ho\xE0n b\u1EB1ng" }),
            dataIndex: "channel",
            render: (c) => /* @__PURE__ */ jsx(Tag, { color: c === "cash" ? "green" : c === "banktransfer" ? "blue" : "orange", children: CHANNEL_LABEL$1[c] ?? `${c} (ho\xE0n tr\xEAn c\u1ED5ng thanh to\xE1n)` })
          },
          { title: t("court.refunds.reason", { defaultValue: "L\xFD do" }), dataIndex: "reason" },
          { title: t("court.refunds.requestedBy", { defaultValue: "Ng\u01B0\u1EDDi y\xEAu c\u1EA7u" }), dataIndex: "requestedBy" },
          {
            title: "",
            render: (_, r) => /* @__PURE__ */ jsx(
              Button,
              {
                type: "primary",
                size: "small",
                onClick: () => {
                  form.resetFields();
                  setTarget(r);
                },
                children: t("court.refunds.markDone", { defaultValue: "\u0110\xE3 ho\xE0n" })
              }
            )
          }
        ]
      }
    ),
    /* @__PURE__ */ jsxs(
      Modal,
      {
        open: !!target,
        title: t("court.refunds.confirmTitle", { defaultValue: "X\xE1c nh\u1EADn \u0111\xE3 ho\xE0n ti\u1EC1n" }) + (target ? ` \u2014 ${target.orderCode}` : ""),
        onCancel: () => setTarget(null),
        onOk: () => void submit(),
        okText: t("court.refunds.markDone", { defaultValue: "\u0110\xE3 ho\xE0n" }),
        confirmLoading: saving,
        destroyOnClose: true,
        children: [
          target && /* @__PURE__ */ jsx(
            Alert,
            {
              type: "info",
              showIcon: true,
              style: { marginBottom: 12 },
              message: `${target.amountMinor.toLocaleString("vi-VN")}\u0111 \u2014 ${CHANNEL_LABEL$1[target.channel] ?? target.channel}`,
              description: t("court.refunds.confirmHint", { defaultValue: "Ch\u1EC9 x\xE1c nh\u1EADn khi ti\u1EC1n \u0111\xE3 th\u1EF1c s\u1EF1 tr\u1EA3 l\u1EA1i cho kh\xE1ch." })
            }
          ),
          /* @__PURE__ */ jsxs(Form, { form, layout: "vertical", children: [
            /* @__PURE__ */ jsx(Form.Item, { name: "receiptUrl", label: t("court.refunds.receipt", { defaultValue: "Li\xEAn k\u1EBFt \u1EA3nh bi\xEAn lai / ch\u1EE9ng t\u1EEB (n\u1EBFu c\xF3)" }), children: /* @__PURE__ */ jsx(Input, { placeholder: "https://\u2026" }) }),
            /* @__PURE__ */ jsx(Form.Item, { name: "note", label: t("court.refunds.note", { defaultValue: "Ghi ch\xFA" }), children: /* @__PURE__ */ jsx(Input, {}) })
          ] })
        ]
      }
    )
  ] });
};

function downloadCsv(filename, header, rows) {
  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = [header, ...rows].map((r) => r.map(esc).join(",")).join("\r\n");
  const blob = new Blob(["\uFEFF" + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
function formatVnd(n) {
  return `${Math.round(n ?? 0).toLocaleString("vi-VN")}\u0111`;
}

const DATE_FORMAT$1 = "DD/MM/YYYY";
const API_DATE = "YYYY-MM-DD";
const CHANNEL_LABEL = { web: "Webshop", backoffice: "Backoffice", pos: "POS (qu\u1EA7y)", unknown: "Kh\xF4ng r\xF5" };
const WEEKDAYS = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
const pct = (r) => `${Math.round(r * 100)}%`;
const hoursOf = (min) => (min / 60).toLocaleString("vi-VN", { maximumFractionDigits: 1 });
const ReportsPage = () => {
  const { t, api } = useCourtContext();
  const navigate = useNavigate();
  const { supplierId } = useParams();
  const [range, setRange] = useState([dayjs().startOf("month"), dayjs()]);
  const [revGroup, setRevGroup] = useState("day");
  const [utilGroup, setUtilGroup] = useState("court");
  const [util, setUtil] = useState(null);
  const [rev, setRev] = useState(null);
  const [cancel, setCancel] = useState(null);
  const [errs, setErrs] = useState({});
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    if (!supplierId) return;
    setLoading(true);
    const q = `from=${range[0].format(API_DATE)}&to=${range[1].format(API_DATE)}`;
    const e = {};
    const run = async (key, path, set) => {
      try {
        const r = await api.request(supplierId, path, void 0, "GET");
        set(r?.report ?? null);
      } catch (err) {
        set(null);
        e[key] = err instanceof Error ? err.message : "L\u1ED7i";
      }
    };
    await Promise.all([
      run("util", `reports/utilization?${q}&group_by=${utilGroup}`, setUtil),
      run("rev", `reports/revenue?${q}&group_by=${revGroup}`, setRev),
      run("cancel", `reports/cancellations?${q}`, setCancel)
    ]);
    setErrs(e);
    setLoading(false);
  }, [api, supplierId, range, revGroup, utilGroup]);
  useEffect(() => {
    void load();
  }, [load]);
  const keyLabel = (k, group) => group === "channel" ? CHANNEL_LABEL[k] ?? k : group === "staff" && k === "unknown" ? "Kh\xF4ng r\xF5" : k;
  const rangeText = `${range[0].format("DDMMYYYY")}-${range[1].format("DDMMYYYY")}`;
  const heat = () => {
    if (!util || util.heatmap.length === 0) return null;
    const hours = Array.from(new Set(util.heatmap.map((h) => h.key.split("-")[1]))).sort();
    const cell = new Map(util.heatmap.map((h) => [h.key, h]));
    const maxRate = Math.max(1e-4, ...util.heatmap.map((h) => h.rate));
    return /* @__PURE__ */ jsx("div", { style: { overflowX: "auto", marginTop: 16 }, children: /* @__PURE__ */ jsxs("table", { style: { borderCollapse: "separate", borderSpacing: 2, fontSize: 12 }, children: [
      /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { children: [
        /* @__PURE__ */ jsx("th", {}),
        hours.map((h) => /* @__PURE__ */ jsxs("th", { style: { fontWeight: 400, minWidth: 34 }, children: [
          h,
          "h"
        ] }, h))
      ] }) }),
      /* @__PURE__ */ jsx("tbody", { children: [1, 2, 3, 4, 5, 6, 0].map((w) => /* @__PURE__ */ jsxs("tr", { children: [
        /* @__PURE__ */ jsx("th", { style: { fontWeight: 500, textAlign: "left", paddingRight: 8 }, children: WEEKDAYS[w] }),
        hours.map((h) => {
          const c = cell.get(`${w}-${h}`);
          const rate = c?.rate ?? 0;
          const sold = (c?.soldMinutes ?? 0) > 0;
          const alpha = sold ? 0.25 + 0.7 * (rate / maxRate) : c ? 0.06 : 0;
          return /* @__PURE__ */ jsx("td", { style: { padding: 0 }, children: /* @__PURE__ */ jsx(Tooltip, { title: c ? `${WEEKDAYS[w]} ${h}h: ${pct(rate)} (${hoursOf(c.soldMinutes)}/${hoursOf(c.availableMinutes)} gi\u1EDD)` : "\u0110\xF3ng c\u1EEDa", children: /* @__PURE__ */ jsx("div", { style: {
            height: 26,
            borderRadius: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 500,
            color: sold && alpha > 0.6 ? "#fff" : "inherit",
            background: c ? `rgba(22, 119, 255, ${alpha})` : "transparent",
            border: c ? "none" : "1px dashed rgba(128,128,128,.3)"
          }, children: sold ? pct(rate) : "" }) }) }, h);
        })
      ] }, w)) })
    ] }) });
  };
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(
      PageHeader,
      {
        title: t("court.reports.title", { defaultValue: "B\xE1o c\xE1o s\xE2n" }),
        description: t("court.reports.description", { defaultValue: "C\xF4ng su\u1EA5t, doanh thu v\xE0 hu\u1EF7 / kh\xF4ng \u0111\u1EBFn. Ng\xE0y theo gi\u1EDD Vi\u1EC7t Nam." }),
        extra: /* @__PURE__ */ jsxs(Space, { wrap: true, children: [
          /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(ArrowLeftOutlined, {}), onClick: () => navigate("../courts", { relative: "path" }), children: t("court.courts.title", { defaultValue: "S\xE2n" }) }),
          /* @__PURE__ */ jsx(DatePicker.RangePicker, { value: range, format: DATE_FORMAT$1, allowClear: false, onChange: (v) => v && v[0] && v[1] && setRange([v[0], v[1]]) }),
          /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(ReloadOutlined, {}), onClick: () => void load(), loading, children: t("common.refresh", { defaultValue: "L\xE0m m\u1EDBi" }) })
        ] })
      }
    ),
    /* @__PURE__ */ jsxs(
      Card,
      {
        title: "C\xF4ng su\u1EA5t",
        style: { marginBottom: 16 },
        extra: /* @__PURE__ */ jsxs(Space, { wrap: true, children: [
          /* @__PURE__ */ jsx(
            Select,
            {
              value: utilGroup,
              onChange: setUtilGroup,
              style: { width: 150 },
              options: [{ value: "court", label: "Theo s\xE2n" }, { value: "day", label: "Theo ng\xE0y" }, { value: "weekday", label: "Theo th\u1EE9" }, { value: "hour", label: "Theo gi\u1EDD" }]
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              icon: /* @__PURE__ */ jsx(DownloadOutlined, {}),
              disabled: !util,
              onClick: () => util && downloadCsv(`cong-suat-${rangeText}.csv`, ["Nh\xF3m", "Gi\u1EDD b\xE1n", "Gi\u1EDD m\u1EDF kh\u1EA3 d\u1EE5ng", "C\xF4ng su\u1EA5t"], util.rows.map((r) => [r.label, hoursOf(r.soldMinutes), hoursOf(r.availableMinutes), pct(r.rate)])),
              children: "CSV"
            }
          )
        ] }),
        children: [
          errs.util && /* @__PURE__ */ jsx(Alert, { type: "error", message: errs.util, showIcon: true }),
          util && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsxs(Space, { size: 32, wrap: true, children: [
              /* @__PURE__ */ jsx(Statistic, { title: "C\xF4ng su\u1EA5t", value: pct(util.summary.rate) }),
              /* @__PURE__ */ jsx(Statistic, { title: "Gi\u1EDD \u0111\xE3 b\xE1n", value: hoursOf(util.summary.soldMinutes), suffix: "gi\u1EDD" }),
              /* @__PURE__ */ jsx(Statistic, { title: "Gi\u1EDD m\u1EDF kh\u1EA3 d\u1EE5ng", value: hoursOf(util.summary.availableMinutes), suffix: "gi\u1EDD" })
            ] }),
            /* @__PURE__ */ jsx(Typography.Paragraph, { type: "secondary", style: { marginTop: 8 }, children: "\u0110\xE3 b\xE1n g\u1ED3m c\u1EA3 \u0111\u01A1n kh\xE1ch kh\xF4ng \u0111\u1EBFn (s\xE2n \u0111\xE3 b\u1ECB chi\u1EBFm). Gi\u1EDD kh\u1EA3 d\u1EE5ng \u0111\xE3 tr\u1EEB \u0111\xF3ng c\u1EEDa / b\u1EA3o tr\xEC." }),
            /* @__PURE__ */ jsx(
              Table,
              {
                size: "small",
                rowKey: "key",
                pagination: false,
                dataSource: util.rows,
                scroll: { x: "max-content" },
                columns: [
                  { title: "Nh\xF3m", dataIndex: "label" },
                  { title: "Gi\u1EDD b\xE1n", dataIndex: "soldMinutes", align: "right", render: (v) => hoursOf(v) },
                  { title: "Gi\u1EDD m\u1EDF", dataIndex: "availableMinutes", align: "right", render: (v) => hoursOf(v) },
                  { title: "C\xF4ng su\u1EA5t", dataIndex: "rate", width: 220, render: (v) => /* @__PURE__ */ jsx(Progress, { percent: Math.round(v * 100), size: "small" }) }
                ]
              }
            ),
            /* @__PURE__ */ jsx(Typography.Title, { level: 5, style: { marginTop: 16 }, children: "B\u1EA3n \u0111\u1ED3 nhi\u1EC7t theo th\u1EE9 v\xE0 gi\u1EDD" }),
            heat()
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsxs(
      Card,
      {
        title: "Doanh thu",
        style: { marginBottom: 16 },
        extra: /* @__PURE__ */ jsxs(Space, { wrap: true, children: [
          /* @__PURE__ */ jsx(
            Select,
            {
              value: revGroup,
              onChange: setRevGroup,
              style: { width: 160 },
              options: [{ value: "day", label: "Theo ng\xE0y ch\u01A1i" }, { value: "court", label: "Theo s\xE2n" }, { value: "hour", label: "Theo gi\u1EDD" }, { value: "channel", label: "Theo k\xEAnh \u0111\u1EB7t" }, { value: "staff", label: "Theo nh\xE2n vi\xEAn" }]
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              icon: /* @__PURE__ */ jsx(DownloadOutlined, {}),
              disabled: !rev,
              onClick: () => rev && downloadCsv(`doanh-thu-${rangeText}.csv`, ["Nh\xF3m", "S\u1ED1 \u0111\u01A1n", "Gi\u1EDD b\xE1n", "Ti\u1EC1n s\xE2n (\u0111)", "C\u1ECDc gi\u1EEF l\u1EA1i (\u0111)", "Doanh thu (\u0111)", "Ho\xE0n d\u1EF1 ki\u1EBFn (\u0111)"], rev.rows.map((r) => [r.label || keyLabel(r.key, revGroup), r.orders, r.hours, r.soldMinor, r.forfeitMinor, r.revenueMinor, r.refundMinor])),
              children: "CSV"
            }
          )
        ] }),
        children: [
          errs.rev && /* @__PURE__ */ jsx(Alert, { type: "error", message: errs.rev, showIcon: true }),
          rev && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsxs(Space, { size: 32, wrap: true, children: [
              /* @__PURE__ */ jsx(Statistic, { title: "Doanh thu theo ng\xE0y ch\u01A1i", value: formatVnd(rev.summary.revenueMinor) }),
              /* @__PURE__ */ jsx(Statistic, { title: "Ti\u1EC1n s\xE2n (\u0111\u01A1n \u0111\xE3 tr\u1EA3 \u0111\u1EE7)", value: formatVnd(rev.summary.soldMinor) }),
              /* @__PURE__ */ jsx(Statistic, { title: "C\u1ECDc gi\u1EEF l\u1EA1i (kh\xF4ng \u0111\u1EBFn / hu\u1EF7 mu\u1ED9n)", value: formatVnd(rev.summary.forfeitMinor) }),
              /* @__PURE__ */ jsx(Statistic, { title: "Ti\u1EC1n \u0111\xE3 thu trong k\u1EF3", value: formatVnd(rev.collectedMinor) }),
              /* @__PURE__ */ jsx(Statistic, { title: "Ho\xE0n d\u1EF1 ki\u1EBFn", value: formatVnd(rev.summary.refundMinor) }),
              /* @__PURE__ */ jsx(Statistic, { title: "Thu\xEA s\xE2n s\u1EF1 ki\u1EC7n (\u0111\xE3 di\u1EC5n ra, t\xEDnh ri\xEAng)", value: formatVnd(rev.rentalsMinor) })
            ] }),
            /* @__PURE__ */ jsx(
              Alert,
              {
                style: { margin: "12px 0" },
                type: "info",
                showIcon: true,
                message: "Hai s\u1ED1 kh\xE1c nhau c\xF3 ch\u1EE7 \u0111\xEDch",
                description: "Doanh thu t\xEDnh theo ng\xE0y kh\xE1ch ch\u01A1i. Ti\u1EC1n \u0111\xE3 thu t\xEDnh theo ng\xE0y nh\u1EADn ti\u1EC1n (c\u1ECDc v\xE0 thanh to\xE1n), ch\u01B0a tr\u1EEB ho\xE0n. Ch\u01B0a g\u1ED3m F&B (xem b\xE1o c\xE1o POS) v\xE0 g\xF3i \u0111\u1ECBnh k\u1EF3; thu\xEA s\xE2n s\u1EF1 ki\u1EC7n hi\u1EC7n \u1EDF d\xF2ng ri\xEAng, kh\xF4ng c\u1ED9ng v\xE0o doanh thu s\xE2n l\u1EBB."
              }
            ),
            /* @__PURE__ */ jsx(
              Table,
              {
                size: "small",
                rowKey: "key",
                pagination: false,
                dataSource: rev.rows,
                scroll: { x: "max-content" },
                columns: [
                  { title: "Nh\xF3m", dataIndex: "key", render: (k, r) => r.label || keyLabel(k, revGroup) },
                  { title: "S\u1ED1 \u0111\u01A1n", dataIndex: "orders", align: "right" },
                  { title: "Gi\u1EDD b\xE1n", dataIndex: "hours", align: "right", render: (v) => v.toLocaleString("vi-VN", { maximumFractionDigits: 1 }) },
                  { title: "Ti\u1EC1n s\xE2n", dataIndex: "soldMinor", align: "right", render: formatVnd },
                  { title: "C\u1ECDc gi\u1EEF l\u1EA1i", dataIndex: "forfeitMinor", align: "right", render: formatVnd },
                  { title: "Doanh thu", dataIndex: "revenueMinor", align: "right", render: (v) => /* @__PURE__ */ jsx("strong", { children: formatVnd(v) }) },
                  { title: "Ho\xE0n d\u1EF1 ki\u1EBFn", dataIndex: "refundMinor", align: "right", render: formatVnd }
                ]
              }
            )
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsxs(
      Card,
      {
        title: "Hu\u1EF7 v\xE0 kh\xF4ng \u0111\u1EBFn",
        extra: /* @__PURE__ */ jsx(
          Button,
          {
            icon: /* @__PURE__ */ jsx(DownloadOutlined, {}),
            disabled: !cancel,
            onClick: () => cancel && downloadCsv(`huy-no-show-${rangeText}.csv`, ["Ch\u1EC9 s\u1ED1", "Gi\xE1 tr\u1ECB"], [
              ["\u0110\u01A1n t\u1EA1o trong k\u1EF3", cancel.created],
              ["\u0110\u01A1n \u0111\xE3 tr\u1EA3 \u0111\u1EE7", cancel.paid],
              ["Kh\xE1ch hu\u1EF7", cancel.canceledByCustomer],
              ["Nh\xE2n vi\xEAn hu\u1EF7", cancel.canceledByStaff],
              ["C\u01A1 s\u1EDF hu\u1EF7", cancel.canceledByVenue],
              ["H\u1EBFt h\u1EA1n gi\u1EEF ch\u1ED7", cancel.expired],
              ["Kh\xF4ng \u0111\u1EBFn", cancel.noShows],
              ["C\u1ECDc gi\u1EEF l\u1EA1i (\u0111)", cancel.forfeitMinor],
              ["Ho\xE0n d\u1EF1 ki\u1EBFn (\u0111)", cancel.refundMinor]
            ]),
            children: "CSV"
          }
        ),
        children: [
          errs.cancel && /* @__PURE__ */ jsx(Alert, { type: "error", message: errs.cancel, showIcon: true }),
          cancel && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsxs(Space, { size: 32, wrap: true, children: [
              /* @__PURE__ */ jsx(Statistic, { title: "\u0110\u01A1n t\u1EA1o trong k\u1EF3", value: cancel.created }),
              /* @__PURE__ */ jsx(Statistic, { title: "T\u1EF7 l\u1EC7 hu\u1EF7", value: cancel.created ? pct((cancel.canceledByCustomer + cancel.canceledByStaff + cancel.canceledByVenue) / cancel.created) : "0%" }),
              /* @__PURE__ */ jsx(Statistic, { title: "T\u1EF7 l\u1EC7 kh\xF4ng \u0111\u1EBFn", value: cancel.created ? pct(cancel.noShows / cancel.created) : "0%" }),
              /* @__PURE__ */ jsx(Statistic, { title: "C\u1ECDc gi\u1EEF l\u1EA1i", value: formatVnd(cancel.forfeitMinor) }),
              /* @__PURE__ */ jsx(Statistic, { title: "Ho\xE0n ch\u1EDD x\u1EED l\xFD", value: cancel.refundPending, suffix: "\u0111\u01A1n" })
            ] }),
            /* @__PURE__ */ jsxs(Typography.Paragraph, { type: "secondary", style: { marginTop: 8 }, children: [
              "Kh\xE1ch hu\u1EF7 ",
              cancel.canceledByCustomer,
              " \xB7 Nh\xE2n vi\xEAn hu\u1EF7 ",
              cancel.canceledByStaff,
              " \xB7 C\u01A1 s\u1EDF hu\u1EF7 ",
              cancel.canceledByVenue,
              " \xB7 H\u1EBFt h\u1EA1n gi\u1EEF ch\u1ED7 ",
              cancel.expired,
              " (kh\xF4ng t\xEDnh v\xE0o t\u1EF7 l\u1EC7 hu\u1EF7)."
            ] }),
            cancel.topNoShow.length > 0 && /* @__PURE__ */ jsx(
              Table,
              {
                size: "small",
                rowKey: "phone",
                pagination: false,
                dataSource: cancel.topNoShow,
                columns: [{ title: "S\u1ED1 \u0111i\u1EC7n tho\u1EA1i hay kh\xF4ng \u0111\u1EBFn", dataIndex: "phone" }, { title: "S\u1ED1 l\u1EA7n", dataIndex: "noShows", align: "right" }]
              }
            )
          ] })
        ]
      }
    )
  ] });
};

const dueMinorOf = (r) => {
  if (r.status === "quote" || r.status === "canceled" || r.status === "expired") return 0;
  const final = r.settlement?.finalMinor ?? r.totalMinor;
  return Math.max(0, final - r.paidMinor - (r.settlement?.extraPaidMinor ?? 0));
};
const statusTag = (r) => r.paidInFull && (r.status === "held" || r.status === "deposited") ? /* @__PURE__ */ jsx(Tag, { color: "green", children: "\u0110\xE3 thanh to\xE1n \u0111\u1EE7" }) : /* @__PURE__ */ jsx(Tag, { color: STATUS[r.status].color, children: STATUS[r.status].label });
const STATUS = {
  quote: { label: "B\xE1o gi\xE1", color: "default" },
  held: { label: "Gi\u1EEF ch\u1ED7 t\u1EA1m", color: "orange" },
  deposited: { label: "\u0110\xE3 c\u1ECDc", color: "blue" },
  completed: { label: "\u0110\xE3 di\u1EC5n ra", color: "cyan" },
  settled: { label: "\u0110\xE3 quy\u1EBFt to\xE1n", color: "green" },
  canceled: { label: "\u0110\xE3 hu\u1EF7", color: "red" },
  expired: { label: "H\u1EBFt h\u1EA1n gi\u1EEF ch\u1ED7", color: "volcano" }
};
const KIND_LABEL = { court: "S\xE2n", service: "D\u1ECBch v\u1EE5", surcharge: "Ph\u1EE5 thu", discount: "Gi\u1EA3m gi\xE1" };
const DATE_FORMAT = "DD/MM/YYYY";
const toBlocksBody = (courtIds, range, from, to, tz) => {
  const out = [];
  for (let d = range[0].startOf("day"); !d.isAfter(range[1], "day"); d = d.add(1, "day")) {
    for (const id of courtIds) {
      out.push({ resource_id: id, start_at: venueLocalToISO(d.format("YYYY-MM-DD"), from.format("HH:mm"), tz), end_at: venueLocalToISO(d.format("YYYY-MM-DD"), to.format("HH:mm"), tz) });
    }
  }
  return out;
};
const MONEY_INPUT = {
  formatter: (v) => `${v ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, "."),
  parser: (v) => Number((v ?? "").replace(/\./g, ""))
};
const EventRentalsPage = () => {
  const { t, api } = useCourtContext();
  const navigate = useNavigate();
  const { supplierId } = useParams();
  const [rentals, setRentals] = useState([]);
  const [courts, setCourts] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    if (!supplierId) return;
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.request(supplierId, `rentals?status=${encodeURIComponent(filter)}`, void 0, "GET"),
        api.request(supplierId, "courts", void 0, "GET")
      ]);
      setRentals(r?.rentals ?? []);
      setCourts((c ?? []).filter((x) => x.active));
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [api, supplierId, filter]);
  useEffect(() => {
    void load();
  }, [load]);
  const tz = courts[0]?.timezone || DEFAULT_VENUE_TZ;
  const courtName = useMemo(() => new Map(courts.map((c) => [c.resourceId, c.name])), [courts]);
  const reload = async (code) => {
    if (!supplierId) return;
    const r = await api.request(supplierId, `rentals/get?code=${encodeURIComponent(code)}`, void 0, "GET");
    setDetail(r?.rental ?? null);
    await load();
  };
  const act = async (path, body, ok) => {
    if (!supplierId) return false;
    try {
      await api.request(supplierId, path, body, "POST");
      message.success(ok);
      return true;
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
      return false;
    }
  };
  const draftLines = async () => {
    if (!supplierId) return;
    const v = form.getFieldsValue();
    if (!v.courtIds?.length || !v.dates || !v.times) {
      message.warning("Ch\u1ECDn s\xE2n, ng\xE0y v\xE0 gi\u1EDD tr\u01B0\u1EDBc khi t\xEDnh gi\xE1 nh\xE1p");
      return;
    }
    const blocks = toBlocksBody(v.courtIds, v.dates, v.times[0], v.times[1], tz);
    try {
      const r = await api.request(supplierId, "rentals/draft-quote", { input: { blocks }, mode: v.mode }, "POST");
      form.setFieldsValue({ lines: r?.lines ?? [] });
      if (r?.warning) message.warning(r.warning, 8);
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    }
  };
  const submitCreate = async () => {
    if (!supplierId) return;
    try {
      const v = await form.validateFields();
      setSaving(true);
      const blocks = toBlocksBody(v.courtIds, v.dates, v.times[0], v.times[1], tz);
      const lines = (v.lines ?? []).map((l) => ({ kind: l.kind, label: l.label, qty: l.qty ?? 1, unit_minor: l.unitMinor ?? 0, total_minor: l.totalMinor ?? 0 }));
      const stages = v.defaultStages === false ? (v.stages ?? []).map((s) => ({ label: s.label, amount_minor: s.amountMinor, due_on: s.dueOn ? s.dueOn.format("YYYY-MM-DD") : "" })) : [];
      const input = {
        title: v.title,
        customer_name: v.customerName,
        contact_name: v.contactName || "",
        phone: v.phone || "",
        email: v.email || "",
        tax_code: v.taxCode || "",
        address: v.address || "",
        vat_requested: !!v.vat,
        blocks,
        mode: v.mode,
        lines,
        stages
      };
      await api.request(supplierId, "rentals/create", { input }, "POST");
      message.success("\u0110\xE3 t\u1EA1o b\xE1o gi\xE1");
      setCreating(false);
      form.resetFields();
      await load();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  const printDoc = (r, kind) => {
    const esc = (s) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);
    const blocks = r.blocks.map((b) => `<tr><td>${esc(courtName.get(b.resourceId) ?? b.resourceId)}</td><td>${formatVenueDateTime(b.startAt, tz)}</td><td>${formatVenueDateTime(b.endAt, tz)}</td></tr>`).join("");
    const lines = r.lines.map((l) => `<tr><td>${KIND_LABEL[l.kind]}: ${esc(l.label)}</td><td style="text-align:right">${l.qty}</td><td style="text-align:right">${formatVnd(l.unitMinor)}</td><td style="text-align:right">${formatVnd(l.totalMinor)}</td></tr>`).join("");
    const stages = r.stages.map((s) => `<tr><td>${esc(s.label)}</td><td>${s.dueOn ? dayjs(s.dueOn).format(DATE_FORMAT) : ""}</td><td style="text-align:right">${formatVnd(s.amountMinor)}</td></tr>`).join("");
    const title = kind === "contract" ? "BI\xCAN B\u1EA2N / H\u1EE2P \u0110\u1ED2NG THU\xCA S\xC2N" : "B\xC1O GI\xC1 THU\xCA S\xC2N";
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html><head><meta charset="utf-8"><title>${title} ${r.code}</title><style>body{font-family:sans-serif;margin:32px;color:#111}table{border-collapse:collapse;width:100%;margin:8px 0 18px}td,th{border:1px solid #999;padding:6px 8px;font-size:13px;text-align:left}h1{font-size:20px}h2{font-size:15px;margin-top:18px}</style></head><body>
      <h1>${title}</h1><p>M\xE3: <b>${r.code}</b>${r.contractNo ? ` \xB7 S\u1ED1 h\u1EE3p \u0111\u1ED3ng: <b>${esc(r.contractNo)}</b>` : ""}</p>
      <h2>B\xEAn thu\xEA</h2><p>${esc(r.customerName)}${r.taxCode ? ` \xB7 MST ${esc(r.taxCode)}` : ""}<br>${esc(r.contactName)} ${esc(r.phone)} ${esc(r.email)}<br>${esc(r.address)}${r.vatRequested ? "<br><i>C\xF3 y\xEAu c\u1EA7u xu\u1EA5t ho\xE1 \u0111\u01A1n VAT</i>" : ""}</p>
      <h2>S\u1EF1 ki\u1EC7n: ${esc(r.title)}</h2><table><tr><th>S\xE2n</th><th>T\u1EEB</th><th>\u0110\u1EBFn</th></tr>${blocks}</table>
      <h2>B\xE1o gi\xE1</h2><table><tr><th>N\u1ED9i dung</th><th>SL</th><th>\u0110\u01A1n gi\xE1</th><th>Th\xE0nh ti\u1EC1n</th></tr>${lines}<tr><td colspan="3"><b>T\u1ED5ng c\u1ED9ng</b></td><td style="text-align:right"><b>${formatVnd(r.totalMinor)}</b></td></tr></table>
      <h2>C\xE1c \u0111\u1EE3t thanh to\xE1n</h2><table><tr><th>\u0110\u1EE3t</th><th>H\u1EA1n</th><th>S\u1ED1 ti\u1EC1n</th></tr>${stages}</table>
      ${r.policy?.termsText ? `<h2>\u0110i\u1EC1u kho\u1EA3n</h2><p style="white-space:pre-wrap">${esc(r.policy.termsText)}</p>` : ""}
      ${kind === "contract" ? '<table style="margin-top:40px"><tr><td style="height:90px">\u0110\u1EA1i di\u1EC7n b\xEAn thu\xEA<br>(k\xFD, ghi r\xF5 h\u1ECD t\xEAn)</td><td>\u0110\u1EA1i di\u1EC7n c\u01A1 s\u1EDF<br>(k\xFD, \u0111\xF3ng d\u1EA5u)</td></tr></table>' : ""}
      </body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };
  const [payOpen, setPayOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);
  const [payForm] = Form.useForm();
  const [cancelForm] = Form.useForm();
  const cancelBy = Form.useWatch("by", cancelForm);
  const cancelOverride = Form.useWatch("override", cancelForm);
  const [cancelPreview, setCancelPreview] = useState(null);
  useEffect(() => {
    if (!cancelOpen || !detail || !supplierId) {
      setCancelPreview(null);
      return;
    }
    let stale = false;
    const body = { code: detail.code, by: cancelBy ?? "customer" };
    if (cancelOverride != null && cancelBy !== "customer") body.refund_percent = cancelOverride;
    api.request(supplierId, "rentals/cancel-preview", body, "POST").then((r) => {
      if (!stale) setCancelPreview(r);
    }).catch(() => {
      if (!stale) setCancelPreview(null);
    });
    return () => {
      stale = true;
    };
  }, [cancelOpen, detail, supplierId, cancelBy, cancelOverride, api]);
  const [settleForm] = Form.useForm();
  const actions = (r) => /* @__PURE__ */ jsxs(Space, { wrap: true, children: [
    /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(PrinterOutlined, {}), onClick: () => printDoc(r, "quote"), children: "In b\xE1o gi\xE1" }),
    /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(PrinterOutlined, {}), onClick: () => printDoc(r, "contract"), children: "In h\u1EE3p \u0111\u1ED3ng" }),
    r.status === "quote" && /* @__PURE__ */ jsx(Popconfirm, { title: "Gi\u1EEF ch\u1ED7 to\xE0n b\u1ED9 kh\u1ED1i s\xE2n? L\u1ECBch chung s\u1EBD b\u1ECB ch\u1EB7n t\u1EDBi h\u1EA1n gi\u1EEF.", onConfirm: async () => {
      if (await act("rentals/hold", { code: r.code }, "\u0110\xE3 gi\u1EEF ch\u1ED7 t\u1EA1m")) await reload(r.code);
    }, children: /* @__PURE__ */ jsx(Button, { type: "primary", children: "Gi\u1EEF ch\u1ED7" }) }),
    r.orderCode && (r.status === "held" || r.status === "deposited" || r.status === "completed" && r.settlement?.orderAdjusted !== false && dueMinorOf(r) > 0) && /* @__PURE__ */ jsx(Button, { type: "primary", onClick: () => setPayOpen(true), children: "Ghi thu" }),
    (r.status === "held" || r.status === "deposited") && /* @__PURE__ */ jsx(Button, { danger: true, onClick: () => setCancelOpen(true), children: "Hu\u1EF7 cu\u1ED9c thu\xEA" }),
    r.status === "quote" && /* @__PURE__ */ jsx(Button, { danger: true, onClick: () => setCancelOpen(true), children: "Hu\u1EF7 b\xE1o gi\xE1" }),
    (r.status === "deposited" || r.status === "completed") && !r.settlement && /* @__PURE__ */ jsx(Button, { onClick: () => setSettleOpen(true), children: "Quy\u1EBFt to\xE1n" }),
    r.status === "completed" && r.settlement && r.settlement.orderAdjusted === false && /* @__PURE__ */ jsx(Button, { type: "primary", onClick: () => setPayOpen(true), children: "Thu kho\u1EA3n quy\u1EBFt to\xE1n" })
  ] });
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(
      PageHeader,
      {
        title: t("court.rentals.title", { defaultValue: "Thu\xEA s\xE2n s\u1EF1 ki\u1EC7n" }),
        description: t("court.rentals.description", { defaultValue: "Thu\xEA nhi\u1EC1u s\xE2n, nhi\u1EC1u ng\xE0y cho gi\u1EA3i \u0111\u1EA5u / s\u1EF1 ki\u1EC7n: b\xE1o gi\xE1, gi\u1EEF ch\u1ED7 t\u1EA1m, c\u1ECDc theo \u0111\u1EE3t, quy\u1EBFt to\xE1n." }),
        extra: /* @__PURE__ */ jsxs(Space, { wrap: true, children: [
          /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(ArrowLeftOutlined, {}), onClick: () => navigate("../courts", { relative: "path" }), children: "S\xE2n" }),
          /* @__PURE__ */ jsx(Select, { value: filter, onChange: setFilter, style: { width: 170 }, options: [{ value: "", label: "T\u1EA5t c\u1EA3 tr\u1EA1ng th\xE1i" }, ...Object.entries(STATUS).map(([k, v]) => ({ value: k, label: v.label }))] }),
          /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(ReloadOutlined, {}), onClick: () => void load(), loading, children: "L\xE0m m\u1EDBi" }),
          /* @__PURE__ */ jsx(Button, { type: "primary", icon: /* @__PURE__ */ jsx(PlusOutlined, {}), onClick: () => setCreating(true), children: "T\u1EA1o b\xE1o gi\xE1" })
        ] })
      }
    ),
    /* @__PURE__ */ jsx(
      Table,
      {
        rowKey: "id",
        loading,
        dataSource: rentals,
        pagination: false,
        scroll: { x: "max-content" },
        onRow: (r) => ({ onClick: () => setDetail(r), style: { cursor: "pointer" } }),
        columns: [
          { title: "M\xE3", dataIndex: "code" },
          { title: "S\u1EF1 ki\u1EC7n", dataIndex: "title" },
          { title: "\u0110\u01A1n v\u1ECB thu\xEA", dataIndex: "customerName" },
          { title: "Th\u1EDDi gian", render: (_, r) => r.blocks.length ? `${formatVenueDateTime(r.blocks[0].startAt, tz)} \u2192 ${formatVenueDateTime(r.blocks[r.blocks.length - 1].endAt, tz)}` : "" },
          { title: "T\u1ED5ng", dataIndex: "totalMinor", align: "right", render: formatVnd },
          { title: "\u0110\xE3 thu", dataIndex: "paidMinor", align: "right", render: (v, r) => /* @__PURE__ */ jsxs("span", { children: [
            formatVnd(v),
            dueMinorOf(r) > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx("br", {}),
              /* @__PURE__ */ jsxs(Typography.Text, { type: "danger", style: { fontSize: 12 }, children: [
                "C\xF2n thi\u1EBFu ",
                formatVnd(dueMinorOf(r))
              ] })
            ] })
          ] }) },
          { title: "Tr\u1EA1ng th\xE1i", dataIndex: "status", render: (_, r) => statusTag(r) }
        ]
      }
    ),
    /* @__PURE__ */ jsx(Modal, { title: "T\u1EA1o b\xE1o gi\xE1 thu\xEA s\xE2n s\u1EF1 ki\u1EC7n", open: creating, onCancel: () => setCreating(false), onOk: () => void submitCreate(), confirmLoading: saving, okText: "L\u01B0u b\xE1o gi\xE1", cancelText: "\u0110\xF3ng", width: 820, destroyOnClose: true, children: /* @__PURE__ */ jsxs(Form, { form, layout: "vertical", initialValues: { mode: "hourly", defaultStages: true }, children: [
      /* @__PURE__ */ jsxs(Space, { wrap: true, align: "start", children: [
        /* @__PURE__ */ jsx(Form.Item, { name: "title", label: "T\xEAn s\u1EF1 ki\u1EC7n", rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, { style: { width: 240 } }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "customerName", label: "\u0110\u01A1n v\u1ECB thu\xEA", rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, { style: { width: 240 } }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "contactName", label: "Ng\u01B0\u1EDDi li\xEAn h\u1EC7", children: /* @__PURE__ */ jsx(Input, { style: { width: 200 } }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "phone", label: "S\u1ED1 \u0111i\u1EC7n tho\u1EA1i", children: /* @__PURE__ */ jsx(Input, { style: { width: 160 } }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "email", label: "Email", children: /* @__PURE__ */ jsx(Input, { style: { width: 220 } }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "taxCode", label: "M\xE3 s\u1ED1 thu\u1EBF", children: /* @__PURE__ */ jsx(Input, { style: { width: 160 } }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "address", label: "\u0110\u1ECBa ch\u1EC9", children: /* @__PURE__ */ jsx(Input, { style: { width: 340 } }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "vat", valuePropName: "checked", label: " ", children: /* @__PURE__ */ jsx(Checkbox, { children: "Y\xEAu c\u1EA7u ho\xE1 \u0111\u01A1n VAT" }) })
      ] }),
      /* @__PURE__ */ jsx(Typography.Title, { level: 5, children: "Kh\u1ED1i s\xE2n" }),
      /* @__PURE__ */ jsxs(Space, { wrap: true, align: "start", children: [
        /* @__PURE__ */ jsx(Form.Item, { name: "courtIds", label: "S\xE2n", rules: [{ required: true, message: "Ch\u1ECDn \xEDt nh\u1EA5t m\u1ED9t s\xE2n" }], children: /* @__PURE__ */ jsx(Select, { mode: "multiple", style: { minWidth: 260 }, options: courts.map((c) => ({ value: c.resourceId, label: c.name })) }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "dates", label: "Ng\xE0y (t\u1EEB \u2013 \u0111\u1EBFn)", rules: [{ required: true }], children: /* @__PURE__ */ jsx(DatePicker.RangePicker, { format: DATE_FORMAT }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "times", label: "Gi\u1EDD m\u1ED7i ng\xE0y", rules: [{ required: true }], children: /* @__PURE__ */ jsx(TimePicker.RangePicker, { format: "HH:mm", minuteStep: 15 }) })
      ] }),
      /* @__PURE__ */ jsx(Typography.Title, { level: 5, children: "B\xE1o gi\xE1" }),
      /* @__PURE__ */ jsxs(Space, { wrap: true, align: "start", children: [
        /* @__PURE__ */ jsx(Form.Item, { name: "mode", label: "C\xE1ch t\xEDnh d\xF2ng s\xE2n", children: /* @__PURE__ */ jsx(Radio.Group, { options: [{ value: "hourly", label: "Theo s\xE2n-gi\u1EDD" }, { value: "daily", label: "Tr\u1ECDn g\xF3i theo ng\xE0y" }] }) }),
        /* @__PURE__ */ jsx(Form.Item, { label: " ", children: /* @__PURE__ */ jsx(Button, { onClick: () => void draftLines(), children: "T\xEDnh gi\xE1 nh\xE1p t\u1EEB b\u1EA3ng gi\xE1" }) })
      ] }),
      /* @__PURE__ */ jsx(Form.List, { name: "lines", children: (fields, { add, remove }) => /* @__PURE__ */ jsxs(Fragment, { children: [
        fields.map((f) => /* @__PURE__ */ jsxs(Space, { align: "baseline", wrap: true, children: [
          /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "kind"], rules: [{ required: true }], children: /* @__PURE__ */ jsx(Select, { style: { width: 120 }, options: Object.entries(KIND_LABEL).map(([k, v]) => ({ value: k, label: v })) }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "label"], rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, { placeholder: "N\u1ED9i dung", style: { width: 220 } }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "qty"], children: /* @__PURE__ */ jsx(InputNumber, { placeholder: "SL", min: 0 }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "unitMinor"], children: /* @__PURE__ */ jsx(InputNumber, { placeholder: "\u0110\u01A1n gi\xE1 (\u0111)", min: 0, style: { width: 130 }, ...MONEY_INPUT }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "totalMinor"], children: /* @__PURE__ */ jsx(InputNumber, { placeholder: "Th\xE0nh ti\u1EC1n (\u0111)", style: { width: 150 }, ...MONEY_INPUT }) }),
          /* @__PURE__ */ jsx(MinusCircleOutlined, { onClick: () => remove(f.name) })
        ] }, f.key)),
        /* @__PURE__ */ jsx(Button, { type: "dashed", icon: /* @__PURE__ */ jsx(PlusOutlined, {}), onClick: () => add({ kind: "service", qty: 1 }), children: "Th\xEAm d\u1ECBch v\u1EE5 / ph\u1EE5 thu / gi\u1EA3m gi\xE1" })
      ] }) }),
      /* @__PURE__ */ jsx(Typography.Paragraph, { type: "secondary", style: { marginTop: 8 }, children: "\u0110\u1EC3 tr\u1ED1ng th\xEC h\u1EC7 th\u1ED1ng t\u1EF1 t\xEDnh d\xF2ng s\xE2n t\u1EEB b\u1EA3ng gi\xE1 s\u1EF1 ki\u1EC7n c\u1EE7a c\u01A1 s\u1EDF. Gi\u1EA3m gi\xE1 nh\u1EADp s\u1ED1 d\u01B0\u01A1ng, h\u1EC7 th\u1ED1ng t\u1EF1 tr\u1EEB." }),
      /* @__PURE__ */ jsx(Form.Item, { name: "defaultStages", valuePropName: "checked", children: /* @__PURE__ */ jsx(Checkbox, { children: "D\xF9ng \u0111\u1EE3t thu m\u1EB7c \u0111\u1ECBnh (c\u1ECDc theo ch\xEDnh s\xE1ch + ph\u1EA7n c\xF2n l\u1EA1i tr\u01B0\u1EDBc s\u1EF1 ki\u1EC7n)" }) }),
      /* @__PURE__ */ jsx(Form.Item, { noStyle: true, shouldUpdate: (a, b) => a.defaultStages !== b.defaultStages, children: () => form.getFieldValue("defaultStages") === false ? /* @__PURE__ */ jsx(Form.List, { name: "stages", children: (fields, { add, remove }) => /* @__PURE__ */ jsxs(Fragment, { children: [
        fields.map((f) => /* @__PURE__ */ jsxs(Space, { align: "baseline", wrap: true, children: [
          /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "label"], children: /* @__PURE__ */ jsx(Input, { placeholder: "T\xEAn \u0111\u1EE3t" }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "amountMinor"], children: /* @__PURE__ */ jsx(InputNumber, { placeholder: "S\u1ED1 ti\u1EC1n (\u0111)", min: 1, style: { width: 150 }, ...MONEY_INPUT }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "dueOn"], children: /* @__PURE__ */ jsx(DatePicker, { format: DATE_FORMAT, placeholder: "H\u1EA1n" }) }),
          /* @__PURE__ */ jsx(MinusCircleOutlined, { onClick: () => remove(f.name) })
        ] }, f.key)),
        /* @__PURE__ */ jsx(Button, { type: "dashed", icon: /* @__PURE__ */ jsx(PlusOutlined, {}), disabled: fields.length >= 6, onClick: () => add({}), children: "Th\xEAm \u0111\u1EE3t (t\u1ED1i \u0111a 6)" })
      ] }) }) : null })
    ] }) }),
    /* @__PURE__ */ jsx(Drawer, { title: detail ? `${detail.title} (${detail.code})` : "", open: !!detail, onClose: () => setDetail(null), width: 720, destroyOnClose: true, children: detail && /* @__PURE__ */ jsxs(Space, { direction: "vertical", style: { width: "100%" }, size: "middle", children: [
      /* @__PURE__ */ jsxs(Space, { wrap: true, children: [
        statusTag(detail),
        detail.orderCode && /* @__PURE__ */ jsxs(Typography.Text, { type: "secondary", children: [
          "\u0110\u01A1n: ",
          detail.orderCode
        ] })
      ] }),
      detail.status === "held" && detail.holdExpiresAt && /* @__PURE__ */ jsx(Alert, { type: "warning", showIcon: true, message: `Gi\u1EEF ch\u1ED7 t\u1EA1m t\u1EDBi ${formatVenueDateTime(detail.holdExpiresAt, tz)}. H\u1EBFt h\u1EA1n m\xE0 ch\u01B0a \u0111\u1EE7 c\u1ECDc th\xEC t\u1EF1 nh\u1EA3 s\xE2n.` }),
      actions(detail),
      /* @__PURE__ */ jsxs(Descriptions, { size: "small", column: 2, bordered: true, children: [
        /* @__PURE__ */ jsx(Descriptions.Item, { label: "\u0110\u01A1n v\u1ECB thu\xEA", children: detail.customerName }),
        /* @__PURE__ */ jsxs(Descriptions.Item, { label: "Li\xEAn h\u1EC7", children: [
          detail.contactName,
          " ",
          detail.phone
        ] }),
        /* @__PURE__ */ jsx(Descriptions.Item, { label: "Email", children: detail.email }),
        /* @__PURE__ */ jsxs(Descriptions.Item, { label: "MST / VAT", children: [
          detail.taxCode,
          " ",
          detail.vatRequested ? "\xB7 y\xEAu c\u1EA7u VAT" : ""
        ] }),
        /* @__PURE__ */ jsx(Descriptions.Item, { label: "T\u1ED5ng", children: formatVnd(detail.totalMinor) }),
        /* @__PURE__ */ jsx(Descriptions.Item, { label: "\u0110\xE3 thu", children: formatVnd(detail.paidMinor + (detail.settlement?.extraPaidMinor ?? 0)) }),
        dueMinorOf(detail) > 0 && /* @__PURE__ */ jsx(Descriptions.Item, { label: "C\xF2n thi\u1EBFu", children: /* @__PURE__ */ jsx(Typography.Text, { type: "danger", strong: true, children: formatVnd(dueMinorOf(detail)) }) })
      ] }),
      /* @__PURE__ */ jsx(
        Table,
        {
          size: "small",
          rowKey: (b) => `${b.resourceId}${b.startAt}`,
          pagination: false,
          dataSource: detail.blocks,
          columns: [{ title: "S\xE2n", dataIndex: "resourceId", render: (id) => courtName.get(id) ?? id }, { title: "T\u1EEB", dataIndex: "startAt", render: (v) => formatVenueDateTime(v, tz) }, { title: "\u0110\u1EBFn", dataIndex: "endAt", render: (v) => formatVenueDateTime(v, tz) }]
        }
      ),
      /* @__PURE__ */ jsx(
        Table,
        {
          size: "small",
          rowKey: (l) => `${l.kind}${l.label}`,
          pagination: false,
          dataSource: detail.lines,
          columns: [{ title: "B\xE1o gi\xE1", render: (_, l) => `${KIND_LABEL[l.kind]}: ${l.label}` }, { title: "SL", dataIndex: "qty", align: "right" }, { title: "\u0110\u01A1n gi\xE1", dataIndex: "unitMinor", align: "right", render: formatVnd }, { title: "Th\xE0nh ti\u1EC1n", dataIndex: "totalMinor", align: "right", render: formatVnd }]
        }
      ),
      /* @__PURE__ */ jsx(
        Table,
        {
          size: "small",
          rowKey: "label",
          pagination: false,
          dataSource: detail.stages,
          columns: [{ title: "\u0110\u1EE3t thu", dataIndex: "label" }, { title: "H\u1EA1n", dataIndex: "dueOn", render: (v) => v ? dayjs(v).format(DATE_FORMAT) : "" }, { title: "S\u1ED1 ti\u1EC1n", dataIndex: "amountMinor", align: "right", render: formatVnd }]
        }
      ),
      detail.cancelInfo && /* @__PURE__ */ jsx(Alert, { type: "info", showIcon: true, message: `\u0110\xE3 hu\u1EF7: ${detail.cancelInfo.reason ?? ""} \xB7 ho\xE0n ${formatVnd(detail.cancelInfo.refundMinor)} (${detail.cancelInfo.refundPercent ?? 0}%), gi\u1EEF l\u1EA1i ${formatVnd(detail.cancelInfo.forfeitMinor)}` }),
      detail.settlement && /* @__PURE__ */ jsx(Alert, { type: "success", showIcon: true, message: `Quy\u1EBFt to\xE1n: s\u1ED1 cu\u1ED1i ${formatVnd(detail.settlement.finalMinor)}${detail.settlement.note ? ` \xB7 ${detail.settlement.note}` : ""}`, description: (detail.settlement.adjustments ?? []).map((a) => `${a.label}: ${formatVnd(a.amountMinor)}`).join(" \xB7 ") })
    ] }) }),
    /* @__PURE__ */ jsx(
      Modal,
      {
        title: "Ghi thu",
        open: payOpen,
        onCancel: () => setPayOpen(false),
        okText: "Ghi nh\u1EADn",
        cancelText: "\u0110\xF3ng",
        destroyOnClose: true,
        onOk: async () => {
          const v = await payForm.validateFields();
          if (!detail) return;
          const extra = detail.status === "completed" && detail.settlement && detail.settlement.orderAdjusted === false;
          const ref = `rental-${detail.code}-${Date.now()}`;
          const ok = extra ? await act("rentals/extra", { code: detail.code, amount_minor: v.amount, provider: v.provider, payment_ref: ref }, "\u0110\xE3 ghi nh\u1EADn kho\u1EA3n thu") : await act("deposits/record", { order_code: detail.orderCode, amount_minor: v.amount, provider: v.provider, receipt_url: v.receipt || "", note: v.note || "", client_ref: ref }, "\u0110\xE3 ghi nh\u1EADn kho\u1EA3n thu");
          if (ok) {
            setPayOpen(false);
            payForm.resetFields();
            await reload(detail.code);
          }
        },
        children: /* @__PURE__ */ jsxs(Form, { form: payForm, layout: "vertical", initialValues: { provider: "banktransfer" }, children: [
          /* @__PURE__ */ jsx(Form.Item, { name: "amount", label: "S\u1ED1 ti\u1EC1n (\u0111)", rules: [{ required: true }], children: /* @__PURE__ */ jsx(InputNumber, { min: 1, style: { width: 200 }, ...MONEY_INPUT }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "provider", label: "H\xECnh th\u1EE9c", children: /* @__PURE__ */ jsx(Radio.Group, { options: [{ value: "banktransfer", label: "Chuy\u1EC3n kho\u1EA3n" }, { value: "cash", label: "Ti\u1EC1n m\u1EB7t" }] }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "receipt", label: "\u1EA2nh bi\xEAn lai (link)", children: /* @__PURE__ */ jsx(Input, {}) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "note", label: "Ghi ch\xFA", children: /* @__PURE__ */ jsx(Input, {}) })
        ] })
      }
    ),
    /* @__PURE__ */ jsx(
      Modal,
      {
        title: "Hu\u1EF7 cu\u1ED9c thu\xEA",
        open: cancelOpen,
        onCancel: () => setCancelOpen(false),
        okText: "Hu\u1EF7 cu\u1ED9c thu\xEA",
        okButtonProps: { danger: true },
        cancelText: "\u0110\xF3ng",
        destroyOnClose: true,
        onOk: async () => {
          const v = await cancelForm.validateFields();
          if (!detail) return;
          const body = { code: detail.code, by: v.by, reason: v.reason };
          if (v.override != null) body.refund_percent = v.override;
          if (await act("rentals/cancel", body, '\u0110\xE3 hu\u1EF7 cu\u1ED9c thu\xEA; t\xE1c v\u1EE5 ho\xE0n (n\u1EBFu c\xF3) n\u1EB1m \u1EDF "Ho\xE0n ti\u1EC1n ch\u1EDD x\u1EED l\xFD"')) {
            setCancelOpen(false);
            cancelForm.resetFields();
            await reload(detail.code);
          }
        },
        children: /* @__PURE__ */ jsxs(Form, { form: cancelForm, layout: "vertical", initialValues: { by: "customer" }, children: [
          /* @__PURE__ */ jsx(Alert, { style: { marginBottom: 12 }, type: "info", showIcon: true, message: "Ho\xE0n theo m\u1ED1c ng\xE0y tr\u01B0\u1EDBc gi\u1EDD b\u1EAFt \u0111\u1EA7u kh\u1ED1i \u0111\u1EA7u ti\xEAn (m\u1EB7c \u0111\u1ECBnh > 14 ng\xE0y 100%, > 7 ng\xE0y 50%, c\xF2n l\u1EA1i 0%). C\u01A1 s\u1EDF hu\u1EF7 th\xEC ho\xE0n 100%." }),
          cancelPreview && /* @__PURE__ */ jsx(
            Alert,
            {
              style: { marginBottom: 12 },
              type: cancelPreview.forfeitMinor > 0 ? "warning" : "success",
              showIcon: true,
              message: `\u0110\xE3 thu ${formatVnd(cancelPreview.paidMinor)}: ho\xE0n ${formatVnd(cancelPreview.refundMinor)} (${cancelPreview.refundPercent}%), c\u01A1 s\u1EDF gi\u1EEF ${formatVnd(cancelPreview.forfeitMinor)}`,
              description: `C\xF2n ${Math.max(0, cancelPreview.daysBefore).toFixed(1)} ng\xE0y t\u1EDBi kh\u1ED1i \u0111\u1EA7u ti\xEAn. S\u1ED1 ho\xE0n \u0111\u1ED5i theo ng\u01B0\u1EDDi hu\u1EF7 v\xE0 % ghi \u0111\xE8.`
            }
          ),
          /* @__PURE__ */ jsx(Form.Item, { name: "by", label: "Ai hu\u1EF7", children: /* @__PURE__ */ jsx(Radio.Group, { options: [{ value: "customer", label: "Kh\xE1ch" }, { value: "staff", label: "Nh\xE2n vi\xEAn" }, { value: "venue", label: "C\u01A1 s\u1EDF" }] }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "reason", label: "L\xFD do", rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, {}) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "override", label: "Ghi \u0111\xE8 % ho\xE0n (tu\u1EF3 ch\u1ECDn, kh\xF4ng \xE1p d\u1EE5ng khi kh\xE1ch hu\u1EF7)", children: /* @__PURE__ */ jsx(InputNumber, { min: 0, max: 100, addonAfter: "%" }) })
        ] })
      }
    ),
    /* @__PURE__ */ jsx(
      Modal,
      {
        title: "Quy\u1EBFt to\xE1n",
        open: settleOpen,
        onCancel: () => setSettleOpen(false),
        okText: "Quy\u1EBFt to\xE1n",
        cancelText: "\u0110\xF3ng",
        destroyOnClose: true,
        width: 640,
        onOk: async () => {
          const v = await settleForm.validateFields();
          if (!detail) return;
          const adjustments = (v.adj ?? []).map((a) => ({ label: a.label, amount_minor: a.amount }));
          if (await act("rentals/settle", { code: detail.code, settlement: { adjustments, note: v.note || "" } }, "\u0110\xE3 quy\u1EBFt to\xE1n")) {
            setSettleOpen(false);
            settleForm.resetFields();
            await reload(detail.code);
          }
        },
        children: /* @__PURE__ */ jsxs(Form, { form: settleForm, layout: "vertical", children: [
          /* @__PURE__ */ jsx(Alert, { style: { marginBottom: 12 }, type: "info", showIcon: true, message: "S\u1ED1 cu\u1ED1i = b\xE1o gi\xE1 \xB1 c\xE1c \u0111i\u1EC1u ch\u1EC9nh. Thu d\u01B0 s\u1EBD sinh t\xE1c v\u1EE5 ho\xE0n; c\xF2n thi\u1EBFu th\xEC ghi thu ti\u1EBFp. Ch\u1EC9 quy\u1EBFt to\xE1n \u0111\u01B0\u1EE3c sau khi kh\u1ED1i cu\u1ED1i k\u1EBFt th\xFAc." }),
          /* @__PURE__ */ jsx(Form.List, { name: "adj", children: (fields, { add, remove }) => /* @__PURE__ */ jsxs(Fragment, { children: [
            fields.map((f) => /* @__PURE__ */ jsxs(Space, { align: "baseline", wrap: true, children: [
              /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "label"], rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, { placeholder: "N\u1ED9i dung (vd: qu\xE1 gi\u1EDD 30 ph\xFAt)", style: { width: 280 } }) }),
              /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "amount"], rules: [{ required: true }], children: /* @__PURE__ */ jsx(InputNumber, { placeholder: "\xB1 s\u1ED1 ti\u1EC1n (\u0111)", style: { width: 160 }, ...MONEY_INPUT }) }),
              /* @__PURE__ */ jsx(MinusCircleOutlined, { onClick: () => remove(f.name) })
            ] }, f.key)),
            /* @__PURE__ */ jsx(Button, { type: "dashed", icon: /* @__PURE__ */ jsx(PlusOutlined, {}), onClick: () => add({}), children: "Th\xEAm \u0111i\u1EC1u ch\u1EC9nh (d\u01B0\u01A1ng = thu th\xEAm, \xE2m = gi\u1EA3m tr\u1EEB)" })
          ] }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "note", label: "Ghi ch\xFA", style: { marginTop: 12 }, children: /* @__PURE__ */ jsx(Input, {}) })
        ] })
      }
    )
  ] });
};

export { BookingsPage, ClosuresPage, CourtCalendarModal, CourtContext, CourtLayout, CourtPricingPage, CourtStaffPage, CourtsPage, DepositsPage, EventRentalsPage, OrderStatusTag, PageHeader, RefundsPage, ReportsPage, StaffPinGate, VenueSettingsPage, VenuesPage, courtMenuItems, courtTranslations, en, useCourtContext, vi };
//# sourceMappingURL=index.esm.js.map
